import React, { useEffect, useState, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import LinkedCorrespondenceCard from '../../components/LinkedCorrespondenceCard';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import TableEmptyRow from '../../components/TableEmptyRow';
import TablePagination from '../../components/TablePagination';
import KpiBreakdownTooltip from '../../components/KpiBreakdownTooltip';
import StatusBadge from '../../components/StatusBadge';
import { escapeHtml, getReportFullHtml, openPrintWindow, tableHtmlFromRows } from '../../utils/exportUtils';
import TabbedSection from '../../components/TabbedSection';
import RARecoveriesWithheldView from './RARecoveriesWithheldView';
import './RARecoveriesWithheldView.css';
import RABillDocument from './raBillDocument/RABillDocument';
import {
  additionalRowsForSection,
  carryForwardAdditionalRows,
  normalizeAdditionalBillRows,
  pruneEmptyAdditionalBillRows,
  SECTION_CODES,
  unionAdditionalBillRows,
} from '../../utils/raBillDocumentRows';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../utils/attachmentStorage';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN, stripBrackets } from '../../utils/dateFormat';
import { parseRABillSerialNumber } from '../../utils/parseRABillSerial';
import { formatKpiCrores, formatKpiAdaptiveINR } from '../../utils/kpiAmountFormat';
import { adminAPI, bankGuaranteesAPI, correspondenceAPI, raBillsAPI } from '../../services/authService';
import { useFormConfiguration } from '../../hooks/useFormConfiguration';
import {
  extractCommercialInputs,
  nz,
  resolveGrossValueOfWorkDoneForCpwd,
  sumUptoDateGrossValue,
} from '../../utils/cpwdRABillFormula';
import { computeRABillAmounts } from '../../utils/raBillCalculationRouter';
import {
  applyResolvedTemplateToBill,
  buildDepartmentValuesFromRule,
  buildPreviousTemplateValues,
  buildTemplateComparisonRows,
  buildTemplateFreezePayload,
  buildTemplateInputs,
  getTemplateFieldValueFromBill,
  groupTemplateFieldsBySection,
  hasFrozenTemplateCalculation,
  isConfigurableSnapshot,
} from '../../utils/raBillTemplateRuntime';

/** CPWD sheet rows 5(a)/5(b) from stored payload when cumulative Σ vs previous RA wrongly matches (RA3 E&M etc.). */
const raBillCpwdLine5Fallbacks = (src = {}) => ({
  fallbackNetSincePreviousBill: nz(
    src.net_value_work_done_since_prev_bill ?? src.current_bill_value,
  ),
  fallbackNetExclSecuredAdvance: nz(src.net_value_work_done_without_sec_adv),
});

const raBillStatutoryOverride = (src = {}) => nz(src?.cpwd_statutory_recovery_base_override);

/** When payload includes `cpwd_security_deposit_override` (≥ 0), use it instead of CPWD 2.5% SD. */
const raBillSecurityDepositOverride = (src = {}) => {
  if (!src || typeof src !== 'object') return undefined;
  if (!Object.prototype.hasOwnProperty.call(src, 'cpwd_security_deposit_override')) return undefined;
  const n = Number(src.cpwd_security_deposit_override);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
};

/** When user edits line 4 / prev sec to differ from auto chain, use their figures (fixes duplicate Σ). */
const mergeUserPreviousRaWithChain = (userGross, chainGross, userSec, chainSec) => {
  const u = nz(userGross);
  const c = nz(chainGross);
  const us = nz(userSec);
  const cs = nz(chainSec);
  return {
    previousUptoDateGrossCpwd: (u > 0 && u !== c) ? u : c,
    previousUptoDateSecuredAdvance: (us > 0 && us !== cs) ? us : cs,
  };
};

/** Classic CPWD register sections when project has no CONFIGURABLE template. */
const CPWD_REGISTER_SECTIONS = [
  {
    key: 'work',
    sectionName: 'Work Done and Bill Value',
    fields: [
      { fieldCode: 'agreement_amount', fieldLabel: 'Agreement Amount (Rs.)' },
      { fieldCode: 'upto_date_gross_work_done', fieldLabel: '1. Gross Value of Work done (Rs.)' },
      { fieldCode: 'sec_advance_material', fieldLabel: '2 (a). Secured Advance on material (Rs.)' },
      { fieldCode: 'mobilisation_advance', fieldLabel: '2 (b). Mobilisation Advance (Rs.)' },
      { fieldCode: 'clause_10cc_10ca_payment', fieldLabel: '2 (Clause 10CC / 10CA) (Rs.)' },
      { fieldCode: 'advance_payment_workdone_not_measured', fieldLabel: '2 (c). Advance payment (not measured) (Rs.)' },
      { fieldCode: 'upto_date_gross_value_of_work_done', fieldLabel: '3. Upto date Gross Value of Work done (Rs.)' },
      { fieldCode: 'secured_advance_added_in_this_bill', fieldLabel: 'Secured Advance added in this bill (Rs.)' },
      { fieldCode: 'gross_value_as_per_previous_ra_bill', fieldLabel: '4. Gross value as per Previous RA (Rs.)' },
      { fieldCode: 'net_value_work_done_since_prev_bill', fieldLabel: '5 (a). Net value since Previous Bill (Rs.)' },
      { fieldCode: 'net_value_work_done_without_sec_adv', fieldLabel: '5 (b). Net Value excl. Secured Advance (Rs.)' },
    ],
  },
  {
    key: 'recoveries',
    sectionName: 'Deductions - Recoveries',
    fields: [
      { fieldCode: 'security_deposit', fieldLabel: 'a) Security Deposit (Rs.)' },
      { fieldCode: 'gst_deduction', fieldLabel: 'b) GST (Rs.)' },
      { fieldCode: 'income_tax_deduction', fieldLabel: 'c) Income Tax (Rs.)' },
      { fieldCode: 'labour_cess', fieldLabel: 'd) Labour Cess (Rs.)' },
      { fieldCode: 'additional_sd_special_items', fieldLabel: 'e) Addl. SD for Spl.items (Rs.)' },
      { fieldCode: 'total_recoveries', fieldLabel: 'Total Recoveries (Rs.)' },
    ],
  },
  {
    key: 'totals',
    sectionName: 'Bill Totals',
    fields: [
      { fieldCode: 'total_withheld', fieldLabel: 'Total Withheld (Rs.)' },
      { fieldCode: 'total_deductions', fieldLabel: 'Total Deductions (Rs.)' },
      { fieldCode: 'net_payable', fieldLabel: 'Net Payable / Net Payment (Rs.)' },
      { fieldCode: 'paid_value', fieldLabel: 'Paid Value (Rs.)' },
      { fieldCode: 'balance_value', fieldLabel: 'Balance Value (Rs.)' },
    ],
  },
];

const getCpwdRegisterFieldValue = (bill, fieldCode) => {
  if (!bill || !fieldCode) return 0;
  if (fieldCode === 'upto_date_gross_work_done') {
    return Number(bill.upto_date_gross_work_done ?? bill.gross_value_of_work_done ?? 0) || 0;
  }
  if (fieldCode === 'upto_date_gross_value_of_work_done') {
    return Number(
      bill.upto_date_gross_value_of_work_done
      ?? bill.upto_date_gross_inc_sec_adv
      ?? bill.upto_date_gross_work_done
      ?? 0,
    ) || 0;
  }
  if (fieldCode === 'gross_value_as_per_previous_ra_bill') {
    return Number(
      bill.gross_value_as_per_previous_ra_bill
      ?? bill.previous_ra_gross_inc_sec_adv
      ?? bill.previous_ra_upto_date_gross
      ?? 0,
    ) || 0;
  }
  if (fieldCode === 'net_value_work_done_since_prev_bill') {
    return Number(bill.net_value_work_done_since_prev_bill ?? bill.current_bill_value ?? 0) || 0;
  }
  if (fieldCode === 'net_payable') {
    return Number(bill.net_payable ?? bill.net_payment_received ?? 0) || 0;
  }
  return Number(bill[fieldCode] ?? 0) || 0;
};

/** Prefer an explicit TOTAL / last formula field as the section column value. */
const pickSectionTotalField = (fields = []) => {
  if (!fields.length) return null;
  let best = null;
  let bestScore = -1;
  fields.forEach((f, idx) => {
    const label = `${f.fieldLabel || ''} ${f.fieldCode || ''}`.toUpperCase();
    let score = idx;
    if (/\bTOTAL\b/.test(label)) score += 100;
    if (/\bGROSS\b/.test(label) && /\bAMOUNT\b/.test(label)) score += 50;
    if (String(f.fieldType || '').toUpperCase() === 'FORMULA') score += 15;
    if (score >= bestScore) {
      bestScore = score;
      best = f;
    }
  });
  return best || fields[fields.length - 1];
};

const RA_STATUS_CLASS_MAP = {
  Draft: 'draft',
  Submitted: 'submitted',
  Paid: 'paid',
  Returned: 'high',
  Resubmitted: 'under-review',
  Certified: 'certified',
  Closed: 'closed',
};

const PAYMENT_STATUS_CLASS_MAP = {
  Paid: 'paid',
  'Part Paid': 'part-paid',
  Unpaid: 'unpaid',
  Withheld: 'critical',
};

const WITHHELD_REASON_OPTIONS = [
  { value: 'milestone', label: 'Towards Non-achievement of Milestone' },
  { value: 'mpr', label: 'Towards Non-submission of MPR' },
  { value: 'other', label: 'Others' },
];

const formatCurrency = (val) => (
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val)}`
);

const formatCurrencyNoSymbol = (val) => (
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val || 0)
);

const RaReadonlyField = ({ label, children, spanFull = false, valueClassName = '' }) => (
  <div className={`form-group${spanFull ? ' span-full' : ''}`}>
    <label>{label}</label>
    <div className={`ra-readonly-value${valueClassName ? ` ${valueClassName}` : ''}`}>{children}</div>
  </div>
);

const DeductionsTooltip = ({ bill, formatAmount }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const leaveTimerRef = useRef(null);

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
      setShow(true);
    }
  };

  const handleMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => setShow(false), 150);
  };

  useEffect(() => {
    return () => { if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current); };
  }, []);

  const rows = [
    { label: 'Security Deposit', rate: 'CPWD 2.5%', amount: bill.security_deposit },
    { label: 'GST Deduction', rate: 'CPWD 2%', amount: bill.gst_deduction },
    { label: 'Income Tax', rate: 'CPWD 2%', amount: bill.income_tax_deduction },
    { label: 'Labour Cess', rate: 'CPWD 1%', amount: bill.labour_cess },
    { label: 'Addl. SD (Spl. Items)', rate: '—', amount: bill.additional_sd_special_items },
    { label: 'Total Recoveries', rate: '—', amount: bill.total_recoveries },
    { label: 'Total Withheld', rate: '—', amount: bill.total_withheld },
    { label: 'Total Deductions', rate: '—', amount: bill.total_deductions || bill.deductions_total, bold: true },
  ];

  const popup = show && ReactDOM.createPortal(
    <div
      className="deductions-tooltip-popup print-hidden"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: 'translate(-50%, -100%)',
        zIndex: 10000,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
        Deductions breakup (RA: {bill.ra_ref || bill.ra_no || '-'})
      </div>
      <table className="deductions-tooltip-table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Rate</th>
            <th>Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={r.bold ? { fontWeight: 600 } : undefined}>
              <td>{r.label}</td>
              <td>{r.rate}</td>
              <td style={{ textAlign: 'right' }}>{formatAmount(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>,
    document.body
  );

  return (
    <span
      ref={anchorRef}
      className="deductions-tooltip-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(bill.deductions_total || 0)}
      {popup}
    </span>
  );
};

/** KPI hover: pending withheld split by reason (full labels, incl. custom “Others” text). */
const PendingWithheldReasonsTooltip = ({ reasonLines }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const leaveTimerRef = useRef(null);

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
      setShow(true);
    }
  };

  const handleMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => setShow(false), 150);
  };

  useEffect(() => () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
  }, []);

  if (!Array.isArray(reasonLines) || reasonLines.length === 0) return null;

  const popup = show && ReactDOM.createPortal(
    <div
      className="deductions-tooltip-popup print-hidden"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: 'translate(-50%, -100%)',
        zIndex: 10000,
        maxWidth: 400,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: '#374151' }}>
        Pending withheld by reason
      </div>
      <p style={{ fontSize: 10, color: '#6b7280', margin: '0 0 8px', lineHeight: 1.35 }}>
        Approximate pending ₹ per reason. If a bill has partial releases, amounts are split across lines in proportion to each line on that bill. “Others” uses the text you entered.
      </p>
      <table className="deductions-tooltip-table">
        <thead>
          <tr>
            <th>Reason</th>
            <th style={{ textAlign: 'right' }}>Pending (₹)</th>
          </tr>
        </thead>
        <tbody>
          {reasonLines.map((row, idx) => (
            <tr key={`rw-reason-${idx}`}>
              <td style={{ maxWidth: 260, wordBreak: 'break-word', verticalAlign: 'top' }}>{row.label}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{formatCurrencyNoSymbol(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>,
    document.body,
  );

  return (
    <span style={{ display: 'block', marginTop: 6 }} className="print-hidden">
      <span
        ref={anchorRef}
        className="deductions-tooltip-wrapper"
        style={{
          fontSize: 11,
          color: '#b45309',
          cursor: 'help',
          borderBottom: '1px dotted #d97706',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        Hover for breakdown by reason
      </span>
      {popup}
    </span>
  );
};

/** CPWD register: same serial resolution for Civil, E&M, Horticulture (item-rate or EPC). */
const parseRANumber = parseRABillSerialNumber;

const CONTRACT_TYPES = { EPC: 'EPC', ITEM_RATE: 'Item rate % Contract' };

/** Same grouping as computed RA rows: item-rate contracts split by division. */
const getRABillStreamKey = (bill, projectFinancials) => {
  const projectId = Number(bill.project_id || 0);
  const contractType = projectFinancials[projectId]?.contract_type || '';
  const division = bill.division || '';
  return contractType === CONTRACT_TYPES.ITEM_RATE ? `${projectId}:${division}` : String(projectId);
};

const grossIncSecAdvAmount = (bill) => Number(
  bill?.upto_date_gross_value_of_work_done
    ?? bill?.upto_date_gross_inc_sec_adv
    ?? bill?.upto_date_gross_work_done
    ?? 0,
);

const emptyNewPaymentState = () => ({
  payment_date: '',
  amount_received: '',
  paying_authority: '',
  cm_book_no: '',
  cm_book_page_nos: '',
  cash_book_no: '',
  cash_book_page_no: '',
  voucher_no: '',
  remarks: '',
  attachment: null,
});

/** Recompute paid / balance / status from payment line items. */
const aggregatePaymentTotals = (payments, netPayable) => {
  const list = safeArray(payments);
  const totalPaid = list.reduce((sum, p) => sum + Number(p.amount_received || 0), 0);
  const net = Number(netPayable || 0);
  const dates = list.map((p) => p.payment_date).filter(Boolean).sort();
  const payment_received_date = dates.length ? dates[dates.length - 1] : null;
  let payment_status = 'Unpaid';
  if (totalPaid > 0 && totalPaid >= net) payment_status = 'Paid';
  else if (totalPaid > 0) payment_status = 'Part Paid';
  return {
    paid_value: totalPaid,
    balance_value: Math.max(0, net - totalPaid),
    payment_received_date,
    payment_status,
  };
};

const normalizeReleasedWithheldRecords = (payload) => (
  safeArray(payload?.released_withheld_records).map((r, i) => ({
    id: String(r.id || `rw-${i}`),
    release_date: r.release_date || r.date || '',
    amount: Number(r.amount || 0),
  }))
);

const sumReleasedWithheldRecords = (bill) => (
  safeArray(bill?.released_withheld_records).reduce((s, r) => s + Number(r.amount || 0), 0)
);

/** Sum of each stream's latest RA bill cumulative gross i/c Sec.Adv (filtered set). */
const sumLatestGrossIncSecAdvPerStream = (bills, projectFinancials) => {
  const list = safeArray(bills);
  if (list.length === 0) return 0;
  const byKey = new Map();
  list.forEach((bill) => {
    const key = getRABillStreamKey(bill, projectFinancials);
    const ra = parseRANumber(bill);
    const val = grossIncSecAdvAmount(bill);
    const prev = byKey.get(key);
    if (!prev || ra > prev.ra || (ra === prev.ra && Number(bill.id) > Number(prev.id))) {
      byKey.set(key, { ra, id: bill.id, val });
    }
  });
  let sum = 0;
  byKey.forEach((entry) => { sum += entry.val; });
  return sum;
};

const DIVISIONS = ['Civil', 'E&M', 'Horticulture'];
const DIVISION_COLORS = { Civil: '#7C3AED', 'E&M': '#3B82F6', Horticulture: '#22C55E' };

/** Division label from stream key `projectId:Division` (item-rate) or empty for EPC-style key. */
const streamKeyDivisionLabel = (streamKey) => {
  const s = String(streamKey || '');
  const i = s.indexOf(':');
  if (i === -1) return '';
  return s.slice(i + 1) || '';
};

const streamKeySortOrder = (streamKey) => {
  const div = streamKeyDivisionLabel(streamKey);
  const idx = DIVISIONS.indexOf(div);
  return idx === -1 ? 999 : idx;
};

const roundAmount = (value) => Math.max(0, Math.round(Number(value || 0)));

const createWithheldItem = (overrides = {}) => ({
  id: overrides.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  reason: overrides.reason || 'milestone',
  custom_reason: overrides.custom_reason || '',
  amount: overrides.amount ?? '',
});

const getWithheldReasonLabel = (item) => {
  const matched = WITHHELD_REASON_OPTIONS.find((option) => option.value === item?.reason);
  if (item?.reason === 'other') return item?.custom_reason?.trim() || 'Others';
  return matched?.label || 'Others';
};

const sumWithheldItems = (items) => safeArray(items)
  .reduce((sum, item) => sum + Number(item?.amount || 0), 0);

const normalizeWithheldItems = (payload = {}) => {
  const explicitItems = safeArray(payload.withheld_items).map((item, index) => createWithheldItem({
    id: item?.id || `withheld-${index}`,
    reason: item?.reason || 'other',
    custom_reason: item?.custom_reason || '',
    amount: Number(item?.amount || 0),
  })).filter((item) => item.reason);

  if (explicitItems.length > 0) {
    return explicitItems;
  }

  const fallbackItems = [];
  if (Number(payload.withheld_milestone || 0) > 0) {
    fallbackItems.push(createWithheldItem({ id: 'legacy-milestone', reason: 'milestone', amount: Number(payload.withheld_milestone || 0) }));
  }
  if (Number(payload.withheld_mpr || 0) > 0) {
    fallbackItems.push(createWithheldItem({ id: 'legacy-mpr', reason: 'mpr', amount: Number(payload.withheld_mpr || 0) }));
  }
  if (Number(payload.withheld_other_1 || 0) > 0) {
    fallbackItems.push(createWithheldItem({
      id: 'legacy-other-1',
      reason: 'other',
      custom_reason: payload.withheld_other_1_reason || 'Others 1',
      amount: Number(payload.withheld_other_1 || 0),
    }));
  }
  if (Number(payload.withheld_other_2 || 0) > 0) {
    fallbackItems.push(createWithheldItem({
      id: 'legacy-other-2',
      reason: 'other',
      custom_reason: payload.withheld_other_2_reason || 'Others 2',
      amount: Number(payload.withheld_other_2 || 0),
    }));
  }
  return fallbackItems;
};

/** Withheld lines for register KPIs: prefer explicit `withheld_items`, else legacy milestone/mpr/others. */
const getWithheldLinesForBreakdown = (bill) => {
  const explicit = safeArray(bill?.withheld_items).filter((it) => Number(it?.amount || 0) > 0);
  if (explicit.length > 0) return explicit;
  return normalizeWithheldItems({
    withheld_items: [],
    withheld_milestone: bill?.withheld_milestone,
    withheld_mpr: bill?.withheld_mpr,
    withheld_other_1: bill?.withheld_other_1,
    withheld_other_2: bill?.withheld_other_2,
    withheld_other_1_reason: bill?.withheld_other_1_reason,
    withheld_other_2_reason: bill?.withheld_other_2_reason,
  }).filter((it) => Number(it?.amount || 0) > 0);
};

/**
 * Pending withheld (per bill: total_withheld − released) split across line reasons.
 * When releases are partial, pending is allocated to lines in proportion to line amounts (same bill).
 */
const aggregatePendingWithheldByReasonLines = (bills) => {
  const map = new Map();
  for (const b of safeArray(bills)) {
    const wh = Number(b.total_withheld || 0);
    if (wh <= 0) continue;
    const released = sumReleasedWithheldRecords(b);
    const pending = Math.max(0, wh - released);
    if (pending <= 0) continue;
    const lines = getWithheldLinesForBreakdown(b);
    if (lines.length === 0) {
      map.set('Withheld (no reason lines)', (map.get('Withheld (no reason lines)') || 0) + pending);
      continue;
    }
    const lineSum = lines.reduce((s, it) => s + Number(it.amount || 0), 0);
    if (lineSum <= 0) {
      map.set('Withheld (no reason lines)', (map.get('Withheld (no reason lines)') || 0) + pending);
      continue;
    }
    for (const it of lines) {
      const amt = Number(it.amount || 0);
      const share = (amt / lineSum) * pending;
      const label = getWithheldReasonLabel(it);
      map.set(label, (map.get(label) || 0) + share);
    }
  }
  return Array.from(map.entries())
    .map(([label, v]) => ({ label, amount: Math.round(v) }))
    .filter((row) => row.amount > 0)
    .sort((a, b) => b.amount - a.amount);
};

const getLegacyWithheldAmounts = (items) => {
  let milestone = 0;
  let mpr = 0;
  let other1 = 0;
  let other2 = 0;

  safeArray(items).forEach((item) => {
    const amount = Number(item?.amount || 0);
    if (amount <= 0) return;
    if (item.reason === 'milestone') {
      milestone += amount;
      return;
    }
    if (item.reason === 'mpr') {
      mpr += amount;
      return;
    }
    if (other1 === 0) {
      other1 = amount;
      return;
    }
    other2 += amount;
  });

  return {
    milestone: roundAmount(milestone),
    mpr: roundAmount(mpr),
    other1: roundAmount(other1),
    other2: roundAmount(other2),
  };
};

/** CPWD statutory rates (shown in UI; recoveries use fixed workbook formulas, not project % overrides). */
const DEFAULT_RA_RATES = {
  security_deposit_pct: 2.5,
  gst_pct: 2,
  income_tax_pct: 2,
  labour_cess_pct: 1,
};

const ENABLE_DEPARTMENT_RULE_RATES_FOR_NEW_BILLS = true;
const MIN_RESTRUCTURE_REASON_LENGTH = 10;

const calculateRABillAmounts = ({
  agreementAmount,
  uptoDateGross,
  grossValueOfWorkDone,
  previousUptoDateGrossCpwd,
  previousUptoDateSecuredAdvance,
  isFirstBill,
  previousUptoDateGross,
  previousUptoDateGrossIncSecAdv,
  secAdvanceMaterial,
  mobilisationAdvance,
  advancePaymentWorkdoneNotMeasured,
  clause10cc10caPayment,
  raNo,
  additionalSd,
  withheldMilestone,
  withheldMpr,
  withheldOther1,
  withheldOther2,
  withheldItems,
  fallbackNetSincePreviousBill,
  fallbackNetExclSecuredAdvance,
  statutoryNetExclSecOverride,
  securityDepositOverride,
  rates: _rates,
  useRateOverrides = false,
  billMeta = null,
  templateInputs = null,
  previousTemplateValues = null,
  templateOverrides = null,
  departmentValues = null,
}) => {
  const activeRates = _rates || DEFAULT_RA_RATES;
  const commercialInputs = extractCommercialInputs({
    gross_value_of_work_done: grossValueOfWorkDone ?? uptoDateGross,
    upto_date_gross_work_done: grossValueOfWorkDone ?? uptoDateGross,
    sec_advance_material: secAdvanceMaterial,
    mobilisation_advance: mobilisationAdvance,
    clause_10cc_10ca_payment: clause10cc10caPayment,
    advance_payment_workdone_not_measured: advancePaymentWorkdoneNotMeasured,
  });

  const ra = Number(raNo || 1);
  const first = typeof isFirstBill === 'boolean' ? isFirstBill : (ra <= 1);
  const prevGross = first ? 0 : (
    previousUptoDateGrossCpwd != null && previousUptoDateGrossCpwd !== '' && !Number.isNaN(Number(previousUptoDateGrossCpwd))
      ? Number(previousUptoDateGrossCpwd)
      : Number(previousUptoDateGrossIncSecAdv ?? previousUptoDateGross ?? 0)
  );
  const prevSec = first ? 0 : (
    previousUptoDateSecuredAdvance != null && previousUptoDateSecuredAdvance !== '' && !Number.isNaN(Number(previousUptoDateSecuredAdvance))
      ? Number(previousUptoDateSecuredAdvance)
      : 0
  );

  const totalWithheld = safeArray(withheldItems).length > 0
    ? sumWithheldItems(withheldItems)
    : Number(withheldMilestone || 0) + Number(withheldMpr || 0) + Number(withheldOther1 || 0) + Number(withheldOther2 || 0);

  // Router: CONFIGURABLE snapshot → generic engine; otherwise identical CPWD path.
  const legacy = computeRABillAmounts({
    agreementAmount,
    commercialInputs,
    previousUptoDateGrossValue: prevGross,
    previousUptoDateSecuredAdvance: prevSec,
    isFirstBill: Boolean(first),
    addlSdForSpecialItems: additionalSd,
    totalWithheld,
    fallbackNetSincePreviousBill: nz(fallbackNetSincePreviousBill),
    fallbackNetExclSecuredAdvance: nz(fallbackNetExclSecuredAdvance),
    statutoryNetExclSecOverride: nz(statutoryNetExclSecOverride),
    securityDepositOverride: securityDepositOverride === undefined ? undefined : nz(securityDepositOverride),
    recoveryRateOverrides: useRateOverrides ? activeRates : undefined,
    billMeta: billMeta || undefined,
    templateInputs: templateInputs || undefined,
    previousTemplateValues: previousTemplateValues || undefined,
    templateOverrides: templateOverrides || undefined,
    departmentValues: departmentValues || undefined,
  });
  const workOnly = Number(commercialInputs.gross_value_of_work_done || 0);

  return {
    ...legacy,
    upto_date_gross_work_done: roundAmount(workOnly),
    upto_date_gross_value_of_work_done: legacy.upto_date_gross_value_of_work_done,
    gross_value_as_per_previous_ra_bill: legacy.gross_value_as_per_previous_ra_bill,
    secured_advance_added_in_this_bill: legacy.secured_advance_added_in_this_bill,
    sec_advance_material: roundAmount(secAdvanceMaterial),
    mobilisation_advance: roundAmount(mobilisationAdvance),
    advance_payment_workdone_not_measured: roundAmount(advancePaymentWorkdoneNotMeasured),
    clause_10cc_10ca_payment: roundAmount(clause10cc10caPayment),
    withheld_milestone: roundAmount(withheldMilestone),
    withheld_mpr: roundAmount(withheldMpr),
    withheld_other_1: roundAmount(withheldOther1),
    withheld_other_2: roundAmount(withheldOther2),
  };
};

/**
 * Same CPWD previous-RA snapshot as the register table: walk project (+ division) stream in RA order
 * and return cumulative Σ + secured advance immediately before `targetBill`. Merge `targetBill` into
 * `billList` by id so saves/edits use fresh inputs. Avoids wrong deductions when stored previous_ra_* is stale.
 */
function getCpwdChainContextForBill(targetBill, billList, projectFinancials) {
  const projectId = Number(targetBill.project_id || 0);
  const agreementAmount = Number(
    projectFinancials[projectId]?.agreement_amount ?? targetBill.agreement_amount ?? 0,
  );
  const contractType = projectFinancials[projectId]?.contract_type || '';
  const division = targetBill.division || '';
  const targetId = Number(targetBill.id);

  let stream = safeArray(billList).filter((b) => Number(b.project_id) === projectId);
  if (contractType === CONTRACT_TYPES.ITEM_RATE && division) {
    stream = stream.filter((b) => (b.division || '') === division);
  }
  const sorted = stream.slice().sort((a, b) => parseRANumber(a) - parseRANumber(b));

  const idx = Number.isFinite(targetId) && targetId > 0
    ? sorted.findIndex((b) => Number(b.id) === targetId)
    : -1;

  if (idx < 0) {
    return {
      previousUptoDateGrossCpwd: Number(targetBill.previous_ra_gross_inc_sec_adv ?? 0),
      previousUptoDateSecuredAdvance: Number(targetBill.previous_ra_sec_advance_upto ?? 0),
      isFirstBill: Number(parseRANumber(targetBill) || 1) <= 1,
      agreementAmount,
    };
  }

  let prevSnap = null;
  for (let i = 0; i < idx; i += 1) {
    const bill = sorted[i];
    const commercial = extractCommercialInputs(bill);
    const workGross = resolveGrossValueOfWorkDoneForCpwd(bill, commercial);
    const prevUptoCpwd = prevSnap ? prevSnap.upto_date_gross_value_of_work_done : 0;
    const prevSecInput = prevSnap ? prevSnap.sec_input : 0;
    const formula = calculateRABillAmounts({
      agreementAmount,
      uptoDateGross: workGross,
      grossValueOfWorkDone: workGross,
      previousUptoDateGrossCpwd: prevUptoCpwd,
      previousUptoDateSecuredAdvance: prevSecInput,
      isFirstBill: !prevSnap,
      previousUptoDateGross: Number(bill.previous_ra_upto_date_gross ?? 0),
      previousUptoDateGrossIncSecAdv: Number(bill.previous_ra_gross_inc_sec_adv ?? 0),
      secAdvanceMaterial: commercial.upto_date_secured_advance_on_material_brought_to_site,
      mobilisationAdvance: commercial.mobilization_advance,
      advancePaymentWorkdoneNotMeasured: commercial.advance_payment_work_done_but_not_measured,
      clause10cc10caPayment: commercial.clause_10cc_10ca_payment,
      raNo: parseRANumber(bill) || 1,
      additionalSd: bill.additional_sd_special_items,
      withheldItems: bill.withheld_items,
      rates: DEFAULT_RA_RATES,
      statutoryNetExclSecOverride: raBillStatutoryOverride(bill),
      securityDepositOverride: raBillSecurityDepositOverride(bill),
      ...raBillCpwdLine5Fallbacks(bill),
      billMeta: bill,
    });
    prevSnap = {
      upto_date_gross_value_of_work_done: formula.upto_date_gross_value_of_work_done,
      sec_input: commercial.upto_date_secured_advance_on_material_brought_to_site,
    };
  }

  return {
    previousUptoDateGrossCpwd: prevSnap ? prevSnap.upto_date_gross_value_of_work_done : 0,
    previousUptoDateSecuredAdvance: prevSnap ? prevSnap.sec_input : 0,
    isFirstBill: idx === 0,
    agreementAmount,
  };
}

/** Immediate previous RA in the same project (+ division) stream. */
function getPreviousBillInStream(targetBill, billList, projectFinancials) {
  const projectId = Number(targetBill.project_id || 0);
  const contractType = projectFinancials[projectId]?.contract_type || '';
  const division = targetBill.division || '';
  let stream = safeArray(billList).filter((b) => Number(b.project_id) === projectId);
  if (contractType === CONTRACT_TYPES.ITEM_RATE && division) {
    stream = stream.filter((b) => (b.division || '') === division);
  }
  const currentRa = parseRANumber(targetBill);
  return stream
    .filter((row) => parseRANumber(row) === currentRa - 1)
    .sort((a, b) => Number(b.id) - Number(a.id))[0] || null;
}

const normalizeLinkedBG = (row) => {
  const payload = row.data || {};
  return {
    id: row.id,
    project_id: row.projectId ?? row.project_id ?? 1,
    bg_type: payload.bg_type ?? row.bg_type ?? 'Security BG',
    purpose: payload.purpose ?? payload.bg_purpose ?? '',
    bg_no: row.bgNo ?? row.bg_no ?? payload.bg_no ?? `BG-${row.id}`,
    bank: payload.bank ?? row.bank ?? '-',
    amount: Number(row.amount ?? payload.amount ?? 0),
    issue_date: payload.issue_date ?? row.issue_date ?? '',
    expiry_date: payload.expiry_date ?? row.expiry_date ?? '',
    claim_expiry_date: payload.claim_expiry_date ?? row.claim_expiry_date ?? '',
    status: row.status || payload.status || 'Active',
    attachments: payload.attachments ?? row.attachments ?? [],
  };
};

const getBGTowardsLabel = (bg) => {
  const purpose = String(bg?.purpose || '').toLowerCase();
  const type = String(bg?.bg_type || '').toLowerCase();
  if (purpose.includes('performance') || type.includes('performance')) return 'P.G';
  if (purpose.includes('special') || purpose.includes('spl')) return 'Spl. Items S.D';
  if (purpose.includes('advance') || purpose.includes('insurance') || type.includes('insurance')) return 'Ins. for Sec. Adv';
  return 'S.D';
};

const formatCurrencyPlain = (value) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatPercentValue = (value) => {
  const num = Number(value || 0);
  const normalized = num > 0 && num < 1 ? num * 100 : num;
  return `${normalized.toFixed(2)}%`;
};
const formatDateOrPlaceholder = (value) => (value ? formatDateIN(value) : 'dd-mm-yyyy');

const normalizeRABillRow = (row) => {
  const payload = row.data || {};
  const withheldItems = normalizeWithheldItems(payload);
  const legacyWithheld = getLegacyWithheldAmounts(withheldItems);
  const commercial = extractCommercialInputs({
    ...payload,
    gross_value_of_work_done: payload.gross_value_of_work_done ?? payload.upto_date_gross_work_done ?? payload.gross_work_done,
  });
  const billForResolve = {
    ...payload,
    gross_work_done: payload.gross_work_done ?? row.gross_work_done,
  };
  const workGross = resolveGrossValueOfWorkDoneForCpwd(billForResolve, commercial)
    || Number(
      commercial.gross_value_of_work_done
        || payload.upto_date_gross_work_done
        || payload.gross_work_done
        || row.gross_work_done
        || row.amount
        || 0,
    );
  const previousGross = Number(payload.previous_ra_upto_date_gross ?? 0);
  const agreementAmount = Number(payload.agreement_amount ?? 0);
  const raNo = parseRANumber({ ...row, ...payload });
  const explicitPrevCpwd = payload.gross_value_as_per_previous_ra_bill;
  const formula = calculateRABillAmounts({
    agreementAmount,
    uptoDateGross: workGross,
    grossValueOfWorkDone: workGross,
    previousUptoDateGrossCpwd: explicitPrevCpwd != null && explicitPrevCpwd !== ''
      ? Number(explicitPrevCpwd)
      : undefined,
    previousUptoDateSecuredAdvance: payload.previous_ra_sec_advance_upto != null && payload.previous_ra_sec_advance_upto !== ''
      ? Number(payload.previous_ra_sec_advance_upto)
      : undefined,
    isFirstBill: (raNo || 1) <= 1,
    previousUptoDateGross: previousGross,
    previousUptoDateGrossIncSecAdv: Number(payload.previous_ra_gross_inc_sec_adv ?? previousGross ?? 0),
    secAdvanceMaterial: commercial.upto_date_secured_advance_on_material_brought_to_site,
    mobilisationAdvance: commercial.mobilization_advance,
    advancePaymentWorkdoneNotMeasured: commercial.advance_payment_work_done_but_not_measured,
    clause10cc10caPayment: commercial.clause_10cc_10ca_payment,
    raNo: raNo || 1,
    additionalSd: payload.additional_sd_special_items ?? 0,
    withheldItems,
    withheldMilestone: payload.withheld_milestone ?? 0,
    withheldMpr: payload.withheld_mpr ?? 0,
    withheldOther1: payload.withheld_other_1 ?? 0,
    withheldOther2: payload.withheld_other_2 ?? 0,
    statutoryNetExclSecOverride: raBillStatutoryOverride(payload),
    securityDepositOverride: raBillSecurityDepositOverride(payload),
    ...raBillCpwdLine5Fallbacks(payload),
      billMeta: payload,
  });
  const gross = Number(payload.gross_work_done ?? formula.gross_work_done ?? 0);
  const deductions = Number(payload.deductions_total ?? row.deductions_total ?? 0);
  const net = Number(payload.net_payable ?? row.net_payable ?? formula.net_payment_received ?? Math.max(0, gross - deductions));
  const paid = Number(payload.paid_value ?? row.paid_value ?? 0);
  const totalWithheld = Number(payload.total_withheld ?? formula.total_withheld);
  const totalRecoveries = Number(payload.total_recoveries ?? formula.total_recoveries);
  const actualRecovered = payload.actual_total_recovered_amount ?? payload.actual_recovered_amount ?? null;
  const division = payload.division ?? row.division ?? null;
  const releasedWithheldRecords = normalizeReleasedWithheldRecords(payload);
  return {
    id: row.id,
    project_id: row.projectId ?? row.project_id ?? 1,
    division,
    ra_no: payload.ra_no ?? row.ra_no ?? row.billNo ?? row.bill_no ?? row.id,
    ra_ref: payload.ra_ref ?? row.ra_ref ?? row.billNo ?? row.bill_no ?? `RA-${row.id}`,
    period_from: payload.period_from ?? row.period_from ?? '',
    period_to: payload.period_to ?? row.period_to ?? '',
    gross_work_done: gross,
    deductions_total: deductions,
    net_payable: net,
    status: row.status || payload.status || 'Submitted',
    payment_status: payload.payment_status ?? row.payment_status ?? 'Unpaid',
    payment_due_date: payload.payment_due_date ?? row.payment_due_date ?? null,
    payment_received_date: payload.payment_received_date ?? row.payment_received_date ?? null,
    paid_value: paid,
    balance_value: Number(payload.balance_value ?? row.balance_value ?? Math.max(0, net - paid)),
    submitted_date: payload.submitted_date ?? row.submitted_date ?? null,
    linked_correspondence: payload.linked_correspondence ?? row.linked_correspondence ?? null,
    approved_quantities: Number(payload.approved_quantities ?? row.approved_quantities ?? 0),
    linked_milestone_id: payload.linked_milestone_id ?? null,
    linked_eot_id: payload.linked_eot_id ?? null,
    linked_bg_ids: safeArray(payload.linked_bg_ids).map((id) => Number(id)).filter((id) => !Number.isNaN(id)),
    primary_bg_id: payload.primary_bg_id ?? null,
    billing_basis_type: payload.billing_basis_type ?? 'Quantity Based',
    billing_dependency_status: payload.billing_dependency_status ?? 'Clear',
    billing_hold_reason: payload.billing_hold_reason ?? '',
    withheld_due_to_milestone_flag: Boolean(payload.withheld_due_to_milestone_flag),
    withheld_due_to_eot_flag: Boolean(payload.withheld_due_to_eot_flag),
    related_correspondence_ids: safeArray(payload.related_correspondence_ids),
    primary_correspondence_chain_id: payload.primary_correspondence_chain_id ?? payload.linked_correspondence ?? row.linked_correspondence ?? null,
    agreement_amount: Number(payload.agreement_amount ?? 0),
    bills_paid_by_dept: Number(payload.bills_paid_by_dept ?? 0),
    agreement_no: payload.agreement_no ?? '',
    ra_bill_date: payload.ra_bill_date ?? payload.bill_date ?? row.ra_bill_date ?? row.bill_date ?? null,
    certified_date: payload.certified_date ?? null,
    remarks: payload.remarks ?? '',
    percent_workdone: Number(formula.percent_workdone ?? 0),
    gross_value_of_work_done: Number(payload.gross_value_of_work_done ?? workGross),
    upto_date_gross_work_done: Number(payload.upto_date_gross_work_done ?? workGross),
    upto_date_gross_value_of_work_done: Number(payload.upto_date_gross_value_of_work_done ?? formula.upto_date_gross_value_of_work_done ?? 0),
    gross_value_as_per_previous_ra_bill: Number(payload.gross_value_as_per_previous_ra_bill ?? formula.gross_value_as_per_previous_ra_bill ?? 0),
    secured_advance_added_in_this_bill: Number(payload.secured_advance_added_in_this_bill ?? formula.secured_advance_added_in_this_bill ?? 0),
    clause_10cc_10ca_payment: Number(payload.clause_10cc_10ca_payment ?? 0),
    previous_ra_sec_advance_upto: Number(payload.previous_ra_sec_advance_upto ?? 0),
    previous_ra_upto_date_gross: Number(payload.previous_ra_upto_date_gross ?? previousGross),
    sec_advance_material: Number(payload.sec_advance_material ?? commercial.upto_date_secured_advance_on_material_brought_to_site ?? 0),
    mobilisation_advance: Number(payload.mobilisation_advance ?? commercial.mobilization_advance ?? 0),
    advance_payment_workdone_not_measured: Number(payload.advance_payment_workdone_not_measured ?? commercial.advance_payment_work_done_but_not_measured ?? 0),
    upto_date_gross_inc_sec_adv: Number(payload.upto_date_gross_inc_sec_adv ?? formula.upto_date_gross_inc_sec_adv),
    previous_ra_gross_inc_sec_adv: Number(payload.previous_ra_gross_inc_sec_adv ?? formula.previous_ra_gross_inc_sec_adv),
    net_value_work_done_since_prev_bill: Number(payload.net_value_work_done_since_prev_bill ?? formula.net_value_work_done_since_prev_bill),
    net_value_work_done_without_sec_adv: Number(payload.net_value_work_done_without_sec_adv ?? formula.net_value_work_done_without_sec_adv),
    current_bill_value: Number(payload.current_bill_value ?? formula.current_bill_value),
    security_deposit: Number(payload.security_deposit ?? formula.security_deposit),
    gst_deduction: Number(payload.gst_deduction ?? formula.gst_deduction),
    income_tax_deduction: Number(payload.income_tax_deduction ?? formula.income_tax_deduction),
    labour_cess: Number(payload.labour_cess ?? formula.labour_cess),
    additional_sd_special_items: Number(payload.additional_sd_special_items ?? 0),
    total_recoveries: totalRecoveries,
    actual_total_recovered_amount: actualRecovered == null ? null : Number(actualRecovered || 0),
    total_withheld: totalWithheld,
    withheld_items: withheldItems,
    withheld_milestone: Number(payload.withheld_milestone ?? legacyWithheld.milestone),
    withheld_mpr: Number(payload.withheld_mpr ?? legacyWithheld.mpr),
    withheld_other_1: Number(payload.withheld_other_1 ?? legacyWithheld.other1),
    withheld_other_2: Number(payload.withheld_other_2 ?? legacyWithheld.other2),
    withheld_other_1_reason: payload.withheld_other_1_reason ?? '',
    withheld_other_2_reason: payload.withheld_other_2_reason ?? '',
    total_deductions: Number(payload.total_deductions ?? deductions),
    net_payment_received: Number(payload.net_payment_received ?? net),
    percent_financial_progress: Number(payload.percent_financial_progress ?? 0),
    payments: safeArray(payload.payments),
    released_withheld_records: releasedWithheldRecords,
    released_withheld_total: releasedWithheldRecords.reduce((s, r) => s + Number(r.amount || 0), 0),
    restructure_history: safeArray(payload.restructure_history),
    last_restructure_at: payload.last_restructure_at ?? null,
    last_restructure_by: payload.last_restructure_by ?? null,
    last_restructure_reason: payload.last_restructure_reason ?? '',
    attachments: payload.attachments ?? row.attachments ?? [],
        cpwd_statutory_recovery_base_override: Number(payload.cpwd_statutory_recovery_base_override ?? 0),
    department_id: payload.department_id ?? null,
    department_code: payload.department_code ?? null,
    department_name: payload.department_name ?? null,
    financial_year: payload.financial_year ?? null,
    department_rule_snapshot: payload.department_rule_snapshot ?? null,
    legacy_mode: payload.legacy_mode,
    calculation_mode: payload.calculation_mode ?? null,
    applied_recovery_rates: payload.applied_recovery_rates ?? null,
    ra_template_id: payload.ra_template_id ?? null,
    ra_template_code: payload.ra_template_code ?? null,
    ra_template_version: payload.ra_template_version ?? null,
    ra_template_snapshot: payload.ra_template_snapshot ?? null,
    calculation_engine: payload.calculation_engine ?? null,
    calculation_snapshot: payload.calculation_snapshot ?? null,
    template_field_inputs: payload.template_field_inputs && typeof payload.template_field_inputs === 'object'
      ? payload.template_field_inputs
      : {},
    template_field_values: payload.template_field_values && typeof payload.template_field_values === 'object'
      ? payload.template_field_values
      : {},
    previous_template_values: payload.previous_template_values && typeof payload.previous_template_values === 'object'
      ? payload.previous_template_values
      : {},
    additional_bill_rows: normalizeAdditionalBillRows(payload.additional_bill_rows),
    ...(Object.prototype.hasOwnProperty.call(payload, 'cpwd_security_deposit_override')
      ? { cpwd_security_deposit_override: Number(payload.cpwd_security_deposit_override) }
      : {}),
  };
};

/** Register/print date: show only explicit RA bill date; keep blank if not entered. */
const effectiveRABillDate = (bill) => (
  bill?.ra_bill_date
  || bill?.bill_date
  || null
);

const formatFinancialYearFromDate = (value) => {
  const parsed = value ? new Date(value) : new Date();
  if (Number.isNaN(parsed.getTime())) return '';
  const month = parsed.getMonth();
  const year = parsed.getFullYear();
  const start = month >= 3 ? year : year - 1;
  const end = String(start + 1).slice(-2);
  return `${start}-${end}`;
};

const formatFinancialYearLabelIN = (fyValue) => {
  const raw = String(fyValue || '').trim();
  if (!raw) return '';
  const m = raw.match(/^(\d{4})-(\d{2})$/);
  if (!m) return raw;
  const start = Number(m[1]);
  const end = start + 1;
  return `FY ${m[1]}-${m[2]} (01 Apr ${start} - 31 Mar ${end})`;
};

const createEmptyNewBill = () => ({
  project_id: '',
  status: 'Draft',
  period_from: '',
  period_to: '',
  agreement_amount: 0,
  agreement_no: '',
  ra_bill_date: '',
  certified_date: '',
  remarks: '',
  upto_date_gross_work_done: '',
  sec_advance_material: '',
  mobilisation_advance: '',
  advance_payment_workdone_not_measured: '',
  clause_10cc_10ca_payment: '',
  previous_ra_upto_date_gross: 0,
  previous_ra_gross_inc_sec_adv: 0,
  previous_ra_sec_advance_upto: 0,
  current_bill_value: 0,
  security_deposit: 0,
  gst_deduction: 0,
  income_tax_deduction: 0,
  labour_cess: 0,
  additional_sd_special_items: '',
  cpwd_statutory_recovery_base_override: 0,
  total_recoveries: 0,
  actual_total_recovered_amount: '',
  withheld_items: [],
  total_withheld: 0,
  total_deductions: 0,
  net_payment_received: 0,
  percent_workdone: 0,
  ra_no: '',
  bills_paid_by_dept: 0,
  gross_work_done: '',
  deductions_total: '',
  payment_due_date: '',
  billing_basis_type: 'Quantity Based',
  billing_dependency_status: 'Clear',
  linked_milestone_id: '',
  linked_eot_id: '',
  linked_bg_ids: [],
  primary_bg_id: '',
  linked_correspondence: '',
  related_correspondence_ids: [],
  billing_hold_reason: '',
  withheld_due_to_milestone_flag: false,
  withheld_due_to_eot_flag: false,
  division: '',
  attachments: [],
  additional_bill_rows: [],
  payment_status: 'Unpaid',
  paid_value: 0,
  balance_value: 0,
  ra_template_id: null,
  ra_template_code: null,
  ra_template_version: null,
  ra_template_snapshot: null,
  calculation_engine: null,
  calculation_snapshot: null,
  template_field_inputs: {},
  template_field_values: {},
  previous_template_values: {},
});

const RABillsRegister = ({ onViewThread, isAdmin }) => {
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [financialYearFilter, setFinancialYearFilter] = useState('all');
  const [registerDateFrom, setRegisterDateFrom] = useState('');
  const [registerDateTo, setRegisterDateTo] = useState('');
  const [billSearch, setBillSearch] = useState('');
  const [page, setPage] = useState(1);
  const [mainRegisterTab, setMainRegisterTab] = useState('register');
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [createFieldError, setCreateFieldError] = useState('');
  const [previousBillSnapshot, setPreviousBillSnapshot] = useState(null);
  const [createGeneratedAt, setCreateGeneratedAt] = useState('');
  const lastCreateStreamRef = useRef('');
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedBillTab, setSelectedBillTab] = useState('summary');
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [departmentRuleInfo, setDepartmentRuleInfo] = useState(null);
  const [departmentRuleLoading, setDepartmentRuleLoading] = useState(false);
  const [registerTemplateInfo, setRegisterTemplateInfo] = useState(null);
  const [registerTemplateLoading, setRegisterTemplateLoading] = useState(false);
  const [raBills, setRaBills] = useState([]);
  const [projectFinancials, setProjectFinancials] = useState({});
  const [projectChains, setProjectChains] = useState([]);
  const [projectBGs, setProjectBGs] = useState([]);
  const [selectedBillChains, setSelectedBillChains] = useState([]);
  const [selectedBillBGs, setSelectedBillBGs] = useState([]);
  const [raControlSaving, setRaControlSaving] = useState(false);
  const [restructureMode, setRestructureMode] = useState(false);
  const [restructureReason, setRestructureReason] = useState('');
  const [customValues, setCustomValues] = useState({});
  const formConfig = useFormConfiguration({ pageKey: 'ra_bills', formKey: 'create_ra_bill', mode: 'create' });
  const customFields = formConfig.customFields || [];

  const defaultsFromCustomFields = () => {
    const out = {};
    for (const f of customFields) {
      if (f.defaultValue != null && f.defaultValue !== '') out[f.fieldKey] = f.defaultValue;
      else if (f.fieldType === 'boolean') out[f.fieldKey] = false;
      else out[f.fieldKey] = '';
    }
    return out;
  };

  const onCustomChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  };
  const [newBill, setNewBill] = useState(createEmptyNewBill);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [paymentAttachmentUploading, setPaymentAttachmentUploading] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentEditIndex, setPaymentEditIndex] = useState(null);
  const [raPrintPreview, setRaPrintPreview] = useState(null);
  const [previewDownloading, setPreviewDownloading] = useState(false);
  const [newPayment, setNewPayment] = useState(emptyNewPaymentState);
  const perPage = 10;
  const tableRef = useRef(null);
  const printPreviewFrameRef = useRef(null);

  const getProjectRates = (projectId) => {
    const pid = Number(projectId || 0);
    const configured = projectFinancials?.[pid]?.ra_deduction_rates;
    return configured && typeof configured === 'object'
      ? {
          security_deposit_pct: configured.security_deposit_pct ?? DEFAULT_RA_RATES.security_deposit_pct,
          gst_pct: configured.gst_pct ?? DEFAULT_RA_RATES.gst_pct,
          income_tax_pct: configured.income_tax_pct ?? DEFAULT_RA_RATES.income_tax_pct,
          labour_cess_pct: configured.labour_cess_pct ?? DEFAULT_RA_RATES.labour_cess_pct,
        }
      : DEFAULT_RA_RATES;
  };

  useEffect(() => {
    const loadRABills = async () => {
      try {
        const response = await raBillsAPI.getAll({ page: 1, pageSize: 100 });
        const { rows: rawRows } = unwrapListResponse(response);
        const rows = safeArray(rawRows).map(normalizeRABillRow);
        if (rows.length > 0) setRaBills(rows);
      } catch (error) {
        console.warn('RA Bills API unavailable, using local data:', error?.message || error);
      } finally {
        setDataReady(true);
      }
    };
    loadRABills();
  }, []);

  useEffect(() => {
    const loadProjectFinancials = async () => {
      try {
        const response = await adminAPI.getProjects();
        const rows = safeArray(response?.data?.data ?? response?.data);
        const mapped = {};
        rows.forEach((row) => {
          const rates = row?.metadata?.ra_deduction_rates || row?.metadata?.scope?.ra_deduction_rates || {};
          const scope = row?.metadata?.scope || {};
          mapped[row.id] = {
            agreement_amount: Number(row?.metadata?.scope?.agreement_value ?? 0),
            project_name: row?.name || `Project ${row.id}`,
            contract_type: scope.contract_type || row?.metadata?.contract_type || '',
            ra_deduction_rates: {
              security_deposit_pct: rates.security_deposit_pct ?? DEFAULT_RA_RATES.security_deposit_pct,
              gst_pct: rates.gst_pct ?? DEFAULT_RA_RATES.gst_pct,
              income_tax_pct: rates.income_tax_pct ?? DEFAULT_RA_RATES.income_tax_pct,
              labour_cess_pct: rates.labour_cess_pct ?? DEFAULT_RA_RATES.labour_cess_pct,
            },
          };
        });
        setProjectFinancials(mapped);
      } catch (_error) {
        setProjectFinancials({});
      }
    };
    loadProjectFinancials();
  }, []);

  useEffect(() => {
    if (!selectedBill) return;
    setSelectedBillTab('summary');
    setPaymentEditIndex(null);
    setNewPayment(emptyNewPaymentState());
    setRestructureMode(false);
    setRestructureReason('');
  }, [selectedBill?.id]);

  useEffect(() => {
    if (!showCreatePage || !newBill.project_id) {
      setDepartmentRuleInfo(null);
      return;
    }
    let cancelled = false;
    const loadRule = async () => {
      setDepartmentRuleLoading(true);
      try {
        const billDate = newBill.ra_bill_date || new Date().toISOString().slice(0, 10);
        const resp = await raBillsAPI.resolveRule({
          projectId: Number(newBill.project_id),
          billDate,
        });
        if (cancelled) return;
        const resolved = resp?.data || null;
        setDepartmentRuleInfo(resolved);
        setNewBill((prev) => {
          const next = applyResolvedTemplateToBill(prev, resolved || {});
          // Refresh previous template values against new snapshot + known previous bill
          const prevValues = buildPreviousTemplateValues(
            previousBillSnapshot,
            next.ra_template_snapshot,
          );
          return {
            ...next,
            previous_template_values: prevValues,
          };
        });
      } catch (error) {
        if (!cancelled) {
          setDepartmentRuleInfo(null);
          setNewBill((prev) => applyResolvedTemplateToBill(prev, {}));
        }
      } finally {
        if (!cancelled) setDepartmentRuleLoading(false);
      }
    };
    loadRule();
    return () => { cancelled = true; };
  }, [showCreatePage, newBill.project_id, newBill.ra_bill_date]);

  useEffect(() => {
    if (!newBill.project_id) {
      setPreviousBillSnapshot(null);
      lastCreateStreamRef.current = '';
      return;
    }
    const projectId = Number(newBill.project_id);
    const contractType = projectFinancials[projectId]?.contract_type || '';
    const division = newBill.division || '';
    let projectBills = safeArray(raBills).filter((bill) => Number(bill.project_id) === projectId);
    if (contractType === CONTRACT_TYPES.ITEM_RATE && division) {
      projectBills = projectBills.filter((bill) => (bill.division || '') === division);
    }
    const nextRaNo = (projectBills.reduce((max, bill) => Math.max(max, parseRANumber(bill)), 0) || 0) + 1;
    const sortedBills = projectBills.slice().sort((a, b) => parseRANumber(a) - parseRANumber(b));
    const previousBill = sortedBills.find((bill) => parseRANumber(bill) === nextRaNo - 1) || null;
    const agreementAmount = Number(projectFinancials[projectId]?.agreement_amount ?? 0);
    const prevCommercialBase = previousBill ? extractCommercialInputs(previousBill) : null;
    const prevWork = previousBill && prevCommercialBase
      ? resolveGrossValueOfWorkDoneForCpwd(previousBill, prevCommercialBase)
      : 0;
    const prevCommercial = prevCommercialBase
      ? { ...prevCommercialBase, gross_value_of_work_done: prevWork }
      : null;
    const prevGrossCpwd = prevCommercial ? sumUptoDateGrossValue(prevCommercial) : 0;
    const prevSecInput = prevCommercial?.upto_date_secured_advance_on_material_brought_to_site ?? 0;
    const streamKey = `${projectId}:${division || ''}`;
    const streamChanged = lastCreateStreamRef.current !== streamKey;
    lastCreateStreamRef.current = streamKey;
    setPreviousBillSnapshot(previousBill);
    setNewBill((prev) => {
      const carriedRows = carryForwardAdditionalRows(
        previousBill?.additional_bill_rows,
        streamChanged ? [] : prev.additional_bill_rows,
      );
      const prevTemplateValues = buildPreviousTemplateValues(
        previousBill,
        prev.ra_template_snapshot,
      );
      if (
        Number(prev.ra_no || 0) === nextRaNo
        && Number(prev.previous_ra_gross_inc_sec_adv || 0) === prevGrossCpwd
        && Number(prev.previous_ra_sec_advance_upto || 0) === prevSecInput
        && Number(prev.agreement_amount || 0) === agreementAmount
        && JSON.stringify(normalizeAdditionalBillRows(prev.additional_bill_rows)) === JSON.stringify(carriedRows)
        && JSON.stringify(prev.previous_template_values || {}) === JSON.stringify(prevTemplateValues)
      ) {
        return prev;
      }
      return {
        ...prev,
        ra_no: nextRaNo,
        bills_paid_by_dept: Math.max(0, nextRaNo - 1),
        previous_ra_upto_date_gross: prevGrossCpwd,
        previous_ra_gross_inc_sec_adv: prevGrossCpwd,
        previous_ra_sec_advance_upto: prevSecInput,
        agreement_amount: agreementAmount,
        additional_bill_rows: carriedRows,
        previous_template_values: prevTemplateValues,
      };
    });
  }, [newBill.project_id, newBill.division, projectFinancials, raBills]);

  useEffect(() => {
    const nextRa = Number(newBill.ra_no || 1);
    const nbCommercial = extractCommercialInputs(newBill);
    const workGross = resolveGrossValueOfWorkDoneForCpwd(newBill, nbCommercial);
    const departmentRates = departmentRuleInfo?.rule
      ? {
          security_deposit_pct: Number(departmentRuleInfo.rule.security_deposit_pct ?? DEFAULT_RA_RATES.security_deposit_pct),
          gst_pct: Number(departmentRuleInfo.rule.gst_pct ?? DEFAULT_RA_RATES.gst_pct),
          income_tax_pct: Number(departmentRuleInfo.rule.income_tax_pct ?? DEFAULT_RA_RATES.income_tax_pct),
          labour_cess_pct: Number(departmentRuleInfo.rule.labour_cess_pct ?? DEFAULT_RA_RATES.labour_cess_pct),
        }
      : null;
    const shouldUseRuleRates = ENABLE_DEPARTMENT_RULE_RATES_FOR_NEW_BILLS
      && Boolean(departmentRates)
      && departmentRuleInfo?.legacy_mode === false;
    const templateInputs = isConfigurableSnapshot(newBill)
      ? buildTemplateInputs(newBill)
      : null;
    const previousTemplateValues = isConfigurableSnapshot(newBill)
      ? (newBill.previous_template_values || buildPreviousTemplateValues(previousBillSnapshot, newBill.ra_template_snapshot))
      : null;
    const formula = calculateRABillAmounts({
      agreementAmount: newBill.agreement_amount,
      uptoDateGross: workGross,
      grossValueOfWorkDone: workGross,
      previousUptoDateGrossCpwd: newBill.previous_ra_gross_inc_sec_adv,
      previousUptoDateSecuredAdvance: newBill.previous_ra_sec_advance_upto,
      isFirstBill: nextRa <= 1,
      previousUptoDateGross: newBill.previous_ra_upto_date_gross,
      previousUptoDateGrossIncSecAdv: newBill.previous_ra_gross_inc_sec_adv ?? newBill.previous_ra_upto_date_gross,
      secAdvanceMaterial: newBill.sec_advance_material,
      mobilisationAdvance: newBill.mobilisation_advance,
      advancePaymentWorkdoneNotMeasured: newBill.advance_payment_workdone_not_measured,
      clause10cc10caPayment: newBill.clause_10cc_10ca_payment,
      raNo: nextRa,
      additionalSd: newBill.additional_sd_special_items,
      withheldItems: newBill.withheld_items,
      rates: shouldUseRuleRates ? departmentRates : getProjectRates(newBill.project_id),
      useRateOverrides: shouldUseRuleRates,
      statutoryNetExclSecOverride: raBillStatutoryOverride(newBill),
      securityDepositOverride: raBillSecurityDepositOverride(newBill),
      ...raBillCpwdLine5Fallbacks(newBill),
      billMeta: newBill,
      templateInputs,
      previousTemplateValues,
      departmentValues: buildDepartmentValuesFromRule(departmentRuleInfo),
    });
    setNewBill((prev) => ({
      ...prev,
      upto_date_gross_inc_sec_adv: formula.upto_date_gross_inc_sec_adv,
      upto_date_gross_value_of_work_done: formula.upto_date_gross_value_of_work_done,
      gross_value_as_per_previous_ra_bill: formula.gross_value_as_per_previous_ra_bill,
      secured_advance_added_in_this_bill: formula.secured_advance_added_in_this_bill,
      previous_ra_gross_inc_sec_adv: formula.previous_ra_gross_inc_sec_adv,
      net_value_work_done_since_prev_bill: formula.net_value_work_done_since_prev_bill,
      net_value_work_done_without_sec_adv: formula.net_value_work_done_without_sec_adv,
      current_bill_value: formula.current_bill_value,
      gross_work_done: formula.gross_work_done,
      security_deposit: formula.security_deposit,
      gst_deduction: formula.gst_deduction,
      income_tax_deduction: formula.income_tax_deduction,
      labour_cess: formula.labour_cess,
      total_recoveries: formula.total_recoveries,
      total_withheld: formula.total_withheld,
      total_deductions: formula.total_deductions,
      deductions_total: formula.total_deductions,
      net_payment_received: formula.net_payment_received,
      net_payable: formula.net_payment_received,
      percent_workdone: formula.percent_workdone,
      template_field_values: formula._templateFieldValues || prev.template_field_values || {},
      calculation_snapshot: formula._templateFieldValues
        ? {
            engine: 'CONFIGURABLE',
            fieldValues: formula._templateFieldValues,
            effectiveFieldValues: formula._templateFieldValues,
            at: new Date().toISOString(),
          }
        : prev.calculation_snapshot,
    }));
  }, [
    newBill.additional_sd_special_items,
    newBill.agreement_amount,
    newBill.previous_ra_upto_date_gross,
    newBill.previous_ra_gross_inc_sec_adv,
    newBill.previous_ra_sec_advance_upto,
    newBill.upto_date_gross_work_done,
    newBill.sec_advance_material,
    newBill.mobilisation_advance,
    newBill.advance_payment_workdone_not_measured,
    newBill.clause_10cc_10ca_payment,
    newBill.ra_no,
    newBill.withheld_items,
    newBill.project_id,
    newBill.cpwd_statutory_recovery_base_override,
    newBill.cpwd_security_deposit_override,
    newBill.ra_template_snapshot,
    newBill.calculation_engine,
    newBill.template_field_inputs,
    newBill.previous_template_values,
    projectFinancials,
    departmentRuleInfo,
    previousBillSnapshot,
  ]);

  useEffect(() => {
    const projectId = selectedBill?.project_id || newBill.project_id;
    if (!projectId) {
      setProjectChains([]);
      return;
    }
    let cancelled = false;
    const loadChains = async () => {
      try {
        const resp = await correspondenceAPI.getChainsWithEntries({
          projectId,
          pageSize: 100,
          page: 1,
        });
        const chains = safeArray(resp?.data?.chains);
        const entries = safeArray(resp?.data?.entries);
        const chainsWithEntries = chains.map((chain) => ({
          ...chain,
          entries: entries.filter((entry) => entry.chain_id === chain.id),
        }));
        if (!cancelled) setProjectChains(chainsWithEntries);
      } catch (error) {
        if (!cancelled) setProjectChains([]);
      }
    };
    loadChains();
    return () => { cancelled = true; };
  }, [selectedBill?.project_id, newBill.project_id]);

  useEffect(() => {
    const projectId = selectedBill?.project_id || newBill.project_id;
    if (!projectId) {
      setProjectBGs([]);
      return;
    }
    let cancelled = false;
    const loadProjectBGs = async () => {
      try {
        const response = await bankGuaranteesAPI.getAll({ projectId, page: 1, pageSize: 200 });
        const { rows: rawRows } = unwrapListResponse(response);
        if (!cancelled) setProjectBGs(safeArray(rawRows).map(normalizeLinkedBG));
      } catch (_error) {
        if (!cancelled) setProjectBGs([]);
      }
    };
    loadProjectBGs();
    return () => { cancelled = true; };
  }, [selectedBill?.project_id, newBill.project_id]);

  useEffect(() => {
    if (!selectedBill) {
      setSelectedBillChains([]);
      return;
    }
    const relevantIds = new Set([
      selectedBill.linked_correspondence,
      selectedBill.primary_correspondence_chain_id,
      ...safeArray(selectedBill.related_correspondence_ids),
    ].filter(Boolean).map(Number));
    if (relevantIds.size === 0) {
      setSelectedBillChains([]);
      return;
    }
    setSelectedBillChains(projectChains.filter((chain) => relevantIds.has(Number(chain.id))));
  }, [projectChains, selectedBill]);

  useEffect(() => {
    if (!selectedBill) {
      setSelectedBillBGs([]);
      return;
    }
    const relevantIds = new Set([
      selectedBill.primary_bg_id,
      ...safeArray(selectedBill.linked_bg_ids),
    ].filter(Boolean).map(Number));
    if (relevantIds.size === 0) {
      setSelectedBillBGs([]);
      return;
    }
    setSelectedBillBGs(projectBGs.filter((bg) => relevantIds.has(Number(bg.id))));
  }, [projectBGs, selectedBill]);

  const projects = safeArray(projectOptions);
  const financialYearOptions = useMemo(() => {
    const years = new Set();
    safeArray(raBills).forEach((bill) => {
      const fy = String(
        bill.financial_year
        || formatFinancialYearFromDate(effectiveRABillDate(bill)),
      ).trim();
      if (fy) years.add(fy);
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [raBills]);

  const computedBills = useMemo(() => {
    const grouped = new Map();
    safeArray(raBills).forEach((bill) => {
      const projectId = Number(bill.project_id || 0);
      const contractType = projectFinancials[projectId]?.contract_type || '';
      const division = bill.division || '';
      const groupKey = contractType === CONTRACT_TYPES.ITEM_RATE ? `${projectId}:${division}` : String(projectId);
      if (!grouped.has(groupKey)) grouped.set(groupKey, []);
      grouped.get(groupKey).push({ ...bill });
    });
    const enriched = [];
    grouped.forEach((projectBills, groupKey) => {
      const projectId = Number(String(groupKey).split(':')[0]);
      const agreementAmount = Number(projectFinancials[projectId]?.agreement_amount ?? 0);
      const contractType = projectFinancials[projectId]?.contract_type || '';
      const sampleBill = projectBills[0];
      const division = sampleBill?.division || '';
      const sorted = projectBills.slice().sort((a, b) => parseRANumber(a) - parseRANumber(b));
      let prevSnap = null;
      sorted.forEach((bill) => {
        const raNo = parseRANumber(bill);
        const commercial = extractCommercialInputs(bill);
        const workGross = resolveGrossValueOfWorkDoneForCpwd(bill, commercial);
        const raRef = contractType === CONTRACT_TYPES.ITEM_RATE && division
          ? `RA-${division}-${raNo || bill.id}`
          : `RA-${raNo || bill.id}`;

        // Frozen configurable bills: keep stored template amounts until user edits in UI.
        // Department template changes must not rewrite historical register rows.
        if (hasFrozenTemplateCalculation(bill)) {
          const storedNet = Number(bill.net_payment_received ?? bill.net_payable ?? 0);
          enriched.push({
            ...bill,
            ra_no: raNo || bill.ra_no,
            ra_ref: bill.ra_ref || raRef,
            agreement_amount: agreementAmount || bill.agreement_amount,
            bills_paid_by_dept: Math.max(0, (raNo || 1) - 1),
            balance_value: Math.max(0, storedNet - Number(bill.paid_value || 0)),
          });
          prevSnap = {
            upto_date_gross_value_of_work_done: Number(
              bill.upto_date_gross_value_of_work_done
              ?? bill.upto_date_gross_inc_sec_adv
              ?? 0,
            ),
            sec_input: commercial.upto_date_secured_advance_on_material_brought_to_site,
          };
          return;
        }

        const prevUptoCpwd = prevSnap ? prevSnap.upto_date_gross_value_of_work_done : 0;
        const prevSecInput = prevSnap ? prevSnap.sec_input : 0;
        const formula = calculateRABillAmounts({
          agreementAmount,
          uptoDateGross: workGross,
          grossValueOfWorkDone: workGross,
          previousUptoDateGrossCpwd: prevUptoCpwd,
          previousUptoDateSecuredAdvance: prevSecInput,
          isFirstBill: !prevSnap,
          previousUptoDateGross: Number(bill.previous_ra_upto_date_gross ?? 0),
          previousUptoDateGrossIncSecAdv: Number(bill.previous_ra_gross_inc_sec_adv ?? 0),
          secAdvanceMaterial: commercial.upto_date_secured_advance_on_material_brought_to_site,
          mobilisationAdvance: commercial.mobilization_advance,
          advancePaymentWorkdoneNotMeasured: commercial.advance_payment_work_done_but_not_measured,
          clause10cc10caPayment: commercial.clause_10cc_10ca_payment,
          raNo: raNo || 1,
          additionalSd: bill.additional_sd_special_items,
          withheldItems: bill.withheld_items,
          rates: getProjectRates(projectId),
          statutoryNetExclSecOverride: raBillStatutoryOverride(bill),
          securityDepositOverride: raBillSecurityDepositOverride(bill),
          ...raBillCpwdLine5Fallbacks(bill),
          billMeta: bill,
        });
        enriched.push({
          ...bill,
          ra_no: raNo || bill.ra_no,
          ra_ref: bill.ra_ref || raRef,
          agreement_amount: agreementAmount,
          bills_paid_by_dept: Math.max(0, (raNo || 1) - 1),
          upto_date_gross_value_of_work_done: formula.upto_date_gross_value_of_work_done,
          gross_value_as_per_previous_ra_bill: formula.gross_value_as_per_previous_ra_bill,
          secured_advance_added_in_this_bill: formula.secured_advance_added_in_this_bill,
          previous_ra_sec_advance_upto: prevSecInput,
          previous_ra_upto_date_gross: formula.gross_value_as_per_previous_ra_bill,
          upto_date_gross_inc_sec_adv: formula.upto_date_gross_inc_sec_adv,
          previous_ra_gross_inc_sec_adv: formula.previous_ra_gross_inc_sec_adv,
          net_value_work_done_since_prev_bill: formula.net_value_work_done_since_prev_bill,
          net_value_work_done_without_sec_adv: formula.net_value_work_done_without_sec_adv,
          sec_advance_material: formula.sec_advance_material,
          mobilisation_advance: formula.mobilisation_advance,
          advance_payment_workdone_not_measured: formula.advance_payment_workdone_not_measured,
          clause_10cc_10ca_payment: formula.clause_10cc_10ca_payment,
          percent_workdone: formula.percent_workdone,
          current_bill_value: formula.current_bill_value,
          gross_work_done: formula.gross_work_done,
          security_deposit: formula.security_deposit,
          gst_deduction: formula.gst_deduction,
          income_tax_deduction: formula.income_tax_deduction,
          labour_cess: formula.labour_cess,
          total_recoveries: formula.total_recoveries,
          total_withheld: formula.total_withheld,
          deductions_total: formula.total_deductions,
          total_deductions: formula.total_deductions,
          net_payable: formula.net_payment_received,
          net_payment_received: formula.net_payment_received,
          balance_value: Math.max(0, formula.net_payment_received - Number(bill.paid_value || 0)),
        });
        prevSnap = {
          upto_date_gross_value_of_work_done: formula.upto_date_gross_value_of_work_done,
          sec_input: commercial.upto_date_secured_advance_on_material_brought_to_site,
        };
      });
    });
    return enriched.sort((a, b) => Number(b.project_id || 0) - Number(a.project_id || 0) || parseRANumber(b) - parseRANumber(a));
  }, [projectFinancials, raBills]);

  const filteredBills = useMemo(() => {
    // Register is project-scoped: no table until a project is selected.
    if (!projectFilter || projectFilter === 'all') return [];
    let bills = [...safeArray(computedBills)];
    bills = bills.filter((b) => b.project_id === parseInt(projectFilter, 10));
    if (statusFilter !== 'all') bills = bills.filter(b => b.status === statusFilter);
    if (paymentFilter !== 'all') bills = bills.filter(b => b.payment_status === paymentFilter);
    if (riskFilter === 'overdue') bills = bills.filter((bill) => getIssueFlags(bill).includes('Payment Overdue'));
    if (riskFilter === 'certified_unpaid') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      bills = bills.filter((bill) => {
        const refDate = effectiveRABillDate(bill);
        const subDate = refDate ? new Date(refDate) : null;
        if (!subDate) return false;
        const certDate = new Date(subDate);
        certDate.setDate(certDate.getDate() + 7);
        certDate.setHours(0, 0, 0, 0);
        const pay = String(bill.payment_status || '').toLowerCase();
        const hasBalance = Number(bill.balance_value || 0) > 0;
        return certDate <= today && (pay === 'unpaid' || (pay === 'part paid' && hasBalance));
      });
    }
    if (riskFilter === 'dependency') bills = bills.filter((bill) => bill.billing_dependency_status && bill.billing_dependency_status !== 'Clear');
    if (riskFilter === 'withheld') bills = bills.filter((bill) => bill.payment_status === 'Withheld' || bill.withheld_due_to_eot_flag || bill.withheld_due_to_milestone_flag);
    if (financialYearFilter !== 'all') {
      bills = bills.filter((bill) => {
        const fy = String(
          bill.financial_year
          || formatFinancialYearFromDate(effectiveRABillDate(bill)),
        ).trim();
        return fy === financialYearFilter;
      });
    }
    if (registerDateFrom) {
      const from = new Date(registerDateFrom);
      from.setHours(0, 0, 0, 0);
      bills = bills.filter((bill) => {
        const raw = effectiveRABillDate(bill);
        if (!raw) return false;
        const d = new Date(raw);
        d.setHours(0, 0, 0, 0);
        return d >= from;
      });
    }
    if (registerDateTo) {
      const to = new Date(registerDateTo);
      to.setHours(23, 59, 59, 999);
      bills = bills.filter((bill) => {
        const raw = effectiveRABillDate(bill);
        if (!raw) return false;
        const d = new Date(raw);
        return d <= to;
      });
    }
    if (billSearch.trim()) {
      const q = billSearch.trim().toLowerCase();
      bills = bills.filter((bill) => (
        String(bill.ra_no ?? '').toLowerCase().includes(q)
        || String(bill.ra_ref ?? '').toLowerCase().includes(q)
        || String(bill.bill_no ?? '').toLowerCase().includes(q)
      ));
    }
    return bills;
  }, [computedBills, projectFilter, statusFilter, paymentFilter, riskFilter, financialYearFilter, registerDateFrom, registerDateTo, billSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredBills.length / perPage));
  const paginatedBills = filteredBills.slice((page - 1) * perPage, page * perPage);
  const hasProjectSelected = Boolean(projectFilter) && projectFilter !== 'all';
  const showProjectColumn = false;
  const selectedRegisterProject = hasProjectSelected
    ? projects.find((p) => Number(p.id) === Number(projectFilter))
    : null;

  const registerBillsOrdered = useMemo(() => (
    [...safeArray(filteredBills)].sort((a, b) => parseRANumber(b) - parseRANumber(a))
  ), [filteredBills]);

  const registerSectionMatrices = useMemo(() => {
    if (!hasProjectSelected) return { mode: 'none', sections: [], sectionColumns: [], bills: [] };
    const bills = registerBillsOrdered;
    const latestConfigurable = [...bills].reverse().find((b) => isConfigurableSnapshot(b));
    const resolveSnap = registerTemplateInfo?.ra_template?.snapshot;
    const snap = latestConfigurable?.ra_template_snapshot
      || (resolveSnap && isConfigurableSnapshot({
        calculation_engine: registerTemplateInfo?.ra_template?.calculationEngine || 'CONFIGURABLE',
        ra_template_snapshot: resolveSnap,
      }) ? resolveSnap : null);

    const buildFromGrouped = (grouped, mode, templateName) => {
      const sections = grouped.map((section) => {
        const fields = section.fields.map((f) => ({
          fieldCode: f.fieldCode,
          fieldLabel: f.fieldLabel || f.fieldCode,
          fieldType: String(f.fieldType || 'INPUT').toUpperCase(),
          valuesByBillId: Object.fromEntries(
            bills.map((bill) => {
              const value = mode === 'cpwd'
                ? getCpwdRegisterFieldValue(bill, f.fieldCode)
                : (Number(getTemplateFieldValueFromBill(bill, f)) || 0);
              return [String(bill.id), value];
            }),
          ),
        }));
        return {
          key: section.key,
          sectionName: section.sectionName,
          fields,
        };
      });
      const sectionColumns = sections.map((section) => {
        const totalField = pickSectionTotalField(section.fields);
        return {
          key: section.key,
          sectionName: section.sectionName,
          fieldCode: totalField?.fieldCode || null,
          fieldLabel: totalField?.fieldLabel || section.sectionName,
          valuesByBillId: totalField?.valuesByBillId || {},
        };
      });
      return { mode, templateName, sections, sectionColumns, bills };
    };

    if (snap && Array.isArray(snap.fields) && snap.fields.length > 0) {
      const grouped = groupTemplateFieldsBySection(snap);
      return buildFromGrouped(
        grouped,
        'template',
        snap.name || registerTemplateInfo?.ra_template?.name || latestConfigurable?.ra_template_code || 'Template',
      );
    }

    return buildFromGrouped(
      CPWD_REGISTER_SECTIONS.map((s) => ({
        key: s.key,
        sectionName: s.sectionName,
        fields: s.fields,
      })),
      'cpwd',
      'CPWD Standard',
    );
  }, [hasProjectSelected, registerBillsOrdered, registerTemplateInfo]);

  useEffect(() => {
    if (!hasProjectSelected) {
      setRegisterTemplateInfo(null);
      setRegisterTemplateLoading(false);
      return undefined;
    }
    let cancelled = false;
    const loadTemplate = async () => {
      setRegisterTemplateLoading(true);
      try {
        const resp = await raBillsAPI.resolveRule({
          projectId: Number(projectFilter),
          billDate: new Date().toISOString().slice(0, 10),
        });
        if (!cancelled) setRegisterTemplateInfo(resp?.data || null);
      } catch (_error) {
        if (!cancelled) setRegisterTemplateInfo(null);
      } finally {
        if (!cancelled) setRegisterTemplateLoading(false);
      }
    };
    loadTemplate();
    return () => { cancelled = true; };
  }, [hasProjectSelected, projectFilter]);

  const pageTotals = useMemo(() => (
    paginatedBills.reduce((acc, bill) => ({
      netSince: acc.netSince + Number(bill.net_value_work_done_since_prev_bill ?? 0),
      gross: acc.gross + Number(bill.gross_work_done || 0),
      deductions: acc.deductions + Number(bill.deductions_total || 0),
      net: acc.net + Number(bill.net_payable || 0),
      balance: acc.balance + Number(bill.balance_value || 0),
    }), {
      netSince: 0,
      gross: 0,
      deductions: 0,
      net: 0,
      balance: 0,
    })
  ), [paginatedBills]);

  /** Cumulative upto-date gross: sum of latest RA per stream (project, or per division for item-rate). */
  const filteredRegisterLatestUptoGross = useMemo(
    () => sumLatestGrossIncSecAdvPerStream(filteredBills, projectFinancials),
    [filteredBills, projectFinancials],
  );

  const raKpis = useMemo(() => {
    const today = new Date();
    const scoped = [...safeArray(filteredBills)];

    const totalBills = scoped.length;
    const paymentState = (b) => String(b.payment_status || '').toLowerCase();
    const paidBills = scoped.filter((b) => paymentState(b) === 'paid');
    const partPaidBills = scoped.filter((b) => paymentState(b) === 'part paid');
    const unpaidBills = scoped.filter((b) => paymentState(b) === 'unpaid' || paymentState(b) === 'withheld');
    const paidCount = paidBills.length;
    const partPaidCount = partPaidBills.length;
    const unpaidCount = unpaidBills.length;

    /** Matches register column "Net payment (₹)": net_payable with net_payment_received fallback. */
    const totalNetPaymentSum = scoped.reduce(
      (sum, b) => sum + Number(b.net_payable ?? b.net_payment_received ?? 0),
      0,
    );

    const totalNet = scoped.reduce((sum, b) => sum + Number(b.net_payable || 0), 0);
    const totalPaid = scoped.reduce((sum, b) => sum + Number(b.paid_value || 0), 0);
    const totalDeductions = scoped.reduce(
      (sum, b) => sum + Number(b.total_deductions ?? b.deductions_total ?? 0),
      0,
    );

    let rwTotalRecoveries = 0;
    let rwTotalWithheld = 0;
    let rwTotalPendingWithheld = 0;
    let rwBillsWithWithheld = 0;
    let rwBillsWithPendingWithheld = 0;
    let rwBillsWithReleased = 0;
    for (const b of scoped) {
      rwTotalRecoveries += Number(b.total_recoveries || 0);
      const wh = Number(b.total_withheld || 0);
      rwTotalWithheld += wh;
      if (wh > 0) rwBillsWithWithheld += 1;
      const rel = sumReleasedWithheldRecords(b);
      if (rel > 0) rwBillsWithReleased += 1;
      const pending = Math.max(0, wh - rel);
      rwTotalPendingWithheld += pending;
      if (pending > 0) rwBillsWithPendingWithheld += 1;
    }

    const pendingCertBills = scoped.filter((b) => {
      const status = String(b.status || '').toLowerCase();
      return status === 'submitted';
    });
    const pendingCertValue = pendingCertBills.reduce((sum, b) => sum + Number(b.net_payable || 0), 0);

    const pendingPayBills = scoped.filter((b) => {
      const pay = String(b.payment_status || '').toLowerCase();
      return (pay === 'unpaid' || pay === 'part paid') && Number(b.balance_value || 0) > 0;
    });
    const pendingPayValue = pendingPayBills.reduce((sum, b) => sum + Number(b.balance_value || 0), 0);

    const pendingPayByProject = (() => {
      const m = {};
      pendingPayBills.forEach((b) => {
        const pid = Number(b.project_id);
        if (!pid) return;
        const bal = Number(b.balance_value || 0);
        if (bal <= 0) return;
        const name = stripBrackets(projects.find((p) => Number(p.id) === pid)?.name || '') || `Project ${pid}`;
        if (!m[pid]) m[pid] = { projectId: pid, projectName: name, bills: 0, amount: 0 };
        m[pid].amount += bal;
        m[pid].bills += 1;
      });
      return Object.values(m).sort((a, b) => b.amount - a.amount);
    })();

    const certifiedUnpaidOverdue = scoped.filter((b) => {
      const pay = String(b.payment_status || '').toLowerCase();
      if (pay === 'paid') return false;
      const refRaw = effectiveRABillDate(b);
      const subDate = refRaw ? new Date(refRaw) : null;
      if (!subDate) return false;
      const certDate = new Date(subDate);
      certDate.setDate(certDate.getDate() + 7);
      certDate.setHours(0, 0, 0, 0);
      const todayStart = new Date(today);
      todayStart.setHours(0, 0, 0, 0);
      return certDate <= todayStart && Number(b.balance_value || 0) > 0;
    });

    const latestGrossIncSecAdvKpi = sumLatestGrossIncSecAdvPerStream(scoped, projectFinancials);
    const totalReleasedWithheld = scoped.reduce((sum, b) => sum + sumReleasedWithheldRecords(b), 0);
    const rwPendingWithheldByReason = aggregatePendingWithheldByReasonLines(scoped);

    return {
      totalBills,
      paidCount,
      partPaidCount,
      unpaidCount,
      totalNetPaymentSum,
      totalReleasedWithheld,
      totalNet,
      totalPaid,
      totalDeductions,
      pendingCertCount: pendingCertBills.length,
      pendingCertValue,
      pendingPayCount: pendingPayBills.length,
      pendingPayValue,
      pendingPayByProject,
      certifiedUnpaidOverdueCount: certifiedUnpaidOverdue.length,
      latestGrossIncSecAdvKpi,
      rwTotalRecoveries,
      rwTotalWithheld,
      rwTotalPendingWithheld,
      rwBillsWithWithheld,
      rwBillsWithPendingWithheld,
      rwBillsWithReleased,
      rwPendingWithheldByReason,
    };
  }, [filteredBills, projectFinancials, projects]);

  const getStatusClass = (status) => RA_STATUS_CLASS_MAP[status] || 'draft';
  const getPaymentClass = (status) => PAYMENT_STATUS_CLASS_MAP[status] || 'draft';

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>RA Bills Register</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const focusRaField = (key) => {
    const el = document.querySelector(`[data-ra-field="${key}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      if (typeof el.focus === 'function') el.focus();
    }, 220);
  };

  const openCreatePage = () => {
    setCustomValues(defaultsFromCustomFields());
    setPreviousBillSnapshot(null);
    lastCreateStreamRef.current = '';
    const seed = createEmptyNewBill();
    if (hasProjectSelected) {
      seed.project_id = String(projectFilter);
      const fin = projectFinancials[Number(projectFilter)];
      if (fin?.agreement_amount != null) seed.agreement_amount = Number(fin.agreement_amount || 0);
    }
    setNewBill(seed);
    setCreateGeneratedAt(new Date().toLocaleString('en-IN'));
    setCreateFieldError('');
    setShowCreatePage(true);
  };

  const closeCreatePage = () => {
    setShowCreatePage(false);
    lastCreateStreamRef.current = '';
    setPreviousBillSnapshot(null);
    setCreateFieldError('');
    setNewBill(createEmptyNewBill());
    setCustomValues({});
  };

  const collectCreateMissingFields = (bill) => {
    const missing = [];
    const contractType = projectFinancials[Number(bill.project_id)]?.contract_type || '';
    if (!bill.project_id) missing.push({ key: 'project_id', label: 'Project' });
    if (contractType === CONTRACT_TYPES.ITEM_RATE && !bill.division) {
      missing.push({ key: 'division', label: 'Division / Category' });
    }
    if (!String(bill.agreement_no || '').trim()) missing.push({ key: 'agreement_no', label: 'Agreement No.' });
    if (!bill.ra_bill_date) missing.push({ key: 'ra_bill_date', label: 'RA Bill Date' });
    if (!bill.certified_date) missing.push({ key: 'certified_date', label: 'Certified Date' });
    if (!bill.status) missing.push({ key: 'status', label: 'Status' });
    if (!bill.period_from) missing.push({ key: 'period_from', label: 'Period From' });
    if (!bill.period_to) missing.push({ key: 'period_to', label: 'Period To' });
    if (!String(bill.remarks || '').trim()) missing.push({ key: 'remarks', label: 'Remarks' });
    pruneEmptyAdditionalBillRows(bill.additional_bill_rows).forEach((row) => {
      if (!String(row.description || '').trim()) {
        missing.push({
          key: `extra_row_${row.row_id || row.row_code}`,
          label: 'Extra row description',
        });
      }
    });
    return missing;
  };

  const handleCreateRABill = async (statusOverride) => {
    const prunedRows = pruneEmptyAdditionalBillRows(newBill.additional_bill_rows);
    if (prunedRows.length !== safeArray(newBill.additional_bill_rows).length) {
      setNewBill((prev) => ({ ...prev, additional_bill_rows: prunedRows }));
    }
    const billForValidation = { ...newBill, additional_bill_rows: prunedRows };
    const missing = collectCreateMissingFields(billForValidation);
    if (missing.length) {
      setCreateFieldError(missing[0].key);
      notifyError(`${missing[0].label} is required.`);
      focusRaField(missing[0].key);
      return;
    }
    setCreateFieldError('');
    if (!newBill.project_id) {
      notifyError('Project is required.');
      return;
    }
    if (!newBill.agreement_amount || Number(newBill.agreement_amount) <= 0) {
      notifyError('Agreement amount is required and must be greater than zero.');
      return;
    }
    if (!newBill.ra_no) {
      notifyError('RA Bill number is required.');
      return;
    }
    const contractType = projectFinancials[Number(newBill.project_id)]?.contract_type || '';
    if (contractType === CONTRACT_TYPES.ITEM_RATE && !newBill.division) {
      notifyError('Division/Category is required for Item rate % Contract projects.');
      return;
    }
    const postedStatus = statusOverride || newBill.status || 'Draft';
    if (postedStatus === 'Submitted') {
      if (!(await confirmDialog('Create this RA Bill?', 'New RA Bill'))) return;
    }
    const nextRa = Number(newBill.ra_no || 1);
    const createCommercial = extractCommercialInputs(newBill);
    const workGross = resolveGrossValueOfWorkDoneForCpwd(newBill, createCommercial);
    const departmentRates = departmentRuleInfo?.rule
      ? {
          security_deposit_pct: Number(departmentRuleInfo.rule.security_deposit_pct ?? DEFAULT_RA_RATES.security_deposit_pct),
          gst_pct: Number(departmentRuleInfo.rule.gst_pct ?? DEFAULT_RA_RATES.gst_pct),
          income_tax_pct: Number(departmentRuleInfo.rule.income_tax_pct ?? DEFAULT_RA_RATES.income_tax_pct),
          labour_cess_pct: Number(departmentRuleInfo.rule.labour_cess_pct ?? DEFAULT_RA_RATES.labour_cess_pct),
        }
      : null;
    const shouldUseRuleRates = ENABLE_DEPARTMENT_RULE_RATES_FOR_NEW_BILLS
      && Boolean(departmentRates)
      && departmentRuleInfo?.legacy_mode === false;
    const templateInputs = isConfigurableSnapshot(newBill)
      ? buildTemplateInputs(newBill)
      : null;
    const previousTemplateValues = isConfigurableSnapshot(newBill)
      ? (newBill.previous_template_values || buildPreviousTemplateValues(previousBillSnapshot, newBill.ra_template_snapshot))
      : null;
    const formula = calculateRABillAmounts({
      agreementAmount: newBill.agreement_amount,
      uptoDateGross: workGross,
      grossValueOfWorkDone: workGross,
      previousUptoDateGrossCpwd: newBill.previous_ra_gross_inc_sec_adv,
      previousUptoDateSecuredAdvance: newBill.previous_ra_sec_advance_upto,
      isFirstBill: nextRa <= 1,
      previousUptoDateGross: newBill.previous_ra_upto_date_gross,
      previousUptoDateGrossIncSecAdv: newBill.previous_ra_gross_inc_sec_adv ?? newBill.previous_ra_upto_date_gross,
      secAdvanceMaterial: newBill.sec_advance_material,
      mobilisationAdvance: newBill.mobilisation_advance,
      advancePaymentWorkdoneNotMeasured: newBill.advance_payment_workdone_not_measured,
      clause10cc10caPayment: newBill.clause_10cc_10ca_payment,
      raNo: nextRa,
      additionalSd: newBill.additional_sd_special_items,
      withheldItems: newBill.withheld_items,
      rates: shouldUseRuleRates ? departmentRates : getProjectRates(newBill.project_id),
      useRateOverrides: shouldUseRuleRates,
      statutoryNetExclSecOverride: raBillStatutoryOverride(newBill),
      securityDepositOverride: raBillSecurityDepositOverride(newBill),
      ...raBillCpwdLine5Fallbacks(newBill),
      billMeta: newBill,
      templateInputs,
      previousTemplateValues,
      departmentValues: buildDepartmentValuesFromRule(departmentRuleInfo),
    });
    const legacyWithheld = getLegacyWithheldAmounts(newBill.withheld_items);
    const division = contractType === CONTRACT_TYPES.ITEM_RATE ? (newBill.division || '') : '';
    const raRef = division ? `RA-${division}-${newBill.ra_no || 1}` : `RA-${newBill.ra_no || 1}`;
    const billNo = division ? `RA-${division}-${newBill.ra_no || 1}` : `RA-${newBill.ra_no || 1}`;
    try {
      const payload = {
        projectId: Number(newBill.project_id),
        billNo,
        status: postedStatus,
        amount: formula.net_payment_received,
        data: {
          ra_no: Number(newBill.ra_no || 1),
          ra_ref: raRef,
          division: division || undefined,
          bills_paid_by_dept: Number(newBill.bills_paid_by_dept || 0),
          period_from: newBill.period_from,
          period_to: newBill.period_to,
          agreement_amount: Number(newBill.agreement_amount || 0),
          agreement_no: newBill.agreement_no || '',
          ra_bill_date: newBill.ra_bill_date || null,
          certified_date: newBill.certified_date || null,
          remarks: newBill.remarks || '',
          department_id: departmentRuleInfo?.department_id ?? null,
          department_code: departmentRuleInfo?.department?.code ?? null,
          department_name: departmentRuleInfo?.department?.name ?? null,
          financial_year: departmentRuleInfo?.financial_year || formatFinancialYearFromDate(newBill.ra_bill_date),
          department_rule_snapshot: departmentRuleInfo?.rule || null,
          legacy_mode: departmentRuleInfo?.legacy_mode !== undefined ? Boolean(departmentRuleInfo.legacy_mode) : true,
          calculation_mode: shouldUseRuleRates ? 'DEPARTMENT_FY_RATES' : 'CPWD_LEGACY_FIXED',
          applied_recovery_rates: shouldUseRuleRates ? departmentRates : DEFAULT_RA_RATES,
          gross_value_of_work_done: Number(newBill.upto_date_gross_work_done || 0),
          upto_date_gross_work_done: Number(newBill.upto_date_gross_work_done || 0),
          upto_date_gross_value_of_work_done: Number(formula.upto_date_gross_value_of_work_done || 0),
          gross_value_as_per_previous_ra_bill: Number(formula.gross_value_as_per_previous_ra_bill || 0),
          secured_advance_added_in_this_bill: Number(formula.secured_advance_added_in_this_bill || 0),
          sec_advance_material: Number(newBill.sec_advance_material || 0),
          mobilisation_advance: Number(newBill.mobilisation_advance || 0),
          advance_payment_workdone_not_measured: Number(newBill.advance_payment_workdone_not_measured || 0),
          clause_10cc_10ca_payment: Number(newBill.clause_10cc_10ca_payment || 0),
          previous_ra_sec_advance_upto: Number(newBill.previous_ra_sec_advance_upto || 0),
          upto_date_gross_inc_sec_adv: Number(formula.upto_date_gross_inc_sec_adv || 0),
          previous_ra_upto_date_gross: Number(formula.previous_ra_gross_inc_sec_adv || 0),
          previous_ra_gross_inc_sec_adv: Number(formula.previous_ra_gross_inc_sec_adv || 0),
          net_value_work_done_since_prev_bill: Number(formula.net_value_work_done_since_prev_bill || 0),
          net_value_work_done_without_sec_adv: Number(formula.net_value_work_done_without_sec_adv || 0),
          current_bill_value: Number(formula.current_bill_value || 0),
          gross_work_done: Number(formula.gross_work_done || 0),
          security_deposit: Number(formula.security_deposit || 0),
          gst_deduction: Number(formula.gst_deduction || 0),
          income_tax_deduction: Number(formula.income_tax_deduction || 0),
          labour_cess: Number(formula.labour_cess || 0),
          additional_sd_special_items: Number(newBill.additional_sd_special_items || 0),
          cpwd_statutory_recovery_base_override: Number(newBill.cpwd_statutory_recovery_base_override || 0),
          ...(Object.prototype.hasOwnProperty.call(newBill, 'cpwd_security_deposit_override')
            ? { cpwd_security_deposit_override: Number(newBill.cpwd_security_deposit_override) }
            : {}),
          total_recoveries: Number(formula.total_recoveries || 0),
          actual_total_recovered_amount: newBill.actual_total_recovered_amount === '' ? null : Number(newBill.actual_total_recovered_amount || 0),
          withheld_items: safeArray(newBill.withheld_items).map((item) => ({
            id: item.id,
            reason: item.reason,
            custom_reason: item.custom_reason || '',
            amount: Number(item.amount || 0),
          })),
          withheld_milestone: legacyWithheld.milestone,
          withheld_mpr: legacyWithheld.mpr,
          withheld_other_1: legacyWithheld.other1,
          withheld_other_2: legacyWithheld.other2,
          total_withheld: Number(formula.total_withheld || 0),
          deductions_total: Number(formula.total_deductions || 0),
          total_deductions: Number(formula.total_deductions || 0),
          net_payable: Number(formula.net_payment_received || 0),
          net_payment_received: Number(formula.net_payment_received || 0),
          percent_workdone: Number(formula.percent_workdone || 0),
          payment_status: 'Unpaid',
          payment_due_date: newBill.payment_due_date || null,
          paid_value: 0,
          balance_value: Number(formula.net_payment_received || 0),
          submitted_date: newBill.ra_bill_date || new Date().toISOString().slice(0, 10),
          billing_basis_type: newBill.billing_basis_type || 'Quantity Based',
          billing_dependency_status: newBill.billing_dependency_status || 'Clear',
          linked_milestone_id: newBill.linked_milestone_id ? Number(newBill.linked_milestone_id) : null,
          linked_eot_id: newBill.linked_eot_id ? Number(newBill.linked_eot_id) : null,
          linked_bg_ids: safeArray(newBill.linked_bg_ids).map(Number).filter((id) => !Number.isNaN(id)),
          primary_bg_id: newBill.primary_bg_id ? Number(newBill.primary_bg_id) : null,
          linked_correspondence: newBill.linked_correspondence ? Number(newBill.linked_correspondence) : null,
          primary_correspondence_chain_id: newBill.linked_correspondence ? Number(newBill.linked_correspondence) : null,
          related_correspondence_ids: safeArray(newBill.related_correspondence_ids).map(Number).filter((id) => !Number.isNaN(id)),
          billing_hold_reason: newBill.billing_hold_reason || '',
          withheld_due_to_milestone_flag: Boolean(newBill.withheld_due_to_milestone_flag),
          withheld_due_to_eot_flag: Boolean(newBill.withheld_due_to_eot_flag),
          released_withheld_records: [],
          attachments: newBill.attachments || [],
          additional_bill_rows: prunedRows,
          ...buildTemplateFreezePayload(newBill, {
            template_field_inputs: templateInputs || newBill.template_field_inputs || {},
            template_field_values: formula._templateFieldValues || newBill.template_field_values || {},
            previous_template_values: previousTemplateValues || newBill.previous_template_values || {},
            calculation_snapshot: formula._templateFieldValues
              ? {
                  engine: 'CONFIGURABLE',
                  fieldValues: formula._templateFieldValues,
                  effectiveFieldValues: formula._templateFieldValues,
                  at: new Date().toISOString(),
                }
              : null,
            calculation_engine: isConfigurableSnapshot(newBill) ? 'CONFIGURABLE' : (newBill.calculation_engine ?? null),
          }),
        },
      };
      const response = await raBillsAPI.create(payload);
      const row = response?.data;
      if (formConfig.customFieldsEnabled && row?.id) {
        try {
          await adminAPI.saveCustomFieldValues('RABill', row.id, {
            pageKey: 'ra_bills',
            projectId: Number(newBill.project_id),
            values: customValues,
            mode: 'create',
          });
        } catch (cvErr) {
          console.error('Custom field save failed:', cvErr);
          notifyError(cvErr?.response?.data?.message || 'RA Bill saved but custom fields failed.');
        }
      }
      setRaBills((prev) => ([
        normalizeRABillRow({
          ...row,
          projectId: row.projectId ?? Number(newBill.project_id),
          billNo: row.billNo ?? billNo,
          data: {
            ...(row.data || {}),
            period_from: row?.data?.period_from ?? newBill.period_from,
            period_to: row?.data?.period_to ?? newBill.period_to,
            ra_no: row?.data?.ra_no ?? Number(newBill.ra_no || 1),
            ra_ref: row?.data?.ra_ref ?? raRef,
            division: row?.data?.division ?? (division || undefined),
            bills_paid_by_dept: row?.data?.bills_paid_by_dept ?? Number(newBill.bills_paid_by_dept || 0),
            agreement_amount: row?.data?.agreement_amount ?? Number(newBill.agreement_amount || 0),
            agreement_no: row?.data?.agreement_no ?? newBill.agreement_no,
            ra_bill_date: row?.data?.ra_bill_date ?? newBill.ra_bill_date,
            certified_date: row?.data?.certified_date ?? newBill.certified_date,
            remarks: row?.data?.remarks ?? newBill.remarks,
            gross_value_of_work_done: row?.data?.gross_value_of_work_done ?? Number(newBill.upto_date_gross_work_done || 0),
            upto_date_gross_work_done: row?.data?.upto_date_gross_work_done ?? Number(newBill.upto_date_gross_work_done || 0),
            upto_date_gross_value_of_work_done: row?.data?.upto_date_gross_value_of_work_done ?? Number(formula.upto_date_gross_value_of_work_done || 0),
            gross_value_as_per_previous_ra_bill: row?.data?.gross_value_as_per_previous_ra_bill ?? Number(formula.gross_value_as_per_previous_ra_bill || 0),
            secured_advance_added_in_this_bill: row?.data?.secured_advance_added_in_this_bill ?? Number(formula.secured_advance_added_in_this_bill || 0),
            sec_advance_material: row?.data?.sec_advance_material ?? Number(newBill.sec_advance_material || 0),
            mobilisation_advance: row?.data?.mobilisation_advance ?? Number(newBill.mobilisation_advance || 0),
            advance_payment_workdone_not_measured: row?.data?.advance_payment_workdone_not_measured ?? Number(newBill.advance_payment_workdone_not_measured || 0),
            clause_10cc_10ca_payment: row?.data?.clause_10cc_10ca_payment ?? Number(newBill.clause_10cc_10ca_payment || 0),
            previous_ra_sec_advance_upto: row?.data?.previous_ra_sec_advance_upto ?? Number(newBill.previous_ra_sec_advance_upto || 0),
            upto_date_gross_inc_sec_adv: row?.data?.upto_date_gross_inc_sec_adv ?? Number(formula.upto_date_gross_inc_sec_adv || 0),
            previous_ra_upto_date_gross: row?.data?.previous_ra_upto_date_gross ?? Number(formula.previous_ra_gross_inc_sec_adv || 0),
            previous_ra_gross_inc_sec_adv: row?.data?.previous_ra_gross_inc_sec_adv ?? Number(formula.previous_ra_gross_inc_sec_adv || 0),
            net_value_work_done_since_prev_bill: row?.data?.net_value_work_done_since_prev_bill ?? Number(formula.net_value_work_done_since_prev_bill || 0),
            net_value_work_done_without_sec_adv: row?.data?.net_value_work_done_without_sec_adv ?? Number(formula.net_value_work_done_without_sec_adv || 0),
            current_bill_value: row?.data?.current_bill_value ?? Number(formula.current_bill_value || 0),
            gross_work_done: row?.data?.gross_work_done ?? Number(formula.gross_work_done || 0),
            security_deposit: row?.data?.security_deposit ?? Number(formula.security_deposit || 0),
            gst_deduction: row?.data?.gst_deduction ?? Number(formula.gst_deduction || 0),
            income_tax_deduction: row?.data?.income_tax_deduction ?? Number(formula.income_tax_deduction || 0),
            labour_cess: row?.data?.labour_cess ?? Number(formula.labour_cess || 0),
            additional_sd_special_items: row?.data?.additional_sd_special_items ?? Number(newBill.additional_sd_special_items || 0),
            total_recoveries: row?.data?.total_recoveries ?? Number(formula.total_recoveries || 0),
            withheld_items: row?.data?.withheld_items ?? safeArray(newBill.withheld_items).map((item) => ({
              id: item.id,
              reason: item.reason,
              custom_reason: item.custom_reason || '',
              amount: Number(item.amount || 0),
            })),
            withheld_milestone: row?.data?.withheld_milestone ?? legacyWithheld.milestone,
            withheld_mpr: row?.data?.withheld_mpr ?? legacyWithheld.mpr,
            withheld_other_1: row?.data?.withheld_other_1 ?? legacyWithheld.other1,
            withheld_other_2: row?.data?.withheld_other_2 ?? legacyWithheld.other2,
            total_withheld: row?.data?.total_withheld ?? Number(formula.total_withheld || 0),
            deductions_total: row?.data?.deductions_total ?? Number(formula.total_deductions || 0),
            total_deductions: row?.data?.total_deductions ?? Number(formula.total_deductions || 0),
            net_payable: row?.data?.net_payable ?? Number(formula.net_payment_received || 0),
            net_payment_received: row?.data?.net_payment_received ?? Number(formula.net_payment_received || 0),
            percent_workdone: Number(formula.percent_workdone || 0),
            payment_status: row?.data?.payment_status ?? 'Unpaid',
            paid_value: row?.data?.paid_value ?? 0,
            balance_value: row?.data?.balance_value ?? Number(formula.net_payment_received || 0),
            submitted_date: row?.data?.submitted_date ?? new Date().toISOString().slice(0, 10),
            linked_bg_ids: row?.data?.linked_bg_ids ?? safeArray(newBill.linked_bg_ids).map(Number).filter((id) => !Number.isNaN(id)),
            primary_bg_id: row?.data?.primary_bg_id ?? (newBill.primary_bg_id ? Number(newBill.primary_bg_id) : null),
            attachments: row?.data?.attachments ?? [],
            additional_bill_rows: row?.data?.additional_bill_rows ?? normalizeAdditionalBillRows(newBill.additional_bill_rows),
          },
        }),
        ...prev,
      ]));
      setShowCreatePage(false);
      lastCreateStreamRef.current = '';
      setPreviousBillSnapshot(null);
      setNewBill(createEmptyNewBill());
      setCustomValues({});
      notifySuccess(postedStatus === 'Draft' ? 'RA Bill draft saved.' : 'RA Bill created successfully.');
    } catch (error) {
      console.error('Create RA bill failed:', error);
      notifyError('Unable to create RA Bill. Please retry.');
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewBill((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  function getIssueFlags(bill) {
    const flags = [];
    if (!bill) return flags;
    if (bill.billing_dependency_status && bill.billing_dependency_status !== 'Clear') {
      flags.push(bill.billing_dependency_status);
    }
    if (bill.payment_status !== 'Paid' && bill.payment_due_date && new Date(bill.payment_due_date) < new Date()) {
      flags.push('Payment Overdue');
    }
    if (bill.withheld_due_to_milestone_flag) flags.push('Milestone Hold');
    if (bill.withheld_due_to_eot_flag) flags.push('EOT Hold');
    if (bill.payment_status === 'Withheld') flags.push('Withheld');
    return flags;
  }

  const persistBillUpdate = async (updatedBill, successMessage, options = {}) => {
    const doRestructure = Boolean(options.restructure);
    // Payment / non-amount updates must not rewrite frozen template calculations.
    const preserveAmounts = Boolean(options.preserveAmounts);
    const restructureReasonInput = String(options.restructureReason || '').trim();
    const actorEmail = (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}')?.email || 'unknown'; } catch (_) { return 'unknown'; }
    })();
    const restructureAt = new Date().toISOString();
    const priorHistory = safeArray(updatedBill.restructure_history);
    const appendedHistory = doRestructure
      ? [...priorHistory, {
          ts: restructureAt,
          by: actorEmail,
          reason: restructureReasonInput,
          bill_no: updatedBill.ra_ref || `RA-${updatedBill.ra_no || updatedBill.id}`,
          net_payable: Number(updatedBill.net_payable ?? updatedBill.net_payment_received ?? 0),
          total_deductions: Number(updatedBill.total_deductions ?? updatedBill.deductions_total ?? 0),
          total_recoveries: Number(updatedBill.total_recoveries ?? 0),
        }]
      : priorHistory;
    const raSeq = parseRANumber(updatedBill) || updatedBill.ra_no || 1;
    const updCommercial = extractCommercialInputs(updatedBill);
    const workGross = resolveGrossValueOfWorkDoneForCpwd(updatedBill, updCommercial);
    const listMerged = safeArray(raBills).map((b) => (
      Number(b.id) === Number(updatedBill.id) ? { ...b, ...updatedBill } : b
    ));
    const chain = getCpwdChainContextForBill(updatedBill, listMerged, projectFinancials);
    const mergedPrev = mergeUserPreviousRaWithChain(
      updatedBill.previous_ra_gross_inc_sec_adv,
      chain.previousUptoDateGrossCpwd,
      updatedBill.previous_ra_sec_advance_upto,
      chain.previousUptoDateSecuredAdvance,
    );
    const formula = preserveAmounts
      ? null
      : calculateRABillAmounts({
        agreementAmount: chain.agreementAmount,
        uptoDateGross: workGross,
        grossValueOfWorkDone: workGross,
        previousUptoDateGrossCpwd: mergedPrev.previousUptoDateGrossCpwd,
        previousUptoDateSecuredAdvance: mergedPrev.previousUptoDateSecuredAdvance,
        isFirstBill: chain.isFirstBill,
        previousUptoDateGross: updatedBill.previous_ra_upto_date_gross,
        previousUptoDateGrossIncSecAdv: updatedBill.previous_ra_gross_inc_sec_adv ?? updatedBill.previous_ra_upto_date_gross,
        secAdvanceMaterial: updatedBill.sec_advance_material ?? 0,
        mobilisationAdvance: updatedBill.mobilisation_advance ?? 0,
        advancePaymentWorkdoneNotMeasured: updatedBill.advance_payment_workdone_not_measured ?? 0,
        clause10cc10caPayment: updatedBill.clause_10cc_10ca_payment,
        raNo: raSeq,
        additionalSd: updatedBill.additional_sd_special_items,
        withheldItems: updatedBill.withheld_items,
        rates: getProjectRates(updatedBill.project_id),
        statutoryNetExclSecOverride: raBillStatutoryOverride(updatedBill),
        securityDepositOverride: raBillSecurityDepositOverride(updatedBill),
        ...raBillCpwdLine5Fallbacks(updatedBill),
        billMeta: updatedBill,
        templateInputs: isConfigurableSnapshot(updatedBill) ? buildTemplateInputs(updatedBill) : undefined,
        previousTemplateValues: isConfigurableSnapshot(updatedBill)
          ? (updatedBill.previous_template_values || buildPreviousTemplateValues(
            getPreviousBillInStream(updatedBill, listMerged, projectFinancials),
            updatedBill.ra_template_snapshot,
          ))
          : undefined,
        departmentValues: buildDepartmentValuesFromRule(
          updatedBill.department_rule_snapshot || departmentRuleInfo,
        ),
      });
    const amountSource = preserveAmounts
      ? {
          upto_date_gross_value_of_work_done: Number(updatedBill.upto_date_gross_value_of_work_done || 0),
          gross_value_as_per_previous_ra_bill: Number(updatedBill.gross_value_as_per_previous_ra_bill || 0),
          secured_advance_added_in_this_bill: Number(updatedBill.secured_advance_added_in_this_bill || 0),
          upto_date_gross_inc_sec_adv: Number(updatedBill.upto_date_gross_inc_sec_adv || 0),
          previous_ra_gross_inc_sec_adv: Number(updatedBill.previous_ra_gross_inc_sec_adv || 0),
          net_value_work_done_since_prev_bill: Number(updatedBill.net_value_work_done_since_prev_bill || 0),
          net_value_work_done_without_sec_adv: Number(updatedBill.net_value_work_done_without_sec_adv || 0),
          current_bill_value: Number(updatedBill.current_bill_value || 0),
          gross_work_done: Number(updatedBill.gross_work_done || 0),
          security_deposit: Number(updatedBill.security_deposit || 0),
          gst_deduction: Number(updatedBill.gst_deduction || 0),
          income_tax_deduction: Number(updatedBill.income_tax_deduction || 0),
          labour_cess: Number(updatedBill.labour_cess || 0),
          total_recoveries: Number(updatedBill.total_recoveries || 0),
          total_withheld: Number(updatedBill.total_withheld || 0),
          total_deductions: Number(updatedBill.total_deductions ?? updatedBill.deductions_total ?? 0),
          net_payment_received: Number(updatedBill.net_payment_received ?? updatedBill.net_payable ?? 0),
          percent_workdone: Number(updatedBill.percent_workdone || 0),
          _templateFieldValues: updatedBill.template_field_values || null,
        }
      : formula;
    const legacyWithheld = getLegacyWithheldAmounts(updatedBill.withheld_items);
    const division = updatedBill.division || '';
    const raNo = parseRANumber(updatedBill) || updatedBill.ra_no || updatedBill.id;
    const raRef = division ? `RA-${division}-${raNo}` : `RA-${raNo}`;
    const billNo = division ? `RA-${division}-${raNo}` : `RA-${raNo}`;
    const templateFreeze = buildTemplateFreezePayload(updatedBill, preserveAmounts
      ? {}
      : {
          template_field_inputs: isConfigurableSnapshot(updatedBill)
            ? buildTemplateInputs(updatedBill)
            : (updatedBill.template_field_inputs || {}),
          template_field_values: amountSource._templateFieldValues || updatedBill.template_field_values || {},
          calculation_snapshot: amountSource._templateFieldValues
            ? {
                engine: 'CONFIGURABLE',
                fieldValues: amountSource._templateFieldValues,
                effectiveFieldValues: amountSource._templateFieldValues,
                at: new Date().toISOString(),
              }
            : (updatedBill.calculation_snapshot ?? null),
        });
    const payload = {
      projectId: Number(updatedBill.project_id),
      billNo,
      status: updatedBill.status,
      amount: Number(amountSource.net_payment_received || 0),
      data: {
        ra_no: parseRANumber(updatedBill) || updatedBill.ra_no,
        ra_ref: raRef,
        division: division || undefined,
        bills_paid_by_dept: Math.max(0, (parseRANumber(updatedBill) || updatedBill.ra_no || 1) - 1),
        period_from: updatedBill.period_from,
        period_to: updatedBill.period_to,
        agreement_amount: Number(updatedBill.agreement_amount || 0),
        agreement_no: updatedBill.agreement_no ?? '',
        ra_bill_date: updatedBill.ra_bill_date || null,
        certified_date: updatedBill.certified_date || null,
        remarks: updatedBill.remarks ?? '',
        gross_value_of_work_done: Number(updatedBill.upto_date_gross_work_done || 0),
        upto_date_gross_work_done: Number(updatedBill.upto_date_gross_work_done || 0),
        upto_date_gross_value_of_work_done: Number(amountSource.upto_date_gross_value_of_work_done || 0),
        gross_value_as_per_previous_ra_bill: Number(amountSource.gross_value_as_per_previous_ra_bill || 0),
        secured_advance_added_in_this_bill: Number(amountSource.secured_advance_added_in_this_bill || 0),
        sec_advance_material: Number(updatedBill.sec_advance_material ?? 0),
        mobilisation_advance: Number(updatedBill.mobilisation_advance ?? 0),
        advance_payment_workdone_not_measured: Number(updatedBill.advance_payment_workdone_not_measured ?? 0),
        clause_10cc_10ca_payment: Number(updatedBill.clause_10cc_10ca_payment ?? 0),
        previous_ra_sec_advance_upto: Number(
          preserveAmounts
            ? (updatedBill.previous_ra_sec_advance_upto ?? 0)
            : (mergedPrev.previousUptoDateSecuredAdvance ?? 0),
        ),
        upto_date_gross_inc_sec_adv: Number(amountSource.upto_date_gross_inc_sec_adv || 0),
        previous_ra_upto_date_gross: Number(amountSource.previous_ra_gross_inc_sec_adv || 0),
        previous_ra_gross_inc_sec_adv: Number(amountSource.previous_ra_gross_inc_sec_adv || 0),
        net_value_work_done_since_prev_bill: Number(amountSource.net_value_work_done_since_prev_bill || 0),
        net_value_work_done_without_sec_adv: Number(amountSource.net_value_work_done_without_sec_adv || 0),
        current_bill_value: Number(amountSource.current_bill_value || 0),
        gross_work_done: Number(amountSource.gross_work_done || 0),
        security_deposit: Number(amountSource.security_deposit || 0),
        gst_deduction: Number(amountSource.gst_deduction || 0),
        income_tax_deduction: Number(amountSource.income_tax_deduction || 0),
        labour_cess: Number(amountSource.labour_cess || 0),
        additional_sd_special_items: Number(updatedBill.additional_sd_special_items || 0),
        cpwd_statutory_recovery_base_override: Number(updatedBill.cpwd_statutory_recovery_base_override || 0),
        // Preserve create-time / template snapshots (additive; do not wipe on update)
        department_id: updatedBill.department_id ?? null,
        department_code: updatedBill.department_code ?? null,
        department_name: updatedBill.department_name ?? null,
        financial_year: updatedBill.financial_year ?? null,
        department_rule_snapshot: updatedBill.department_rule_snapshot ?? null,
        legacy_mode: updatedBill.legacy_mode,
        calculation_mode: updatedBill.calculation_mode ?? null,
        applied_recovery_rates: updatedBill.applied_recovery_rates ?? null,
        ...templateFreeze,
        ...(Object.prototype.hasOwnProperty.call(updatedBill, 'cpwd_security_deposit_override')
          ? { cpwd_security_deposit_override: Number(updatedBill.cpwd_security_deposit_override) }
          : {}),
        total_recoveries: Number(amountSource.total_recoveries || 0),
        actual_total_recovered_amount: updatedBill.actual_total_recovered_amount == null || updatedBill.actual_total_recovered_amount === ''
          ? null
          : Number(updatedBill.actual_total_recovered_amount || 0),
        withheld_items: safeArray(updatedBill.withheld_items).map((item) => ({
          id: item.id,
          reason: item.reason,
          custom_reason: item.custom_reason || '',
          amount: Number(item.amount || 0),
        })),
        withheld_milestone: legacyWithheld.milestone,
        withheld_mpr: legacyWithheld.mpr,
        withheld_other_1: legacyWithheld.other1,
        withheld_other_2: legacyWithheld.other2,
        total_withheld: Number(amountSource.total_withheld || 0),
        deductions_total: Number(amountSource.total_deductions || 0),
        total_deductions: Number(amountSource.total_deductions || 0),
        net_payable: Number(amountSource.net_payment_received || 0),
        net_payment_received: Number(amountSource.net_payment_received || 0),
        percent_workdone: Number(amountSource.percent_workdone || 0),
        paid_value: Number(updatedBill.paid_value || 0),
        balance_value: Math.max(0, Number(amountSource.net_payment_received || 0) - Number(updatedBill.paid_value || 0)),
        payment_status: updatedBill.payment_status,
        payment_due_date: updatedBill.payment_due_date || null,
        payment_received_date: updatedBill.payment_received_date || null,
        submitted_date: updatedBill.submitted_date || null,
        approved_quantities: Number(updatedBill.approved_quantities || 0),
        linked_correspondence: updatedBill.linked_correspondence ?? null,
        linked_milestone_id: updatedBill.linked_milestone_id ?? null,
        linked_eot_id: updatedBill.linked_eot_id ?? null,
        linked_bg_ids: safeArray(updatedBill.linked_bg_ids).map(Number).filter((id) => !Number.isNaN(id)),
        primary_bg_id: updatedBill.primary_bg_id ? Number(updatedBill.primary_bg_id) : null,
        billing_basis_type: updatedBill.billing_basis_type || 'Quantity Based',
        billing_dependency_status: updatedBill.billing_dependency_status || 'Clear',
        billing_hold_reason: updatedBill.billing_hold_reason || '',
        withheld_due_to_milestone_flag: Boolean(updatedBill.withheld_due_to_milestone_flag),
        withheld_due_to_eot_flag: Boolean(updatedBill.withheld_due_to_eot_flag),
        related_correspondence_ids: safeArray(updatedBill.related_correspondence_ids),
        primary_correspondence_chain_id: updatedBill.primary_correspondence_chain_id ?? null,
        percent_financial_progress: Number(updatedBill.percent_financial_progress || 0),
        payments: safeArray(updatedBill.payments),
        released_withheld_records: safeArray(updatedBill.released_withheld_records).map((r, i) => ({
          id: String(r.id || `rw-${updatedBill.id}-${i}`),
          release_date: r.release_date || '',
          amount: Number(r.amount || 0),
        })),
        restructure_history: appendedHistory,
        last_restructure_at: doRestructure ? restructureAt : (updatedBill.last_restructure_at || null),
        last_restructure_by: doRestructure ? actorEmail : (updatedBill.last_restructure_by || null),
        last_restructure_reason: doRestructure ? restructureReasonInput : (updatedBill.last_restructure_reason || ''),
        attachments: updatedBill.attachments || [],
        additional_bill_rows: normalizeAdditionalBillRows(updatedBill.additional_bill_rows),
      },
    };
    await raBillsAPI.update(updatedBill.id, payload);
    const normalized = normalizeRABillRow({ ...updatedBill, projectId: updatedBill.project_id, billNo: payload.billNo, data: payload.data });
    setRaBills((prev) => prev.map((bill) => (bill.id === normalized.id ? normalized : bill)));
    setSelectedBill(normalized);
    if (successMessage) notifySuccess(successMessage);
  };

  const handlePaymentAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setPaymentAttachmentUploading(true);
    try {
      const [file] = await toStoredAttachments(files.slice(0, 1));
      setNewPayment((prev) => ({ ...prev, attachment: file || null }));
    } finally {
      setPaymentAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const handleSavePaymentRecord = async () => {
    if (!selectedBill) return;
    if (!newPayment.payment_date || newPayment.amount_received === '' || newPayment.amount_received === null) {
      notifyError('Payment date and amount received are required.');
      return;
    }
    const amountReceived = Number(newPayment.amount_received || 0);
    if (!Number.isFinite(amountReceived) || amountReceived <= 0) {
      notifyError('Amount received must be greater than zero.');
      return;
    }
    setPaymentSaving(true);
    try {
      const existing = paymentEditIndex != null ? safeArray(selectedBill.payments)[paymentEditIndex] : null;
      const paymentRecord = {
        payment_date: newPayment.payment_date,
        amount_received: amountReceived,
        paying_authority: newPayment.paying_authority || '',
        cm_book_no: newPayment.cm_book_no || '',
        cm_book_page_nos: newPayment.cm_book_page_nos || '',
        cash_book_no: newPayment.cash_book_no || '',
        cash_book_page_no: newPayment.cash_book_page_no || '',
        voucher_no: newPayment.voucher_no || '',
        remarks: newPayment.remarks || '',
        attachment: newPayment.attachment ?? existing?.attachment ?? null,
      };
      const prevList = [...safeArray(selectedBill.payments)];
      let payments;
      if (paymentEditIndex != null && paymentEditIndex >= 0 && paymentEditIndex < prevList.length) {
        payments = prevList.map((row, i) => (i === paymentEditIndex ? paymentRecord : row));
      } else {
        payments = [...prevList, paymentRecord];
      }
      const totals = aggregatePaymentTotals(payments, selectedBill.net_payable);
      const updatedBill = {
        ...selectedBill,
        payments,
        ...totals,
      };
      const msg = paymentEditIndex != null ? 'Payment record updated.' : 'Payment record added.';
      await persistBillUpdate(updatedBill, msg, { preserveAmounts: true });
      setPaymentEditIndex(null);
      setNewPayment(emptyNewPaymentState());
    } catch (error) {
      console.error('Save payment record failed:', error);
      notifyError('Unable to save payment record.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleStartEditPayment = (idx) => {
    if (!selectedBill) return;
    const p = safeArray(selectedBill.payments)[idx];
    if (!p) return;
    setPaymentEditIndex(idx);
    setNewPayment({
      payment_date: p.payment_date || '',
      amount_received: p.amount_received !== undefined && p.amount_received !== null ? String(p.amount_received) : '',
      paying_authority: p.paying_authority || '',
      cm_book_no: p.cm_book_no || '',
      cm_book_page_nos: p.cm_book_page_nos || '',
      cash_book_no: p.cash_book_no || '',
      cash_book_page_no: p.cash_book_page_no || '',
      voucher_no: p.voucher_no || '',
      remarks: p.remarks || '',
      attachment: p.attachment || null,
    });
  };

  const handleCancelPaymentEdit = () => {
    setPaymentEditIndex(null);
    setNewPayment(emptyNewPaymentState());
  };

  const handleDeletePaymentRecord = async (idx) => {
    if (!selectedBill) return;
    const ok = await confirmDialog(
      'Remove this payment line? Paid value and balance will be recalculated.',
      'Delete payment record',
    );
    if (!ok) return;
    setPaymentSaving(true);
    try {
      const payments = safeArray(selectedBill.payments).filter((_, i) => i !== idx);
      const totals = aggregatePaymentTotals(payments, selectedBill.net_payable);
      const updatedBill = {
        ...selectedBill,
        payments,
        ...totals,
      };
      await persistBillUpdate(updatedBill, 'Payment record removed.', { preserveAmounts: true });
      if (paymentEditIndex === idx) {
        setPaymentEditIndex(null);
        setNewPayment(emptyNewPaymentState());
      } else if (paymentEditIndex != null && idx < paymentEditIndex) {
        setPaymentEditIndex(paymentEditIndex - 1);
      }
    } catch (error) {
      console.error('Delete payment record failed:', error);
      notifyError('Unable to delete payment record.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const updateSelectedBillFormula = (patch) => {
    setSelectedBill((prev) => {
      if (!prev) return prev;
      let next = { ...prev, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'cpwd_security_deposit_override')) {
        const v = patch.cpwd_security_deposit_override;
        if (v === null || v === '' || v === undefined) {
          delete next.cpwd_security_deposit_override;
        } else {
          next.cpwd_security_deposit_override = Number(v);
        }
      }
      const raSeq = parseRANumber(next) || next.ra_no || 1;
      const selCommercial = extractCommercialInputs(next);
      const workGross = resolveGrossValueOfWorkDoneForCpwd(next, selCommercial);
      const listMerged = safeArray(raBills).map((b) => (
        Number(b.id) === Number(next.id) ? { ...b, ...next } : b
      ));
      const chain = getCpwdChainContextForBill(next, listMerged, projectFinancials);
      const mergedPrev = mergeUserPreviousRaWithChain(
        next.previous_ra_gross_inc_sec_adv,
        chain.previousUptoDateGrossCpwd,
        next.previous_ra_sec_advance_upto,
        chain.previousUptoDateSecuredAdvance,
      );
      const formula = calculateRABillAmounts({
        agreementAmount: chain.agreementAmount,
        uptoDateGross: workGross,
        grossValueOfWorkDone: workGross,
        previousUptoDateGrossCpwd: mergedPrev.previousUptoDateGrossCpwd,
        previousUptoDateSecuredAdvance: mergedPrev.previousUptoDateSecuredAdvance,
        isFirstBill: chain.isFirstBill,
        previousUptoDateGross: next.previous_ra_upto_date_gross,
        previousUptoDateGrossIncSecAdv: next.previous_ra_gross_inc_sec_adv ?? next.previous_ra_upto_date_gross,
        secAdvanceMaterial: next.sec_advance_material ?? 0,
        mobilisationAdvance: next.mobilisation_advance ?? 0,
        advancePaymentWorkdoneNotMeasured: next.advance_payment_workdone_not_measured ?? 0,
        clause10cc10caPayment: next.clause_10cc_10ca_payment,
        raNo: raSeq,
        additionalSd: next.additional_sd_special_items,
        withheldItems: next.withheld_items,
        rates: getProjectRates(next.project_id),
        statutoryNetExclSecOverride: raBillStatutoryOverride(next),
        securityDepositOverride: raBillSecurityDepositOverride(next),
        ...raBillCpwdLine5Fallbacks(next),
      billMeta: next,
      });
      return {
        ...next,
        previous_ra_sec_advance_upto: mergedPrev.previousUptoDateSecuredAdvance,
        upto_date_gross_value_of_work_done: formula.upto_date_gross_value_of_work_done,
        gross_value_as_per_previous_ra_bill: formula.gross_value_as_per_previous_ra_bill,
        secured_advance_added_in_this_bill: formula.secured_advance_added_in_this_bill,
        upto_date_gross_inc_sec_adv: formula.upto_date_gross_inc_sec_adv,
        previous_ra_gross_inc_sec_adv: formula.previous_ra_gross_inc_sec_adv,
        net_value_work_done_since_prev_bill: formula.net_value_work_done_since_prev_bill,
        net_value_work_done_without_sec_adv: formula.net_value_work_done_without_sec_adv,
        current_bill_value: formula.current_bill_value,
        gross_work_done: formula.gross_work_done,
        security_deposit: formula.security_deposit,
        gst_deduction: formula.gst_deduction,
        income_tax_deduction: formula.income_tax_deduction,
        labour_cess: formula.labour_cess,
        total_recoveries: formula.total_recoveries,
        total_withheld: formula.total_withheld,
        deductions_total: formula.total_deductions,
        total_deductions: formula.total_deductions,
        net_payable: formula.net_payment_received,
        net_payment_received: formula.net_payment_received,
        balance_value: Math.max(0, formula.net_payment_received - Number(next.paid_value || 0)),
        percent_workdone: formula.percent_workdone,
      };
    });
  };

  const handleDeleteRABill = async (bill, e) => {
    e?.stopPropagation?.();
    const id = bill?.id != null ? Number(bill.id) : NaN;
    if (!Number.isInteger(id) || id < 1) {
      notifyError('Invalid RA Bill ID. Please refresh the page and try again.');
      return;
    }
    if (!(await confirmDialog(`Delete RA Bill "${bill.ra_ref}"? This action cannot be undone.`, 'Delete RA Bill'))) return;
    try {
      await raBillsAPI.delete(id);
      setRaBills((prev) => prev.filter((b) => b.id !== bill.id));
      if (selectedBill?.id === bill.id) setSelectedBill(null);
      notifySuccess('RA Bill deleted.');
    } catch (err) {
      const data = err?.response?.data;
      const msg = (typeof data === 'object' && data?.message) || err?.message || 'Unable to delete RA Bill.';
      const hint = err?.response?.status === 404
        ? ' The bill may have been deleted already, or the server may need a restart.'
        : '';
      notifyError(msg + hint);
    }
  };

  const handleSaveBillControls = async () => {
    if (!selectedBill) return;
    const trimmedRestructureReason = String(restructureReason || '').trim();
    if (restructureMode && !trimmedRestructureReason) {
      notifyError('Restructure reason is required in Restructure mode.');
      return;
    }
    if (restructureMode && trimmedRestructureReason.length < MIN_RESTRUCTURE_REASON_LENGTH) {
      notifyError(`Restructure reason must be at least ${MIN_RESTRUCTURE_REASON_LENGTH} characters.`);
      return;
    }
    setRaControlSaving(true);
    try {
      await persistBillUpdate(
        selectedBill,
        restructureMode ? 'RA Bill restructured with audit trail.' : 'RA Bill controls updated.',
        {
          restructure: restructureMode,
          restructureReason: trimmedRestructureReason,
        },
      );
      if (restructureMode) {
        setRestructureMode(false);
        setRestructureReason('');
      }
    } catch (error) {
      console.error('Save RA controls failed:', error);
      notifyError('Unable to update RA Bill controls.');
    } finally {
      setRaControlSaving(false);
    }
  };

  const updateNewBillWithheldItems = (updater) => {
    setNewBill((prev) => ({
      ...prev,
      withheld_items: updater(safeArray(prev.withheld_items)),
    }));
  };

  const updateSelectedBillWithheldItems = (updater) => {
    updateSelectedBillFormula({
      withheld_items: updater(safeArray(selectedBill?.withheld_items)),
    });
  };

  const renderWithheldItemsEditor = ({ items, onAdd, onChange, onRemove }) => (
    <div style={{ display: 'grid', gap: 12 }}>
      {safeArray(items).length === 0 ? (
        <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No withheld deductions added.</p>
      ) : (
        safeArray(items).map((item, index) => (
          <div key={item.id || index} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, display: 'grid', gap: 12 }}>
            <div className="form-row">
              <div className="form-group">
                <label>Reason</label>
                <select value={item.reason || 'milestone'} onChange={(e) => onChange(index, 'reason', e.target.value)}>
                  {WITHHELD_REASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Amount Held</label>
                <input type="number" min="0" value={item.amount ?? ''} onChange={(e) => onChange(index, 'amount', e.target.value)} />
              </div>
            </div>
            {item.reason === 'other' && (
              <div className="form-group">
                <label>Reason Details</label>
                <input type="text" value={item.custom_reason || ''} onChange={(e) => onChange(index, 'custom_reason', e.target.value)} placeholder="Enter withheld reason" />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secondary" onClick={() => onRemove(index)}>Remove</button>
            </div>
          </div>
        ))
      )}
      <div>
        <button type="button" className="btn-secondary" onClick={onAdd}>Add Withheld Reason</button>
      </div>
    </div>
  );

  const handlePrintFinalRABill = () => {
    if (!selectedBill) return;
    handleOpenFinalPrint(selectedBill, selectedBillBGs);
  };

  const handlePreviewNewBillPrintable = async () => {
    if (!newBill.project_id) {
      notifyError('Select project before printable preview.');
      return;
    }
    const contractType = projectFinancials[Number(newBill.project_id)]?.contract_type || '';
    const division = contractType === CONTRACT_TYPES.ITEM_RATE ? (newBill.division || '') : '';
    const raNo = Number(newBill.ra_no || 1);
    const raRef = division ? `RA-${division}-${raNo}` : `RA-${raNo}`;
    await handleOpenFinalPrint({
      ...newBill,
      project_id: Number(newBill.project_id),
      ra_no: raNo,
      ra_ref: raRef,
      bill_no: raRef,
      net_payable: Number(newBill.net_payable || newBill.net_payment_received || 0),
      total_deductions: Number(newBill.total_deductions || 0),
      total_recoveries: Number(newBill.total_recoveries || 0),
      total_withheld: Number(newBill.total_withheld || 0),
      payment_status: newBill.payment_status || 'Unpaid',
      submitted_date: newBill.ra_bill_date || new Date().toISOString().slice(0, 10),
      paid_value: Number(newBill.paid_value || 0),
      balance_value: Number(newBill.balance_value || newBill.net_payable || 0),
      payments: [],
      withheld_items: safeArray(newBill.withheld_items),
      linked_bg_ids: safeArray(newBill.linked_bg_ids),
      primary_bg_id: newBill.primary_bg_id ? Number(newBill.primary_bg_id) : null,
      additional_bill_rows: normalizeAdditionalBillRows(newBill.additional_bill_rows),
    }, projectBGs);
  };

  const handlePrintPreview = () => {
    const frame = printPreviewFrameRef.current;
    if (!frame?.contentWindow) return;
    frame.contentWindow.focus();
    frame.contentWindow.print();
  };

  const handleDownloadPreview = async () => {
    const frame = printPreviewFrameRef.current;
    const sourceElement = frame?.contentDocument?.body;
    if (!sourceElement || previewDownloading) return;

    setPreviewDownloading(true);
    try {
      const html2pdfModule = await import('html2pdf.js');
      const html2pdf = html2pdfModule.default || html2pdfModule;
      const exportNode = sourceElement.cloneNode(true);
      exportNode.style.padding = '12px';
      exportNode.style.background = '#ffffff';
      exportNode.style.color = '#111827';

      await html2pdf()
        .set({
          margin: 8,
          filename: (raPrintPreview?.fileName || 'ra-bill-preview.html').replace(/\.html$/i, '.pdf'),
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        })
        .from(exportNode)
        .save();
    } catch (error) {
      console.error('RA Bill PDF download failed:', error);
      notifyError('Unable to download RA Bill as PDF.');
    } finally {
      setPreviewDownloading(false);
    }
  };

  const handleOpenFinalPrint = async (bill, preloadedBGs = null) => {
    if (!bill) return;
    let resolvedBGs = Array.isArray(preloadedBGs) ? preloadedBGs : null;
    if (!resolvedBGs) {
      try {
        const response = await bankGuaranteesAPI.getAll({ projectId: bill.project_id, page: 1, pageSize: 200 });
        const { rows: rawRows } = unwrapListResponse(response);
        const projectLinkedBGs = safeArray(rawRows).map(normalizeLinkedBG);
        const relevantIds = new Set([
          bill.primary_bg_id,
          ...safeArray(bill.linked_bg_ids),
        ].filter(Boolean).map(Number));
        resolvedBGs = relevantIds.size > 0
          ? projectLinkedBGs.filter((bg) => relevantIds.has(Number(bg.id)))
          : [];
      } catch (_error) {
        resolvedBGs = [];
      }
    }

    const project = projects.find((item) => Number(item.id) === Number(bill.project_id));
    const generatedAt = new Date().toLocaleString('en-IN');
    const printableWithheldItems = safeArray(bill.withheld_items).filter((item) => Number(item.amount || 0) > 0);
    const currentRaNo = parseRANumber(bill);
    const previousBill = safeArray(computedBills)
      .filter((row) => Number(row.project_id) === Number(bill.project_id))
      .filter((row) => String(row.division || '') === String(bill.division || ''))
      .filter((row) => parseRANumber(row) < currentRaNo)
      .sort((a, b) => parseRANumber(b) - parseRANumber(a))[0] || null;

    const showPrev = (value) => (
      previousBill ? formatCurrencyPlain(value) : '-'
    );
    const comparisonBaseLabel = previousBill
      ? (previousBill.ra_ref || `RA-${previousBill.ra_no || parseRANumber(previousBill)}`)
      : 'No previous RA';

    const extraUnionRows = unionAdditionalBillRows(
      previousBill?.additional_bill_rows,
      bill.additional_bill_rows,
    );
    const extraPrintRows = (sectionCode) => additionalRowsForSection(extraUnionRows, sectionCode)
      .map((row) => [row.label, showPrev(row.previous_value), formatCurrencyPlain(row.current_value)]);

    const currentBillLabel = bill.ra_ref || `RA-${bill.ra_no || parseRANumber(bill)}`;
    const previousBillLabel = previousBill
      ? (previousBill.ra_ref || `RA-${previousBill.ra_no || parseRANumber(previousBill)}`)
      : 'No previous RA';

    const parseDisplayAmount = (value) => {
      if (value == null) return null;
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      const cleaned = String(value).replace(/,/g, '').trim();
      if (!cleaned || cleaned === '-') return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const buildAmountRows = (rows) => rows.map(([label, prevAmount, currentAmount], index) => {
      const prevNumeric = parseDisplayAmount(prevAmount);
      const currentNumeric = parseDisplayAmount(currentAmount);
      const sincePrevDisplay = currentNumeric == null
        ? '-'
        : formatCurrencyPlain(prevNumeric == null ? currentNumeric : (currentNumeric - prevNumeric));
      return `
      <tr>
        <td class="${index === rows.length - 1 ? 'total-row' : ''}">${escapeHtml(label)}</td>
        <td class="amount ${index === rows.length - 1 ? 'total-row' : ''}">${escapeHtml(currentAmount)}</td>
        <td class="amount ${index === rows.length - 1 ? 'total-row' : ''}">${escapeHtml(prevAmount)}</td>
        <td class="amount ${index === rows.length - 1 ? 'total-row' : ''}">${escapeHtml(sincePrevDisplay)}</td>
      </tr>
    `;
    }).join('');

    const paymentRows = safeArray(bill.payments).length > 0
      ? safeArray(bill.payments).map((payment, index) => `
        <tr>
          <td class="center">${index + 1}</td>
          <td>${escapeHtml(formatDateIN(payment.payment_date))}</td>
          <td class="amount">${escapeHtml(formatCurrencyPlain(payment.amount_received))}</td>
          <td>${escapeHtml(payment.paying_authority || '-')}</td>
          <td>${escapeHtml(payment.voucher_no || '-')}</td>
          <td>${escapeHtml(`${payment.cm_book_no || '-'}${payment.cm_book_page_nos ? ` / ${payment.cm_book_page_nos}` : ''}`)}</td>
          <td>${escapeHtml(`${payment.cash_book_no || '-'}${payment.cash_book_page_no ? ` / ${payment.cash_book_page_no}` : ''}`)}</td>
          <td>${escapeHtml(payment.remarks || '-')}</td>
        </tr>
      `).join('')
      : `
        <tr>
          <td colspan="8" class="center muted">No payment records added.</td>
        </tr>
      `;

    const bgRows = resolvedBGs.length > 0
      ? resolvedBGs.map((bg, index) => `
        <tr>
          <td class="center">${index + 1}</td>
          <td>${escapeHtml(getBGTowardsLabel(bg))}</td>
          <td>${escapeHtml(bg.bg_no || '-')}</td>
          <td>${escapeHtml(formatDateIN(bg.issue_date))}</td>
          <td class="amount">${escapeHtml(formatCurrencyPlain(bg.amount))}</td>
          <td>${escapeHtml(bg.bank || '-')}</td>
          <td>${escapeHtml(formatDateIN(bg.expiry_date))}</td>
          <td>${escapeHtml(safeArray(bg.attachments).map((file) => file?.name).filter(Boolean).join(', ') || 'No attachments')}</td>
        </tr>
      `).join('')
      : `
        <tr>
          <td class="center">-</td>
          <td>-</td>
          <td>-</td>
          <td>-</td>
          <td class="amount">-</td>
          <td>-</td>
          <td>-</td>
          <td>No linked BG details</td>
        </tr>
      `;

    const holdRemarks = bill.billing_hold_reason
      ? `
        <table class="excel-table">
          <tbody>
            <tr><th class="section-title" colspan="2">Billing Hold Remarks</th></tr>
            <tr><td colspan="2">${escapeHtml(bill.billing_hold_reason)}</td></tr>
          </tbody>
        </table>
      `
      : '';

    const usesTemplatePrint = isConfigurableSnapshot(bill);
    const templatePrintRows = usesTemplatePrint ? buildTemplateComparisonRows(bill, previousBill) : [];
    const templateTitle = bill.ra_template_snapshot?.name
      || bill.ra_template_code
      || 'RA Bill Template';
    const templateMetaLine = usesTemplatePrint
      ? `<p style="margin:0 0 8px; font-size:12px; color:#166534;">
          Template: <strong>${escapeHtml(templateTitle)}</strong>
          ${bill.ra_template_version != null ? ` v${escapeHtml(bill.ra_template_version)}` : ''}
          · Frozen calculation (not CPWD sheet)
        </p>`
      : '';

    let amountTablesHtml = '';
    if (usesTemplatePrint) {
      let lastSection = null;
      const sectionBlocks = [];
      templatePrintRows.forEach((row) => {
        if (row.sectionName && row.sectionName !== lastSection) {
          lastSection = row.sectionName;
          sectionBlocks.push({ sectionName: row.sectionName, rows: [row] });
        } else if (sectionBlocks.length) {
          sectionBlocks[sectionBlocks.length - 1].rows.push(row);
        } else {
          sectionBlocks.push({ sectionName: 'Bill Fields', rows: [row] });
        }
      });
      if (!sectionBlocks.length) {
        amountTablesHtml = `
          <table class="excel-table">
            <tbody>
              <tr><td class="center muted">No frozen template field values on this bill.</td></tr>
            </tbody>
          </table>
        `;
      } else {
      amountTablesHtml = sectionBlocks.map((block) => `
        <table class="excel-table">
          <thead>
            <tr><th class="section-title" colspan="4">${escapeHtml(block.sectionName || templateTitle)}</th></tr>
            <tr>
              <th>Particulars</th>
              <th class="amount">Current (${escapeHtml(currentBillLabel)}) (Rs.)</th>
              <th class="amount">Previous (${escapeHtml(previousBillLabel)}) (Rs.)</th>
              <th class="amount">Since Previous (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            ${buildAmountRows(block.rows.map((r) => [
              r.fieldLabel + (r.fieldType === 'FORMULA' || r.fieldType === 'PREVIOUS_RA'
                ? ` [${r.fieldType === 'PREVIOUS_RA' ? 'Previous RA' : 'Calculated'}]`
                : ''),
              previousBill ? formatCurrencyPlain(r.previous) : '-',
              formatCurrencyPlain(r.current),
            ]))}
          </tbody>
        </table>
      `).join('');

      if (printableWithheldItems.length > 0) {
        amountTablesHtml += `
          <table class="excel-table">
            <thead>
              <tr><th class="section-title" colspan="4">Withheld Breakdown</th></tr>
              <tr><th>Particulars</th><th class="amount">Current (${escapeHtml(currentBillLabel)}) (Rs.)</th><th class="amount">Previous (${escapeHtml(previousBillLabel)}) (Rs.)</th><th class="amount">Since Previous (Rs.)</th></tr>
            </thead>
            <tbody>
              ${buildAmountRows([
                ...printableWithheldItems.map((item) => [getWithheldReasonLabel(item), '-', formatCurrencyPlain(item.amount)]),
                ['Total Withheld', showPrev(previousBill?.total_withheld ?? 0), formatCurrencyPlain(bill.total_withheld)],
              ])}
            </tbody>
          </table>
        `;
      }

      amountTablesHtml += `
        <table class="excel-table">
          <thead>
            <tr><th class="section-title" colspan="4">Bill Totals</th></tr>
            <tr><th>Particulars</th><th class="amount">Current (${escapeHtml(currentBillLabel)}) (Rs.)</th><th class="amount">Previous (${escapeHtml(previousBillLabel)}) (Rs.)</th><th class="amount">Since Previous (Rs.)</th></tr>
          </thead>
          <tbody>
            ${buildAmountRows([
              ['Total Recoveries', showPrev(previousBill?.total_recoveries ?? 0), formatCurrencyPlain(bill.total_recoveries)],
              ['Total Deductions', showPrev(previousBill?.total_deductions ?? 0), formatCurrencyPlain(bill.total_deductions)],
              ['Net Payment / Net Payable', showPrev(previousBill?.net_payment_received ?? previousBill?.net_payable ?? 0), formatCurrencyPlain(bill.net_payment_received || bill.net_payable)],
            ])}
          </tbody>
        </table>
      `;
      }
    } else {
      // Legacy CPWD sheet (bills without configurable template snapshot)
      amountTablesHtml = `
        <table class="excel-table">
          <thead>
            <tr><th class="section-title" colspan="4">Work Done and Bill Value (Current vs Previous Bill)</th></tr>
            <tr><th>Description</th><th class="amount">Current Bill (${escapeHtml(currentBillLabel)}) (Rs.)</th><th class="amount">Previous Bill (${escapeHtml(previousBillLabel)}) (Rs.)</th><th class="amount">Since Previous Bill (Rs.)</th></tr>
          </thead>
          <tbody>
            ${buildAmountRows([
              ['Agreement Amount (Rs.)', showPrev(previousBill?.agreement_amount ?? 0), formatCurrencyPlain(bill.agreement_amount)],
              ['1. Gross Value of Work done (Rs.)', showPrev(previousBill?.upto_date_gross_work_done ?? previousBill?.gross_value_of_work_done ?? 0), formatCurrencyPlain(bill.upto_date_gross_work_done ?? bill.gross_value_of_work_done)],
              ['2 (a). Upto date Secured Advance on material (Rs.)', showPrev(previousBill?.sec_advance_material ?? 0), formatCurrencyPlain(bill.sec_advance_material ?? 0)],
              ['2 (b). Mobilisation Advance (Rs.)', showPrev(previousBill?.mobilisation_advance ?? 0), formatCurrencyPlain(bill.mobilisation_advance ?? 0)],
              ['2 (Clause 10CC / 10CA payment) (Rs.)', showPrev(previousBill?.clause_10cc_10ca_payment ?? 0), formatCurrencyPlain(bill.clause_10cc_10ca_payment ?? 0)],
              ['2 (c). Advance payment (work done, not measured) (Rs.)', showPrev(previousBill?.advance_payment_workdone_not_measured ?? 0), formatCurrencyPlain(bill.advance_payment_workdone_not_measured ?? 0)],
              ['3. Upto date Gross Value of Work done — sum of above (Rs.)', showPrev(previousBill?.upto_date_gross_value_of_work_done ?? previousBill?.upto_date_gross_inc_sec_adv ?? previousBill?.upto_date_gross_work_done ?? 0), formatCurrencyPlain(bill.upto_date_gross_value_of_work_done ?? bill.upto_date_gross_inc_sec_adv ?? bill.upto_date_gross_work_done)],
              ['Secured Advance added in this bill (Rs.)', showPrev(previousBill?.secured_advance_added_in_this_bill ?? 0), formatCurrencyPlain(bill.secured_advance_added_in_this_bill ?? 0)],
              ['4. Gross value as per Previous RA Bill (Rs.)', showPrev(previousBill?.gross_value_as_per_previous_ra_bill ?? previousBill?.previous_ra_gross_inc_sec_adv ?? previousBill?.previous_ra_upto_date_gross ?? 0), formatCurrencyPlain(bill.gross_value_as_per_previous_ra_bill ?? bill.previous_ra_gross_inc_sec_adv ?? bill.previous_ra_upto_date_gross)],
              ['5 (a). Net value since Previous Bill (Rs.)', showPrev(previousBill?.net_value_work_done_since_prev_bill ?? previousBill?.current_bill_value ?? 0), formatCurrencyPlain(bill.net_value_work_done_since_prev_bill ?? bill.current_bill_value)],
              ['5 (b). Net Value excluding Secured Advance (Rs.)', showPrev(previousBill?.net_value_work_done_without_sec_adv ?? previousBill?.current_bill_value ?? 0), formatCurrencyPlain(bill.net_value_work_done_without_sec_adv ?? bill.current_bill_value)],
              ...extraPrintRows(SECTION_CODES.WORK_VALUE),
            ])}
          </tbody>
        </table>

        <table class="excel-table">
          <thead>
            <tr><th class="section-title" colspan="4">Deductions - Recoveries Breakdown (Current vs Previous Bill)</th></tr>
            <tr><th>Particulars</th><th class="amount">Current Bill (${escapeHtml(currentBillLabel)}) (Rs.)</th><th class="amount">Previous Bill (${escapeHtml(previousBillLabel)}) (Rs.)</th><th class="amount">Since Previous Bill (Rs.)</th></tr>
          </thead>
          <tbody>
            ${buildAmountRows([
              ['a) Security Deposit @ 2.5% (Rs.)', showPrev(previousBill?.security_deposit ?? 0), formatCurrencyPlain(bill.security_deposit)],
              ['b) GST @ 2% (Rs.)', showPrev(previousBill?.gst_deduction ?? 0), formatCurrencyPlain(bill.gst_deduction)],
              ['c) Income Tax @ 2% (Rs.)', showPrev(previousBill?.income_tax_deduction ?? 0), formatCurrencyPlain(bill.income_tax_deduction)],
              ['d) Labour Cess @ 1% (Rs.)', showPrev(previousBill?.labour_cess ?? 0), formatCurrencyPlain(bill.labour_cess)],
              ['e) Addl.SD for Spl.items (Rs.)', showPrev(previousBill?.additional_sd_special_items ?? 0), formatCurrencyPlain(bill.additional_sd_special_items)],
              ...extraPrintRows(SECTION_CODES.RECOVERIES),
              ['Total Recoveries', showPrev(previousBill?.total_recoveries ?? 0), formatCurrencyPlain(bill.total_recoveries)],
            ])}
          </tbody>
        </table>

        ${printableWithheldItems.length > 0 ? `
          <table class="excel-table">
            <thead>
              <tr><th class="section-title" colspan="4">Deductions - Withheld Breakdown (Current vs Previous Bill)</th></tr>
              <tr><th>Particulars</th><th class="amount">Current Bill (${escapeHtml(currentBillLabel)}) (Rs.)</th><th class="amount">Previous Bill (${escapeHtml(previousBillLabel)}) (Rs.)</th><th class="amount">Since Previous Bill (Rs.)</th></tr>
            </thead>
            <tbody>
              ${buildAmountRows([
                ...printableWithheldItems.map((item) => [getWithheldReasonLabel(item), '-', formatCurrencyPlain(item.amount)]),
                ['Total Withheld', showPrev(previousBill?.total_withheld ?? 0), formatCurrencyPlain(bill.total_withheld)],
                ['9. Total Deductions (7)+(8)', showPrev(previousBill?.total_deductions ?? 0), formatCurrencyPlain(bill.total_deductions)],
                ['10. Net Payment Received (5)-(9) (Rs.)', showPrev(previousBill?.net_payment_received ?? previousBill?.net_payable ?? 0), formatCurrencyPlain(bill.net_payment_received || bill.net_payable)],
                ...extraPrintRows(SECTION_CODES.TOTALS),
              ])}
            </tbody>
          </table>
        ` : `
          <table class="excel-table">
            <thead>
              <tr><th class="section-title" colspan="4">Bill Totals (Current vs Previous Bill)</th></tr>
              <tr><th>Particulars</th><th class="amount">Current Bill (${escapeHtml(currentBillLabel)}) (Rs.)</th><th class="amount">Previous Bill (${escapeHtml(previousBillLabel)}) (Rs.)</th><th class="amount">Since Previous Bill (Rs.)</th></tr>
            </thead>
            <tbody>
              ${buildAmountRows([
                ['9. Total Deductions (7)+(8)', showPrev(previousBill?.total_deductions ?? 0), formatCurrencyPlain(bill.total_deductions)],
                ['10. Net Payment Received (5)-(9) (Rs.)', showPrev(previousBill?.net_payment_received ?? previousBill?.net_payable ?? 0), formatCurrencyPlain(bill.net_payment_received || bill.net_payable)],
                ...extraPrintRows(SECTION_CODES.TOTALS),
              ])}
            </tbody>
          </table>
        `}
      `;
    }

    const body = `
      <style>
        .ra-sheet { font-family: Arial, sans-serif; font-size: 12px; color: #111827; }
        .ra-sheet .excel-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
        .ra-sheet .excel-table th,
        .ra-sheet .excel-table td { border: 1px solid #9ca3af; padding: 6px 8px; vertical-align: middle; }
        .ra-sheet .sheet-main-title { background: #e9e1f5; text-align: center; font-weight: 700; }
        .ra-sheet .section-title { background: #eef2f7; text-align: left; font-weight: 700; }
        .ra-sheet .head-cell { background: #f8fafc; font-weight: 600; }
        .ra-sheet .amount { text-align: right; white-space: nowrap; }
        .ra-sheet .center { text-align: center; }
        .ra-sheet .muted { color: #6b7280; }
        .ra-sheet .total-row { font-weight: 700; background: #f3f4f6; }
        .ra-sheet .bill-title { font-size: 16px; font-weight: 700; margin: 0 0 6px; }
      </style>
      <div class="ra-sheet">
        <p class="bill-title">Running Account Bill${project?.name ? ` - ${escapeHtml(project.name)}` : ''}</p>
        <p style="margin:0 0 8px; font-size:12px; color:#374151;">
          Comparison Base: <strong>${escapeHtml(comparisonBaseLabel)}</strong>
        </p>
        ${templateMetaLine}
        <table class="excel-table">
          <tbody>
            <tr>
              <td colspan="5"></td>
              <th class="head-cell">No.of bills paid by dept.</th>
              <td class="center">${escapeHtml(bill.bills_paid_by_dept)}</td>
              <th class="head-cell">This R.A Bill No.</th>
              <td class="center">${escapeHtml(bill.ra_no || bill.ra_ref || '-')}</td>
            </tr>
            <tr>
              <th class="head-cell">Period of this Bill</th>
              <td colspan="5">${escapeHtml(formatDateOrPlaceholder(bill.period_from))} to ${escapeHtml(formatDateOrPlaceholder(bill.period_to))}</td>
              <th class="head-cell">% Workdone</th>
              <td colspan="2" class="center">${escapeHtml(formatPercentValue(bill.percent_workdone))}</td>
            </tr>
            ${bill.division ? `
            <tr>
              <th class="head-cell">Division / Category</th>
              <td colspan="8">${escapeHtml(bill.division)}</td>
            </tr>
            ` : ''}
          </tbody>
        </table>

        ${amountTablesHtml}

        <table class="excel-table">
          <thead>
            <tr><th class="section-title" colspan="2">Payment Status</th></tr>
            <tr><th>Field</th><th>Value</th></tr>
          </thead>
          <tbody>
            <tr><td>Paid Value (Rs.)</td><td class="amount">${escapeHtml(formatCurrencyPlain(bill.paid_value))}</td></tr>
            <tr><td>Balance Value (Rs.)</td><td class="amount">${escapeHtml(formatCurrencyPlain(bill.balance_value))}</td></tr>
            <tr><td>Last Payment Received Date</td><td>${escapeHtml(formatDateIN(bill.payment_received_date))}</td></tr>
            <tr><td>Payment Status</td><td>${escapeHtml(bill.payment_status || '-')}</td></tr>
            <tr><td>Active Risk Flags</td><td>${escapeHtml(getIssueFlags(bill).join(', ') || 'No active flags')}</td></tr>
          </tbody>
        </table>

        <table class="excel-table">
          <thead>
            <tr><th class="section-title" colspan="8">Payment Details</th></tr>
            <tr>
              <th>Sl. No</th>
              <th>Date</th>
              <th class="amount">Amount (Rs.)</th>
              <th>Authority</th>
              <th>Voucher No</th>
              <th>CM Book</th>
              <th>Cash Book</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${paymentRows}
          </tbody>
        </table>

        <table class="excel-table">
          <thead>
            <tr><th class="section-title" colspan="8">Details of Bank Guarantees Submitted</th></tr>
            <tr>
              <th>Sl.No.</th>
              <th>Towards</th>
              <th>Instrument</th>
              <th>Date</th>
              <th class="amount">Amount (Rs.)</th>
              <th>Bank</th>
              <th>Validity date</th>
              <th>Attached details</th>
            </tr>
          </thead>
          <tbody>
            ${bgRows}
          </tbody>
        </table>
        ${holdRemarks}
      </div>
    `;

    const title = 'Running Account Bill';
    const subtitle = `RA Bill: ${bill.ra_ref || `RA-${bill.ra_no}`} | Generated: ${generatedAt}`;
    const fullHtml = getReportFullHtml({
      title,
      subtitle,
      bodyHtml: body,
      displayTitle: false,
      displaySubtitle: false,
    });

    setRaPrintPreview({
      title,
      subtitle,
      bodyHtml: body,
      fullHtml,
      fileName: `${bill.ra_ref || `RA-${bill.ra_no}`}.html`,
    });
  };

  const closePrintPreview = () => {
    setRaPrintPreview(null);
  };

  const handleDownloadRegisterPdf = () => {
    if (!hasProjectSelected) {
      notifyError('Select a project before downloading the register PDF.');
      return;
    }
    const grouped = new Map();
    safeArray(filteredBills).forEach((bill) => {
      const pid = bill.project_id || 0;
      if (!grouped.has(pid)) grouped.set(pid, []);
      grouped.get(pid).push(bill);
    });

    let bodyHtml = '';

    const isRecoveriesWithheldPrint = mainRegisterTab === 'recoveries-withheld';
    const includeUptoGrossCol = !isRecoveriesWithheldPrint;
    const includeNetPaymentCol = !isRecoveriesWithheldPrint;
    const includeRABillDateCol = !isRecoveriesWithheldPrint;

    const buildRegisterHeaders = (rates = {}) => {
      const sdPct = Number(rates.security_deposit_pct ?? DEFAULT_RA_RATES.security_deposit_pct);
      const gstPct = Number(rates.gst_pct ?? DEFAULT_RA_RATES.gst_pct);
      const itPct = Number(rates.income_tax_pct ?? DEFAULT_RA_RATES.income_tax_pct);
      const cessPct = Number(rates.labour_cess_pct ?? DEFAULT_RA_RATES.labour_cess_pct);
      const headers = [
        'Sl. No',
        'RA Ref',
        'Net Since Prev (Rs.)',
        `SD (${sdPct}%) (Rs.)`,
        `GST (${gstPct}%) (Rs.)`,
        `IT (${itPct}%) (Rs.)`,
        `Labour Cess (${cessPct}%) (Rs.)`,
        'Addl. SD (Rs.)',
        'Total Deductions (Rs.)',
        'Total Withheld (Rs.)',
      ];
      if (includeRABillDateCol) headers.splice(2, 0, 'RA bill date');
      if (includeUptoGrossCol) {
        headers.splice(3, 0, 'Upto Date Gross (Rs.)');
      }
      if (includeNetPaymentCol) headers.push('Net Payment (Rs.)');
      return headers;
    };

    const registerAmountCols = buildRegisterHeaders().reduce((indices, header, index) => {
      if (header.includes('(Rs.)')) indices.push(index);
      return indices;
    }, []);

    const buildRegisterRowsForBills = (bills, totalRowLabel) => {
      const rows = [];
      let totals = {
        netSince: 0,
        sd: 0,
        gst: 0,
        it: 0,
        cess: 0,
        addl: 0,
        deductions: 0,
        withheld: 0,
        net: 0,
      };
      const sorted = [...safeArray(bills)].sort((a, b) => parseRANumber(a) - parseRANumber(b));
      sorted.forEach((bill, index) => {
        const upto = Number(bill.upto_date_gross_value_of_work_done ?? bill.upto_date_gross_inc_sec_adv ?? 0);
        const netS = Number(bill.net_value_work_done_since_prev_bill ?? 0);
        const sd = Number(bill.security_deposit || 0);
        const gst = Number(bill.gst_deduction || 0);
        const it = Number(bill.income_tax_deduction || 0);
        const cess = Number(bill.labour_cess || 0);
        const addl = Number(bill.additional_sd_special_items || 0);
        totals.netSince += netS;
        totals.sd += sd;
        totals.gst += gst;
        totals.it += it;
        totals.cess += cess;
        totals.addl += addl;
        totals.deductions += Number(bill.total_deductions || 0);
        totals.withheld += Number(bill.total_withheld || 0);
        totals.net += Number(bill.net_payment_received || bill.net_payable || 0);

        const row = [
          String(index + 1),
          String(bill.ra_ref || ''),
          formatCurrencyPlain(netS),
          formatCurrencyPlain(sd),
          formatCurrencyPlain(gst),
          formatCurrencyPlain(it),
          formatCurrencyPlain(cess),
          formatCurrencyPlain(addl),
          formatCurrencyPlain(bill.total_deductions),
          formatCurrencyPlain(bill.total_withheld),
        ];
        if (includeRABillDateCol) row.splice(2, 0, formatDateOrPlaceholder(effectiveRABillDate(bill)));
        if (includeUptoGrossCol) {
          row.splice(3, 0, formatCurrencyPlain(upto));
        }
        if (includeNetPaymentCol) row.push(formatCurrencyPlain(bill.net_payment_received || bill.net_payable));
        rows.push(row);
      });

      const latestUptoGross = sumLatestGrossIncSecAdvPerStream(bills, projectFinancials);
      const totalRow = [
        totalRowLabel,
        '',
        formatCurrencyPlain(totals.netSince),
        formatCurrencyPlain(totals.sd),
        formatCurrencyPlain(totals.gst),
        formatCurrencyPlain(totals.it),
        formatCurrencyPlain(totals.cess),
        formatCurrencyPlain(totals.addl),
        formatCurrencyPlain(totals.deductions),
        formatCurrencyPlain(totals.withheld),
      ];
      if (includeRABillDateCol) totalRow.splice(2, 0, '');
      if (includeUptoGrossCol) {
        totalRow.splice(3, 0, formatCurrencyPlain(latestUptoGross));
      }
      if (includeNetPaymentCol) totalRow.push(formatCurrencyPlain(totals.net));
      rows.push(totalRow);
      return rows;
    };

    if (grouped.size === 0) {
      bodyHtml = tableHtmlFromRows({
        headers: buildRegisterHeaders(),
        rows: [],
        emptyMessage: 'No RA Bills found for selected filters.',
        amountColumnIndices: registerAmountCols,
      });
    } else {
      grouped.forEach((bills, projectId) => {
        const project = projects.find((p) => p.id === projectId);
        const projectName = project?.name || `Project ${projectId || '-'}`;
        const contractType = projectFinancials[projectId]?.contract_type || '';
        const projectRates = projectFinancials[projectId]?.ra_deduction_rates || DEFAULT_RA_RATES;
        const projectAgreementAmount = Number(projectFinancials[projectId]?.agreement_amount ?? safeArray(bills)[0]?.agreement_amount ?? 0);
        const latestBillForProgress = [...safeArray(bills)].sort((a, b) => {
          const raOrder = parseRANumber(a) - parseRANumber(b);
          if (raOrder !== 0) return raOrder;
          return new Date(effectiveRABillDate(a) || 0).getTime() - new Date(effectiveRABillDate(b) || 0).getTime();
        }).pop();
        const financialProgressPct = Number(latestBillForProgress?.percent_workdone || 0);
        const registerHeaders = buildRegisterHeaders(projectRates);

        bodyHtml += `<h2 style="margin:14px 0 6px;font-size:20px;font-weight:700;color:#111827;">${escapeHtml(projectName)}</h2>`;
        bodyHtml += `
          <div style="display:flex;justify-content:space-between;gap:16px;flex-wrap:wrap;margin:-4px 0 10px;">
            <div style="font-size:13px;color:#111827;">Agreement Amount (Rs.): <strong>${escapeHtml(formatCurrencyPlain(projectAgreementAmount))}</strong></div>
            <div style="font-size:13px;color:#111827;">Financial Progress %: ${escapeHtml(formatPercentValue(financialProgressPct))}</div>
          </div>
        `;

        if (contractType === CONTRACT_TYPES.ITEM_RATE) {
          const byStream = new Map();
          safeArray(bills).forEach((bill) => {
            const key = getRABillStreamKey(bill, projectFinancials);
            if (!byStream.has(key)) byStream.set(key, []);
            byStream.get(key).push(bill);
          });
          const streamKeys = [...byStream.keys()].sort((a, b) => {
            const oa = streamKeySortOrder(a);
            const ob = streamKeySortOrder(b);
            if (oa !== ob) return oa - ob;
            return String(a).localeCompare(String(b));
          });
          streamKeys.forEach((streamKey) => {
            const streamBills = byStream.get(streamKey);
            const divLabel = streamKeyDivisionLabel(streamKey);
            bodyHtml += `<h3 style="margin:12px 0 6px;font-size:15px;">${divLabel ? escapeHtml(divLabel) : 'Uncategorised division'}</h3>`;
            const totalLabel = divLabel ? `Total — ${divLabel}` : 'Total';
            const rows = buildRegisterRowsForBills(streamBills, totalLabel);
            const latestStreamBill = [...safeArray(streamBills)].sort((a, b) => {
              const raOrder = parseRANumber(a) - parseRANumber(b);
              if (raOrder !== 0) return raOrder;
              return new Date(effectiveRABillDate(a) || 0).getTime() - new Date(effectiveRABillDate(b) || 0).getTime();
            }).pop();
            bodyHtml += `
              <div style="display:flex;justify-content:flex-end;margin:-2px 0 8px;">
                <div style="font-size:12px;color:#111827;">Financial Progress %: ${escapeHtml(formatPercentValue(Number(latestStreamBill?.percent_workdone || 0)))}</div>
              </div>
            `;
            bodyHtml += tableHtmlFromRows({
              headers: registerHeaders,
              rows,
              emptyMessage: 'No RA Bills in this division.',
              amountColumnIndices: registerAmountCols,
            });
          });
        } else {
          const rows = buildRegisterRowsForBills(bills, 'Project Total');
          bodyHtml += tableHtmlFromRows({
            headers: registerHeaders,
            rows,
            emptyMessage: 'No RA Bills found for this project.',
            amountColumnIndices: registerAmountCols,
          });
        }
      });
    }

    const reportTitle = mainRegisterTab === 'recoveries-withheld'
      ? 'Recoveries and with helds - Project-wise'
      : 'RA Bills Register - Project-wise';
    openPrintWindow({
      title: reportTitle,
      subtitle: `Projects: ${grouped.size || 0} | Records: ${safeArray(filteredBills).length}`,
      bodyHtml,
    });
  };

  return (
    <div>
      {showCreatePage && (
        <div className="ra-create-page">
          <div className="section-header ra-create-page__header">
            <h2>Create New RA Bill</h2>
            <button type="button" className="btn-secondary" onClick={closeCreatePage}>Back</button>
          </div>
          <div className="ra-create-page__body">
            <RABillDocument
              mode="create"
              bill={newBill}
              previousBill={previousBillSnapshot}
              onBillChange={setNewBill}
              projects={projects}
              projectFinancials={projectFinancials}
              formConfig={formConfig}
              departmentRuleInfo={departmentRuleInfo}
              departmentRuleLoading={departmentRuleLoading}
              projectChains={projectChains}
              projectBGs={projectBGs}
              linkedBGs={projectBGs.filter((bg) => {
                const ids = new Set(safeArray(newBill.linked_bg_ids).map(Number));
                if (newBill.primary_bg_id) ids.add(Number(newBill.primary_bg_id));
                return ids.has(Number(bg.id));
              })}
              customFields={customFields}
              customValues={customValues}
              onCustomChange={onCustomChange}
              withheldEditor={renderWithheldItemsEditor({
                items: newBill.withheld_items,
                onAdd: () => updateNewBillWithheldItems((items) => [...items, createWithheldItem()]),
                onChange: (index, field, value) => updateNewBillWithheldItems((items) => items.map((item, itemIndex) => {
                  if (itemIndex !== index) return item;
                  if (field === 'reason') {
                    return {
                      ...item,
                      reason: value,
                      custom_reason: value === 'other' ? item.custom_reason : '',
                    };
                  }
                  return {
                    ...item,
                    [field]: field === 'amount' ? (value === '' ? '' : Number(value)) : value,
                  };
                })),
                onRemove: (index) => updateNewBillWithheldItems((items) => items.filter((_, itemIndex) => itemIndex !== index)),
              })}
              attachmentUploading={attachmentUploading}
              onAttachmentChange={handleAttachmentChange}
              onRemoveAttachment={(idx) => setNewBill((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, i) => i !== idx) }))}
              generatedAt={createGeneratedAt}
              issueFlags={getIssueFlags({
                ...newBill,
                payment_status: newBill.payment_status || 'Unpaid',
              })}
              getBGTowardsLabel={getBGTowardsLabel}
              formatCurrency={formatCurrency}
              formatDateIN={formatDateIN}
              formatPercentValue={formatPercentValue}
              divisions={DIVISIONS}
              contractTypes={CONTRACT_TYPES}
              invalidField={createFieldError}
            />
          </div>
          <div className="modal-actions ra-create-page__footer">
            <button className="btn-secondary" onClick={closeCreatePage}>Cancel</button>
            <button className="btn-secondary" onClick={handlePreviewNewBillPrintable}>Printable Preview</button>
            <button className="btn-secondary" onClick={() => handleCreateRABill('Draft')} disabled={attachmentUploading}>Save Draft</button>
            <button className="btn-primary" onClick={() => handleCreateRABill('Submitted')} disabled={attachmentUploading}>Create / Submit RA Bill</button>
          </div>
        </div>
      )}
      <div hidden={showCreatePage}>
      <div className="section-header ra-register-header">
        <div className="ra-register-header__main">
          <h2>RA Bills Register <span className="count-badge">{filteredBills.length}</span></h2>
          <div className="ra-register-tabs" role="tablist" aria-label="RA Bills register views">
            <button
              type="button"
              role="tab"
              aria-selected={mainRegisterTab === 'register'}
              className={`ra-register-tab ${mainRegisterTab === 'register' ? 'is-active' : ''}`}
              onClick={() => setMainRegisterTab('register')}
            >
              RA register
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mainRegisterTab === 'recoveries-withheld'}
              className={`ra-register-tab ${mainRegisterTab === 'recoveries-withheld' ? 'is-active' : ''}`}
              onClick={() => setMainRegisterTab('recoveries-withheld')}
            >
              Recoveries &amp; withheld
            </button>
          </div>
        </div>
        <div className="ra-register-header__actions">
          <button
            className="btn-secondary"
            onClick={handleDownloadRegisterPdf}
          >
            Download PDF
          </button>
          <button className="btn-primary" onClick={openCreatePage}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New RA Bill
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={(value) => { setProjectFilter(value); setPage(1); }}
          projects={projects}
          requireSelection
          placeholderLabel="Select project"
        />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} disabled={!hasProjectSelected}>
          <option value="all">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Submitted">Submitted</option>
          <option value="Paid">Paid</option>
          <option value="Certified">Certified</option>
          <option value="Closed">Closed</option>
        </select>
        <select value={paymentFilter} onChange={(e) => { setPaymentFilter(e.target.value); setPage(1); }} disabled={!hasProjectSelected}>
          <option value="all">All Payment Status</option>
          <option value="Unpaid">Unpaid</option>
          <option value="Part Paid">Part Paid</option>
          <option value="Paid">Paid</option>
          <option value="Withheld">Withheld</option>
        </select>
        <select value={riskFilter} onChange={(e) => { setRiskFilter(e.target.value); setPage(1); }} disabled={!hasProjectSelected}>
          <option value="all">All Risk</option>
          <option value="overdue">Payment Overdue</option>
          <option value="certified_unpaid">Certified & unpaid</option>
          <option value="dependency">Dependency Issue</option>
          <option value="withheld">Withheld / Hold</option>
        </select>
        <select value={financialYearFilter} onChange={(e) => { setFinancialYearFilter(e.target.value); setPage(1); }} disabled={!hasProjectSelected}>
          <option value="all">All Financial Years</option>
          {financialYearOptions.map((fy) => (
            <option key={fy} value={fy}>{formatFinancialYearLabelIN(fy)}</option>
          ))}
        </select>
        <input
          type="date"
          aria-label="RA bill date from"
          value={registerDateFrom}
          disabled={!hasProjectSelected}
          onChange={(e) => { setRegisterDateFrom(e.target.value); setPage(1); }}
        />
        <input
          type="date"
          aria-label="RA bill date to"
          value={registerDateTo}
          disabled={!hasProjectSelected}
          onChange={(e) => { setRegisterDateTo(e.target.value); setPage(1); }}
        />
        <input
          type="search"
          placeholder="Search RA no. / ref"
          value={billSearch}
          disabled={!hasProjectSelected}
          onChange={(e) => { setBillSearch(e.target.value); setPage(1); }}
          style={{ minWidth: 160 }}
        />
        <ResetFiltersButton
          onClick={() => {
            setProjectFilter('');
            setStatusFilter('all');
            setPaymentFilter('all');
            setRiskFilter('all');
            setFinancialYearFilter('all');
            setRegisterDateFrom('');
            setRegisterDateTo('');
            setBillSearch('');
            setPage(1);
          }}
        />
      </div>

      {!hasProjectSelected ? (
        <div
          className="table-container"
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: '#6b7280',
            background: '#f9fafb',
            borderRadius: 8,
            border: '1px dashed #d1d5db',
          }}
        >
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#374151' }}>
            Select a project to view RA bills
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 13 }}>
            Bills and KPIs are shown for one project at a time, using that project&apos;s assigned RA Bill Template
            (or department default / CPWD).
          </p>
        </div>
      ) : (
        <>
          <div
            className="ra-dept-banner"
            style={{
              marginBottom: 12,
              padding: '10px 14px',
              borderRadius: 8,
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              fontSize: 13,
              color: '#166534',
            }}
          >
            <p style={{ margin: 0, fontWeight: 600, color: '#14532d' }}>
              Project: {stripBrackets(selectedRegisterProject?.name || `Project ${projectFilter}`)}
            </p>
            {registerTemplateLoading ? (
              <p style={{ margin: '4px 0 0' }}>Resolving RA Bill Template…</p>
            ) : registerTemplateInfo?.ra_template ? (
              <p style={{ margin: '4px 0 0' }}>
                Template:{' '}
                <strong>
                  {registerTemplateInfo.ra_template.name || registerTemplateInfo.ra_template.code}
                  {registerTemplateInfo.ra_template.version != null
                    ? ` v${registerTemplateInfo.ra_template.version}`
                    : ''}
                </strong>
                {' '}
                (
                {registerTemplateInfo.ra_template_source === 'project'
                  ? 'project assignment'
                  : registerTemplateInfo.ra_template_source === 'department'
                    ? 'department default'
                    : 'assigned'}
                )
                {String(registerTemplateInfo.ra_template.calculationEngine || '').toUpperCase() === 'CONFIGURABLE'
                  ? ' — new bills use this template sheet (Current / Previous / Since Previous).'
                  : ' — new bills use CPWD calculation.'}
              </p>
            ) : (
              <p style={{ margin: '4px 0 0', color: '#6b7280' }}>
                No configurable template assigned — new bills use the CPWD sheet.
                {registerTemplateInfo?.department?.name
                  ? ` Department: ${registerTemplateInfo.department.name}.`
                  : ''}
              </p>
            )}
          </div>

      {mainRegisterTab === 'register' ? (
              <>
                <div className="cards-grid kpi-cards" style={{ marginBottom: 16 }}>
                  <div className="card" style={{ cursor: 'default' }}>
                    <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total RA Bills</p>
                    <p className="kpi-value">{raKpis.totalBills}</p>
                    <p className="stat-sub" style={{ fontSize: 12, color: '#6b7280' }}>
                      Paid: {raKpis.paidCount} | Part Paid: {raKpis.partPaidCount} | Unpaid: {raKpis.unpaidCount}
                    </p>
                  </div>
                  <div
                    className="card"
                    style={{ cursor: 'default' }}
                    title="Upto date gross value of work done (incl. secured advance) — latest RA for this project"
                  >
                    <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Gross work done</p>
                    <p className="kpi-value">{formatKpiCrores(raKpis.latestGrossIncSecAdvKpi)}</p>
                    <p className="stat-sub" style={{ fontSize: 12, color: '#6b7280' }}>Latest RA upto-date gross</p>
                  </div>
                  <div className="card" style={{ cursor: 'default' }} title="Sum of total deductions across this project's bills">
                    <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Deductions</p>
                    <p className="kpi-value" style={{ color: '#ef4444' }}>{formatKpiCrores(raKpis.totalDeductions)}</p>
                  </div>
                  <div className="card" style={{ cursor: 'default' }} title="Sum of net payable / net payment across this project's bills">
                    <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Net Payable</p>
                    <p className="kpi-value">{formatKpiCrores(raKpis.totalNetPaymentSum)}</p>
                  </div>
                  <div className="card" style={{ cursor: 'default' }} title="Sum of paid_value across this project's bills">
                    <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Paid</p>
                    <p className="kpi-value" style={{ color: '#0f766e' }}>{formatKpiCrores(raKpis.totalPaid)}</p>
                  </div>
                  <div className="card" style={{ cursor: 'default' }} title="Unpaid / part-paid balance remaining">
                    <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Pending Payment</p>
                    <p className="kpi-value" style={{ color: '#b91c1c' }}>{formatKpiCrores(raKpis.pendingPayValue)}</p>
                    <p className="stat-sub" style={{ fontSize: 12, color: '#6b7280' }}>Bills: {raKpis.pendingPayCount}</p>
                  </div>
                </div>

                {registerBillsOrdered.length === 0 ? (
                  <div className="table-container" style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                    No RA bills for this project yet. Create a New RA Bill to populate the register table.
                  </div>
                ) : (
                  <div className="table-container" style={{ overflowX: 'auto', marginBottom: 16 }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>RA Bill</th>
                          {registerSectionMatrices.sectionColumns.map((col) => (
                            <th
                              key={col.key}
                              className="amount-cell"
                              title={col.fieldLabel !== col.sectionName ? col.fieldLabel : undefined}
                            >
                              {col.sectionName}
                            </th>
                          ))}
                          <th className="print-hidden">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registerBillsOrdered.map((bill) => (
                          <tr key={bill.id}>
                            <td>
                              <strong style={{ color: '#6D28D9' }}>{bill.ra_ref || `RA-${bill.ra_no}`}</strong>
                              <div style={{ marginTop: 4 }}>
                                <StatusBadge tone={getPaymentClass(bill.payment_status)}>
                                  {bill.payment_status}
                                </StatusBadge>
                              </div>
                            </td>
                            {registerSectionMatrices.sectionColumns.map((col) => (
                              <td key={col.key} className="amount-cell">
                                {formatCurrencyNoSymbol(col.valuesByBillId[String(bill.id)] ?? 0)}
                              </td>
                            ))}
                            <td className="print-hidden">
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="btn-secondary btn-sm"
                                  onClick={() => handleOpenFinalPrint(bill)}
                                >
                                  View
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary btn-sm"
                                  onClick={() => setSelectedBill(bill)}
                                >
                                  Edit
                                </button>
                                {isAdmin ? (
                                  <button
                                    type="button"
                                    className="btn-secondary btn-sm"
                                    style={{ color: '#b91c1c' }}
                                    onClick={(e) => handleDeleteRABill(bill, e)}
                                  >
                                    Delete
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p style={{ margin: '8px 0 0', fontSize: 11, color: '#6b7280' }}>
                      Columns show each template section total (preferred TOTAL / last calculated field in that section).
                    </p>
                  </div>
                )}
              </>
      ) : (
              <RARecoveriesWithheldView
                bills={filteredBills}
                projects={projects}
                showProjectColumn={showProjectColumn}
                onSelectBill={setSelectedBill}
                onSaveReleasedWithheld={(bill) => persistBillUpdate(bill, 'Released withhelds saved.', { preserveAmounts: true })}
                formatCurrency={formatCurrency}
                formatCurrencyNoSymbol={formatCurrencyNoSymbol}
                getPaymentClass={getPaymentClass}
                getStatusClass={getStatusClass}
              />
      )}
        </>
      )}

      {selectedBill && (
        <div className="modal-overlay">
          <div className="modal-content ra-detail-modal">
            <div className="modal-header">
              <h2>RA Bill Detail - {selectedBill.ra_ref}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" className="btn-secondary" onClick={handlePrintFinalRABill}>
                  Final Print
                </button>
                <button className="btn-close" onClick={() => setSelectedBill(null)}>
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="modal-body">
              <TabbedSection
                tabs={[
                  {
                    key: 'summary',
                    label: 'Summary',
                    content: (
                      <div className="ra-summary">
                        <section className="ra-create-card">
                          <h4 className="ra-create-card__title">Bill identity</h4>
                          <div className="ra-create-fields">
                            <RaReadonlyField label="Project">
                              {stripBrackets(projects.find((p) => p.id === selectedBill.project_id)?.name || '') || '—'}
                            </RaReadonlyField>
                            <RaReadonlyField label="Agreement No.">{selectedBill.agreement_no || '—'}</RaReadonlyField>
                            <RaReadonlyField label="RA No">{selectedBill.ra_no ?? '—'}</RaReadonlyField>
                            <RaReadonlyField label="RA Ref">{selectedBill.ra_ref || '—'}</RaReadonlyField>
                            <RaReadonlyField label="RA Bill Date">
                              {formatDateIN(selectedBill.ra_bill_date || selectedBill.submitted_date)}
                            </RaReadonlyField>
                            <RaReadonlyField label="Bills Paid by Dept.">{selectedBill.bills_paid_by_dept ?? '—'}</RaReadonlyField>
                            {selectedBill.division ? (
                              <RaReadonlyField label="Division">
                                <span style={{ color: DIVISION_COLORS[selectedBill.division] || '#374151', fontWeight: 600 }}>
                                  {selectedBill.division}
                                </span>
                              </RaReadonlyField>
                            ) : null}
                            <RaReadonlyField label="Status" valueClassName="is-badge">
                              <StatusBadge tone={getStatusClass(selectedBill.status)}>{selectedBill.status}</StatusBadge>
                            </RaReadonlyField>
                            <RaReadonlyField label="Agreement Amount" valueClassName="is-amount">
                              {formatCurrency(selectedBill.agreement_amount)}
                            </RaReadonlyField>
                            <RaReadonlyField label="Period">
                              {formatDateIN(selectedBill.period_from)} to {formatDateIN(selectedBill.period_to)}
                            </RaReadonlyField>
                            <RaReadonlyField label="Payment Status" valueClassName="is-badge">
                              <StatusBadge tone={getPaymentClass(selectedBill.payment_status)}>{selectedBill.payment_status}</StatusBadge>
                            </RaReadonlyField>
                            <RaReadonlyField label="Submitted Date">{formatDateIN(selectedBill.submitted_date)}</RaReadonlyField>
                            <RaReadonlyField label="Certified Date">{formatDateIN(selectedBill.certified_date)}</RaReadonlyField>
                            <RaReadonlyField
                              label="Payment Due Date"
                              valueClassName={
                                selectedBill.payment_due_date
                                && new Date(selectedBill.payment_due_date) < new Date()
                                && selectedBill.payment_status !== 'Paid'
                                  ? 'is-overdue'
                                  : ''
                              }
                            >
                              {formatDateIN(selectedBill.payment_due_date)}
                            </RaReadonlyField>
                          </div>
                        </section>

                        <div className="ra-stat-row">
                          <div className="ra-stat-card">
                            <label>Net Payable</label>
                            <strong className="is-net">{formatCurrency(selectedBill.net_payable)}</strong>
                          </div>
                          <div className="ra-stat-card">
                            <label>Paid</label>
                            <strong className="is-paid">{formatCurrency(selectedBill.paid_value)}</strong>
                          </div>
                          <div className="ra-stat-card">
                            <label>Balance</label>
                            <strong className={selectedBill.balance_value > 0 ? 'is-balance-due' : 'is-balance-ok'}>
                              {formatCurrency(selectedBill.balance_value)}
                            </strong>
                          </div>
                          <div className="ra-stat-card">
                            <label>Progress % (Agreement)</label>
                            <strong>{formatPercentValue(selectedBill.percent_workdone)}</strong>
                          </div>
                        </div>

                        {isConfigurableSnapshot(selectedBill) ? (
                          <section className="ra-create-card ra-create-card--flush">
                            <h4 className="ra-create-card__title">
                              Template bill —
                              {' '}
                              {selectedBill.ra_template_snapshot?.name
                                || selectedBill.ra_template_code
                                || 'CONFIGURABLE'}
                              {selectedBill.ra_template_version != null
                                ? ` v${selectedBill.ra_template_version}`
                                : ''}
                            </h4>
                            <div className="table-container">
                              <table className="data-table">
                                <thead>
                                  <tr>
                                    <th>Particulars</th>
                                    <th className="amount-cell">Current (Rs.)</th>
                                    <th className="amount-cell">Previous (Rs.)</th>
                                    <th className="amount-cell">Since Previous (Rs.)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const prevForSummary = safeArray(computedBills)
                                      .filter((row) => Number(row.project_id) === Number(selectedBill.project_id))
                                      .filter((row) => String(row.division || '') === String(selectedBill.division || ''))
                                      .filter((row) => parseRANumber(row) < parseRANumber(selectedBill))
                                      .sort((a, b) => parseRANumber(b) - parseRANumber(a))[0] || null;
                                    const rows = buildTemplateComparisonRows(selectedBill, prevForSummary);
                                    if (!rows.length) {
                                      return (
                                        <tr>
                                          <td colSpan="4" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>
                                            No template field values on this bill.
                                          </td>
                                        </tr>
                                      );
                                    }
                                    return rows.map((row) => (
                                      <React.Fragment key={row.fieldCode}>
                                        {row.sectionName ? (
                                          <tr>
                                            <td
                                              colSpan="4"
                                              style={{
                                                background: '#f3f4f6',
                                                fontWeight: 700,
                                                fontSize: 12,
                                              }}
                                            >
                                              {row.sectionName}
                                            </td>
                                          </tr>
                                        ) : null}
                                        <tr>
                                          <td>
                                            {row.fieldLabel}
                                            {row.fieldType === 'FORMULA' || row.fieldType === 'PREVIOUS_RA' ? (
                                              <span style={{ marginLeft: 6, color: '#6b7280', fontSize: 11 }}>
                                                [{row.fieldType === 'PREVIOUS_RA' ? 'Previous RA' : 'Calculated'}]
                                              </span>
                                            ) : null}
                                          </td>
                                          <td className="amount-cell">{formatCurrency(row.current)}</td>
                                          <td className="amount-cell">
                                            {prevForSummary ? formatCurrency(row.previous) : '—'}
                                          </td>
                                          <td className="amount-cell">{formatCurrency(row.since)}</td>
                                        </tr>
                                      </React.Fragment>
                                    ));
                                  })()}
                                </tbody>
                              </table>
                            </div>
                            <div className="ra-create-fields" style={{ padding: '12px 16px' }}>
                              <RaReadonlyField label="Billing Basis">{selectedBill.billing_basis_type || '—'}</RaReadonlyField>
                              <RaReadonlyField label="Dependency Status" valueClassName="is-badge">
                                <StatusBadge
                                  tone={
                                    selectedBill.billing_dependency_status === 'Blocked'
                                      ? 'critical'
                                      : selectedBill.billing_dependency_status === 'At Risk'
                                        ? 'high'
                                        : 'paid'
                                  }
                                >
                                  {selectedBill.billing_dependency_status}
                                </StatusBadge>
                              </RaReadonlyField>
                            </div>
                          </section>
                        ) : (
                          <section className="ra-create-card">
                            <h4 className="ra-create-card__title">Work value &amp; billing</h4>
                            <div className="ra-create-fields">
                              <RaReadonlyField label="Gross value of work done (input)" valueClassName="is-amount">
                                {formatCurrency(selectedBill.upto_date_gross_work_done)}
                              </RaReadonlyField>
                              <RaReadonlyField label="Upto date gross (CPWD Σ)" valueClassName="is-amount">
                                {formatCurrency(selectedBill.upto_date_gross_value_of_work_done ?? selectedBill.upto_date_gross_inc_sec_adv)}
                              </RaReadonlyField>
                              <RaReadonlyField label="Billing Basis">{selectedBill.billing_basis_type || '—'}</RaReadonlyField>
                              <RaReadonlyField label="Dependency Status" valueClassName="is-badge">
                                <StatusBadge
                                  tone={
                                    selectedBill.billing_dependency_status === 'Blocked'
                                      ? 'critical'
                                      : selectedBill.billing_dependency_status === 'At Risk'
                                        ? 'high'
                                        : 'paid'
                                  }
                                >
                                  {selectedBill.billing_dependency_status}
                                </StatusBadge>
                              </RaReadonlyField>
                            </div>
                          </section>
                        )}

                        <section className="ra-create-card">
                          <h4 className="ra-create-card__title">Links &amp; audit</h4>
                          <div className="ra-create-fields">
                            <RaReadonlyField label="Linked Milestone">{selectedBill.linked_milestone_id || '—'}</RaReadonlyField>
                            <RaReadonlyField label="Linked EOT">{selectedBill.linked_eot_id || '—'}</RaReadonlyField>
                            <RaReadonlyField label="Linked BGs">{selectedBillBGs.length}</RaReadonlyField>
                            <RaReadonlyField label="Risk Flags" spanFull>
                              {getIssueFlags(selectedBill).length > 0 ? getIssueFlags(selectedBill).join(', ') : 'No active flags'}
                            </RaReadonlyField>
                            <RaReadonlyField label="Last Restructure">{formatDateIN(selectedBill.last_restructure_at)}</RaReadonlyField>
                            <RaReadonlyField label="Restructure By">{selectedBill.last_restructure_by || '—'}</RaReadonlyField>
                            <RaReadonlyField label="Last Restructure Reason" spanFull>
                              {selectedBill.last_restructure_reason || '—'}
                            </RaReadonlyField>
                            {selectedBill.billing_hold_reason ? (
                              <RaReadonlyField label="Billing Hold Reason" spanFull>
                                {selectedBill.billing_hold_reason}
                              </RaReadonlyField>
                            ) : null}
                          </div>
                        </section>

                        <section className="ra-create-card ra-create-card--flush">
                          <h4 className="ra-create-card__title">Linked bank guarantees</h4>
                          <div className="table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Sl. No</th>
                                  <th>Towards</th>
                                  <th>Instrument</th>
                                  <th>Date</th>
                                  <th className="amount-cell">Amount</th>
                                  <th>Bank</th>
                                  <th>Validity</th>
                                  <th>Attachments</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedBillBGs.length === 0 ? (
                                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No BGs linked to this RA Bill.</td></tr>
                                ) : (
                                  selectedBillBGs.map((bg, index) => (
                                    <tr key={bg.id}>
                                      <td>{index + 1}</td>
                                      <td>{getBGTowardsLabel(bg)}</td>
                                      <td style={{ fontWeight: 500, color: '#7C3AED' }}>{bg.bg_no}</td>
                                      <td>{formatDateIN(bg.issue_date)}</td>
                                      <td className="amount-cell">{formatCurrency(bg.amount)}</td>
                                      <td>{bg.bank}</td>
                                      <td>{formatDateIN(bg.expiry_date)}</td>
                                      <td>{safeArray(bg.attachments).length > 0 ? `${safeArray(bg.attachments).length} file(s)` : '—'}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </section>

                        <section className="ra-create-card">
                          <h4 className="ra-create-card__title">Control Settings</h4>
                          {isAdmin && (
                            <div className="form-row">
                              <div className="form-group">
                                <label>Status</label>
                                <select value={selectedBill.status || 'Draft'} onChange={(e) => setSelectedBill((prev) => ({ ...prev, status: e.target.value }))}>
                                  <option value="Draft">Draft</option>
                                  <option value="Submitted">Submitted</option>
                                  <option value="Paid">Paid</option>
                                </select>
                              </div>
                            </div>
                          )}
                          <div className="form-row">
                            <div className="form-group">
                              <label>Division / Category</label>
                              <select value={selectedBill.division || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, division: e.target.value || null }))}>
                                <option value="">— None —</option>
                                {DIVISIONS.map((d) => (
                                  <option key={d} value={d}>{d}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Billing Basis Type</label>
                              <select value={selectedBill.billing_basis_type || 'Quantity Based'} onChange={(e) => setSelectedBill((prev) => ({ ...prev, billing_basis_type: e.target.value }))}>
                                <option value="Quantity Based">Quantity Based</option>
                                <option value="Milestone Based">Milestone Based</option>
                                <option value="Hybrid">Hybrid</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Billing Dependency Status</label>
                              <select value={selectedBill.billing_dependency_status || 'Clear'} onChange={(e) => setSelectedBill((prev) => ({ ...prev, billing_dependency_status: e.target.value }))}>
                                <option value="Clear">Clear</option>
                                <option value="At Risk">At Risk</option>
                                <option value="Blocked">Blocked</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>Agreement No.</label><input type="text" value={selectedBill.agreement_no || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, agreement_no: e.target.value }))} /></div>
                            <div className="form-group"><label>Payment status</label>
                              <select value={selectedBill.payment_status || 'Unpaid'} onChange={(e) => setSelectedBill((prev) => ({ ...prev, payment_status: e.target.value }))}>
                                <option value="Unpaid">Unpaid</option>
                                <option value="Part Paid">Part Paid</option>
                                <option value="Paid">Paid</option>
                                <option value="Withheld">Withheld</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>RA Bill Date</label><input type="date" value={selectedBill.ra_bill_date || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, ra_bill_date: e.target.value }))} /></div>
                            <div className="form-group"><label>Certified Date</label><input type="date" value={selectedBill.certified_date || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, certified_date: e.target.value }))} /></div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>Payment Due Date</label><input type="date" value={selectedBill.payment_due_date || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, payment_due_date: e.target.value }))} /></div>
                            <div className="form-group"><label>Submitted Date</label><input type="date" value={selectedBill.submitted_date || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, submitted_date: e.target.value }))} /></div>
                          </div>
                          <div className="form-group"><label>Remarks</label><textarea rows="2" value={selectedBill.remarks || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, remarks: e.target.value }))} /></div>
                          <div className="form-row">
                            <div className="form-group"><label>Linked Milestone ID</label><input type="number" min="0" value={selectedBill.linked_milestone_id || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, linked_milestone_id: e.target.value === '' ? null : Number(e.target.value) }))} /></div>
                            <div className="form-group"><label>Linked EOT ID</label><input type="number" min="0" value={selectedBill.linked_eot_id || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, linked_eot_id: e.target.value === '' ? null : Number(e.target.value) }))} /></div>
                          </div>
                          <div className="form-group">
                            <label>Primary BG for Final Print</label>
                            <select value={selectedBill.primary_bg_id || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, primary_bg_id: e.target.value ? Number(e.target.value) : null }))}>
                              <option value="">None</option>
                              {projectBGs.map((bg) => (
                                <option key={bg.id} value={bg.id}>{bg.bg_no} - {bg.bank}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Linked BG IDs for Final Print</label>
                            <div className="ra-bg-check-list">
                              {projectBGs.length === 0 ? (
                                <span style={{ fontSize: 12, color: '#6b7280' }}>No BGs found for this project.</span>
                              ) : (
                                projectBGs.map((bg) => {
                                  const checked = safeArray(selectedBill.linked_bg_ids).map(Number).includes(Number(bg.id));
                                  return (
                                    <label key={bg.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          const current = new Set(safeArray(selectedBill.linked_bg_ids).map(Number));
                                          if (e.target.checked) current.add(Number(bg.id));
                                          else current.delete(Number(bg.id));
                                          setSelectedBill((prev) => ({ ...prev, linked_bg_ids: Array.from(current) }));
                                        }}
                                      />
                                      <span>{bg.bg_no} | {getBGTowardsLabel(bg)} | {bg.bank} | {formatCurrency(bg.amount)}</span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Primary Correspondence Subject</label>
                            <select value={selectedBill.primary_correspondence_chain_id || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, primary_correspondence_chain_id: e.target.value ? Number(e.target.value) : null, linked_correspondence: e.target.value ? Number(e.target.value) : null }))}>
                              <option value="">None</option>
                              {projectChains.map((chain) => (
                                <option key={chain.id} value={chain.id}>{chain.first_letter_ref || `#${chain.id}`} - {chain.subject}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Related Correspondence IDs</label>
                            <input
                              type="text"
                              placeholder="e.g. 12, 14, 18"
                              value={safeArray(selectedBill.related_correspondence_ids).join(', ')}
                              onChange={(e) => {
                                const ids = e.target.value.split(',').map((value) => value.trim()).filter(Boolean).map((value) => Number(value)).filter((value) => !Number.isNaN(value));
                                setSelectedBill((prev) => ({ ...prev, related_correspondence_ids: ids }));
                              }}
                            />
                          </div>
                          <div className="form-group">
                            <label>Billing Hold Reason</label>
                            <textarea rows="3" value={selectedBill.billing_hold_reason || ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, billing_hold_reason: e.target.value }))} />
                          </div>
                          <div className="form-row">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                              <input type="checkbox" checked={Boolean(selectedBill.withheld_due_to_milestone_flag)} onChange={(e) => setSelectedBill((prev) => ({ ...prev, withheld_due_to_milestone_flag: e.target.checked }))} />
                              Withheld due to milestone
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                              <input type="checkbox" checked={Boolean(selectedBill.withheld_due_to_eot_flag)} onChange={(e) => setSelectedBill((prev) => ({ ...prev, withheld_due_to_eot_flag: e.target.checked }))} />
                              Withheld due to EOT
                            </label>
                          </div>
                          <div className="modal-actions" style={{ marginTop: 0 }}>
                            {isAdmin && (
                              <div style={{ display: 'grid', gap: 8, marginRight: 'auto', flex: 1 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                  <input type="checkbox" checked={restructureMode} onChange={(e) => setRestructureMode(e.target.checked)} />
                                  Restructure Bill (audit required)
                                </label>
                                {restructureMode && (
                                  <div>
                                    <input
                                      type="text"
                                      placeholder="Reason for restructuring (required)"
                                      value={restructureReason}
                                      onChange={(e) => setRestructureReason(e.target.value)}
                                    />
                                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#6b7280' }}>
                                      Minimum {MIN_RESTRUCTURE_REASON_LENGTH} characters.
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            <button type="button" className="btn-primary" onClick={handleSaveBillControls} disabled={raControlSaving}>
                              {raControlSaving ? 'Saving...' : 'Save Controls'}
                            </button>
                          </div>
                        </section>
                      </div>
                    ),
                  },
                  {
                    key: 'cpwd',
                    label: 'CPWD calculation',
                    content: (
                      <div>
                        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                          CPWD workbook flow: edit inputs below; derived figures update immediately. Use <strong>Save Financial Inputs</strong> to persist (same as Recoveries &amp; withheld tab).
                        </p>
                        <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 10 }}>Commercial inputs</h4>
                        <div className="form-row">
                          <div className="form-group"><label>1. Gross value of work done (Rs.)</label><input type="number" min="0" value={selectedBill.upto_date_gross_work_done ?? ''} onChange={(e) => updateSelectedBillFormula({ upto_date_gross_work_done: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                          <div className="form-group"><label>2(a). Upto date secured advance on material (Rs.)</label><input type="number" min="0" value={selectedBill.sec_advance_material ?? ''} onChange={(e) => updateSelectedBillFormula({ sec_advance_material: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                        </div>
                        <div className="form-row">
                          <div className="form-group"><label>2(b). Mobilisation advance (Rs.)</label><input type="number" min="0" value={selectedBill.mobilisation_advance ?? ''} onChange={(e) => updateSelectedBillFormula({ mobilisation_advance: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                          <div className="form-group"><label>Clause 10CC / 10CA payment (Rs.)</label><input type="number" min="0" value={selectedBill.clause_10cc_10ca_payment ?? ''} onChange={(e) => updateSelectedBillFormula({ clause_10cc_10ca_payment: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                        </div>
                        <div className="form-row">
                          <div className="form-group"><label>2(c). Advance payment (not measured) (Rs.)</label><input type="number" min="0" value={selectedBill.advance_payment_workdone_not_measured ?? ''} onChange={(e) => updateSelectedBillFormula({ advance_payment_workdone_not_measured: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                          <div className="form-group"><label>Addl. SD for spl. items (Rs.)</label><input type="number" min="0" value={selectedBill.additional_sd_special_items ?? ''} onChange={(e) => updateSelectedBillFormula({ additional_sd_special_items: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                        </div>
                        <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', margin: '16px 0 10px' }}>Previous RA (line 4 / secured advance chain)</h4>
                        <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 10px' }}>Adjust if auto-filled values do not match the previous RA bill (e.g. E&amp;M stream).</p>
                        <div className="form-row">
                          <div className="form-group"><label>4. Gross value as per previous RA i/c Sec.Adv (Rs.)</label><input type="number" min="0" value={selectedBill.previous_ra_gross_inc_sec_adv ?? ''} onChange={(e) => {
                            const v = e.target.value === '' ? 0 : Number(e.target.value);
                            updateSelectedBillFormula({ previous_ra_gross_inc_sec_adv: v, previous_ra_upto_date_gross: v });
                          }} /></div>
                          <div className="form-group"><label>Secured advance upto (previous RA) (Rs.)</label><input type="number" min="0" value={selectedBill.previous_ra_sec_advance_upto ?? ''} onChange={(e) => updateSelectedBillFormula({ previous_ra_sec_advance_upto: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                        </div>
                        <div className="form-row" style={{ marginTop: 12 }}>
                          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label>CPWD row 5(b) override — net excl. secured adv. for GST / IT / cess / SD (Rs.)</label>
                            <input
                              type="number"
                              min="0"
                              value={selectedBill.cpwd_statutory_recovery_base_override ?? ''}
                              placeholder="0 = calculate automatically"
                              onChange={(e) => updateSelectedBillFormula({
                                cpwd_statutory_recovery_base_override: e.target.value === '' ? 0 : Number(e.target.value),
                              })}
                            />
                            <p style={{ fontSize: 11, color: '#6b7280', margin: '6px 0 0' }}>
                              If recoveries show ₹0 but your workbook line 5(b) is correct, enter that amount here (e.g. 155539774), then <strong>Save Financial Inputs</strong>.
                            </p>
                          </div>
                        </div>
                        <div className="form-row" style={{ marginTop: 12 }}>
                          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label>Security deposit override (₹)</label>
                            <input
                              type="number"
                              min="0"
                              placeholder="Auto — CPWD 2.5% on line 5(b)"
                              value={Object.prototype.hasOwnProperty.call(selectedBill, 'cpwd_security_deposit_override')
                                ? selectedBill.cpwd_security_deposit_override
                                : ''}
                              onChange={(e) => {
                                const t = e.target.value;
                                if (t === '') updateSelectedBillFormula({ cpwd_security_deposit_override: null });
                                else updateSelectedBillFormula({ cpwd_security_deposit_override: Number(t) });
                              }}
                            />
                            <p style={{ fontSize: 11, color: '#6b7280', margin: '6px 0 0' }}>
                              Leave blank for CPWD. Enter <strong>0</strong> to waive security deposit (e.g. E&amp;M bill). Other amounts replace SD only; GST / IT / cess still follow line 5(b). Save financial inputs to persist.
                            </p>
                          </div>
                        </div>
                        <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', margin: '16px 0 10px' }}>Derived (CPWD)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                          <div><p style={{ fontSize: 12, color: '#6b7280' }}>Upto date gross value of work done</p><p style={{ fontSize: 14, fontWeight: 600 }}>{formatCurrency(selectedBill.upto_date_gross_value_of_work_done ?? selectedBill.upto_date_gross_inc_sec_adv)}</p></div>
                          <div><p style={{ fontSize: 12, color: '#6b7280' }}>Gross as per previous RA bill</p><p style={{ fontSize: 14 }}>{formatCurrency(selectedBill.gross_value_as_per_previous_ra_bill ?? selectedBill.previous_ra_gross_inc_sec_adv)}</p></div>
                          <div><p style={{ fontSize: 12, color: '#6b7280' }}>Net value since previous bill</p><p style={{ fontSize: 14, fontWeight: 600 }}>{formatCurrency(selectedBill.net_value_work_done_since_prev_bill)}</p></div>
                          <div><p style={{ fontSize: 12, color: '#6b7280' }}>Secured advance added (this bill)</p><p style={{ fontSize: 14 }}>{formatCurrency(selectedBill.secured_advance_added_in_this_bill ?? 0)}</p></div>
                          <div><p style={{ fontSize: 12, color: '#6b7280' }}>Net excluding secured advance</p><p style={{ fontSize: 14 }}>{formatCurrency(selectedBill.net_value_work_done_without_sec_adv)}</p></div>
                          <div><p style={{ fontSize: 12, color: '#6b7280' }}>Progress % (÷ agreement amount)</p><p style={{ fontSize: 14 }}>{formatPercentValue(selectedBill.percent_workdone)}</p></div>
                        </div>
                        <div className="modal-actions" style={{ marginTop: 16 }}>
                          {isAdmin && (
                            <div style={{ display: 'grid', gap: 8, marginRight: 'auto', flex: 1 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                <input type="checkbox" checked={restructureMode} onChange={(e) => setRestructureMode(e.target.checked)} />
                                Restructure Bill (audit required)
                              </label>
                              {restructureMode && (
                                <div>
                                  <input
                                    type="text"
                                    placeholder="Reason for restructuring (required)"
                                    value={restructureReason}
                                    onChange={(e) => setRestructureReason(e.target.value)}
                                  />
                                  <p style={{ margin: '4px 0 0', fontSize: 11, color: '#6b7280' }}>
                                    Minimum {MIN_RESTRUCTURE_REASON_LENGTH} characters.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                          <button type="button" className="btn-primary" onClick={handleSaveBillControls} disabled={raControlSaving}>
                            {raControlSaving ? 'Saving...' : 'Save Financial Inputs'}
                          </button>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'recoveries',
                    label: 'Recoveries & withheld',
                    content: (
                      <div>
                        <div style={{ marginTop: 0 }}>
                          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Recoveries Breakdown</h4>
                              <div className="table-container">
                            <table className="data-table">
                              <thead><tr><th>Type</th><th className="amount-cell">Amount (₹)</th></tr></thead>
                              <tbody>
                                <tr><td>Security Deposit (CPWD 2.5%)</td><td className="amount-cell">{formatCurrencyNoSymbol(selectedBill.security_deposit)}</td></tr>
                                <tr><td>GST (CPWD 2%)</td><td className="amount-cell">{formatCurrencyNoSymbol(selectedBill.gst_deduction)}</td></tr>
                                <tr><td>Income Tax (CPWD 2%)</td><td className="amount-cell">{formatCurrencyNoSymbol(selectedBill.income_tax_deduction)}</td></tr>
                                <tr><td>Labour Cess (CPWD 1%)</td><td className="amount-cell">{formatCurrencyNoSymbol(selectedBill.labour_cess)}</td></tr>
                                <tr><td>Addl. SD for Spl. Items</td><td className="amount-cell">{formatCurrencyNoSymbol(selectedBill.additional_sd_special_items)}</td></tr>
                                <tr style={{ fontWeight: 600 }}><td>Total Recoveries</td><td className="amount-cell">{formatCurrencyNoSymbol(selectedBill.total_recoveries)}</td></tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="form-row" style={{ marginTop: 12 }}>
                          <div className="form-group"><label>Total recoveries (read-only)</label><input readOnly value={formatCurrencyNoSymbol(selectedBill.total_recoveries)} /></div>
                          <div className="form-group"><label>Total deductions (read-only)</label><input readOnly value={formatCurrencyNoSymbol(selectedBill.total_deductions)} /></div>
                          <div className="form-group"><label>Net payment received (read-only)</label><input readOnly value={formatCurrencyNoSymbol(selectedBill.net_payment_received ?? selectedBill.net_payable)} /></div>
                        </div>
                        <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Commercial inputs (editable)</h4>
                          <div className="form-row">
                            <div className="form-group"><label>1. Gross value of work done (Rs.)</label><input type="number" min="0" value={selectedBill.upto_date_gross_work_done || ''} onChange={(e) => updateSelectedBillFormula({ upto_date_gross_work_done: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                            <div className="form-group"><label>2(a). Upto date secured advance on material (Rs.)</label><input type="number" min="0" value={selectedBill.sec_advance_material ?? ''} onChange={(e) => updateSelectedBillFormula({ sec_advance_material: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>2(b). Mobilisation advance (Rs.)</label><input type="number" min="0" value={selectedBill.mobilisation_advance ?? ''} onChange={(e) => updateSelectedBillFormula({ mobilisation_advance: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                            <div className="form-group"><label>Clause 10CC / 10CA payment (Rs.)</label><input type="number" min="0" value={selectedBill.clause_10cc_10ca_payment ?? ''} onChange={(e) => updateSelectedBillFormula({ clause_10cc_10ca_payment: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>2(c). Advance payment (not measured) (Rs.)</label><input type="number" min="0" value={selectedBill.advance_payment_workdone_not_measured ?? ''} onChange={(e) => updateSelectedBillFormula({ advance_payment_workdone_not_measured: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>Addl. SD for Spl. Items</label><input type="number" min="0" value={selectedBill.additional_sd_special_items || ''} onChange={(e) => updateSelectedBillFormula({ additional_sd_special_items: e.target.value === '' ? 0 : Number(e.target.value) })} /></div>
                            <div className="form-group"><label>Agreement Amount</label><input type="number" value={selectedBill.agreement_amount || 0} disabled /></div>
                          </div>
                            <div className="form-row">
                              <div className="form-group"><label>Actual Total Recovered (optional)</label><input type="number" min="0" value={selectedBill.actual_total_recovered_amount ?? ''} onChange={(e) => setSelectedBill((prev) => ({ ...prev, actual_total_recovered_amount: e.target.value === '' ? null : Number(e.target.value) }))} placeholder="0" /></div>
                            </div>
                          <div style={{ marginTop: 16 }}>
                            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Withheld Breakdown (Optional)</h4>
                            {renderWithheldItemsEditor({
                              items: selectedBill.withheld_items,
                              onAdd: () => updateSelectedBillWithheldItems((items) => [...items, createWithheldItem()]),
                              onChange: (index, field, value) => updateSelectedBillWithheldItems((items) => items.map((item, itemIndex) => {
                                if (itemIndex !== index) return item;
                                if (field === 'reason') {
                                  return {
                                    ...item,
                                    reason: value,
                                    custom_reason: value === 'other' ? item.custom_reason : '',
                                  };
                                }
                                return {
                                  ...item,
                                  [field]: field === 'amount' ? (value === '' ? '' : Number(value)) : value,
                                };
                              })),
                              onRemove: (index) => updateSelectedBillWithheldItems((items) => items.filter((_, itemIndex) => itemIndex !== index)),
                            })}
                          </div>
                          <div className="modal-actions" style={{ marginTop: 0 }}>
                            {isAdmin && (
                              <div style={{ display: 'grid', gap: 8, marginRight: 'auto', flex: 1 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                  <input type="checkbox" checked={restructureMode} onChange={(e) => setRestructureMode(e.target.checked)} />
                                  Restructure Bill (audit required)
                                </label>
                                {restructureMode && (
                                  <div>
                                    <input
                                      type="text"
                                      placeholder="Reason for restructuring (required)"
                                      value={restructureReason}
                                      onChange={(e) => setRestructureReason(e.target.value)}
                                    />
                                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#6b7280' }}>
                                      Minimum {MIN_RESTRUCTURE_REASON_LENGTH} characters.
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            <button type="button" className="btn-primary" onClick={handleSaveBillControls} disabled={raControlSaving}>
                              {raControlSaving ? 'Saving...' : 'Save Financial Inputs'}
                            </button>
                          </div>
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Withheld Breakdown</h4>
                          <div className="table-container">
                            <table className="data-table">
                              <thead><tr><th>Type</th><th className="amount-cell">Amount (₹)</th></tr></thead>
                              <tbody>
                                {safeArray(selectedBill.withheld_items).filter((item) => Number(item.amount || 0) > 0).length === 0 ? (
                                  <tr><td colSpan="2" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No withheld deductions added.</td></tr>
                                ) : (
                                  safeArray(selectedBill.withheld_items)
                                    .filter((item) => Number(item.amount || 0) > 0)
                                    .map((item) => (
                                      <tr key={item.id}>
                                        <td>{getWithheldReasonLabel(item)}</td>
                                        <td className="amount-cell">{formatCurrencyNoSymbol(item.amount)}</td>
                                      </tr>
                                    ))
                                )}
                                <tr style={{ fontWeight: 600 }}><td>Total Withheld</td><td className="amount-cell">{formatCurrencyNoSymbol(selectedBill.total_withheld)}</td></tr>
                                <tr style={{ fontWeight: 600 }}><td>Total Deductions</td><td className="amount-cell">{formatCurrencyNoSymbol(selectedBill.total_deductions)}</td></tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'payments',
                    label: 'Payments',
                    content: (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                          <div><p style={{ fontSize: 12, color: '#6b7280' }}>Paid Value</p><p style={{ fontSize: 14, fontWeight: 500, color: '#047857' }}>{formatCurrency(selectedBill.paid_value)}</p></div>
                          <div><p style={{ fontSize: 12, color: '#6b7280' }}>Balance Value</p><p style={{ fontSize: 14, fontWeight: 500, color: selectedBill.balance_value > 0 ? '#b91c1c' : '#047857' }}>{formatCurrency(selectedBill.balance_value)}</p></div>
                        </div>
                        <div style={{ marginTop: 16 }}>
                          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Payment Records</h4>
                          <div className="table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th className="amount-cell">Amount</th>
                                  <th>Authority</th>
                                  <th>Voucher No</th>
                                  <th>CM Book</th>
                                  <th>Cash Book</th>
                                  <th>Remarks</th>
                                  <th className="print-hidden" style={{ width: 140 }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {safeArray(selectedBill.payments).length === 0 ? (
                                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No payment records added yet.</td></tr>
                                ) : (
                                  safeArray(selectedBill.payments).map((payment, idx) => (
                                    <tr
                                      key={`${payment.payment_date || ''}-${idx}-${payment.amount_received || ''}`}
                                      style={paymentEditIndex === idx ? { background: '#F5F3FF' } : undefined}
                                    >
                                      <td>{formatDateIN(payment.payment_date)}</td>
                                      <td className="amount-cell">{formatCurrency(payment.amount_received || 0)}</td>
                                      <td>{payment.paying_authority || '-'}</td>
                                      <td>{payment.voucher_no || '-'}</td>
                                      <td>{payment.cm_book_no || '-'}{payment.cm_book_page_nos ? ` / ${payment.cm_book_page_nos}` : ''}</td>
                                      <td>{payment.cash_book_no || '-'}{payment.cash_book_page_no ? ` / ${payment.cash_book_page_no}` : ''}</td>
                                      <td>{payment.remarks || '-'}</td>
                                      <td className="print-hidden" onClick={(e) => e.stopPropagation()}>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                          <button type="button" className="btn-secondary btn-sm" onClick={() => handleStartEditPayment(idx)} disabled={paymentSaving}>
                                            Edit
                                          </button>
                                          <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => handleDeletePaymentRecord(idx)} disabled={paymentSaving}>
                                            Delete
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>
                            {paymentEditIndex != null ? 'Edit payment record' : 'Add payment record'}
                          </h4>
                          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Payment Date and Amount Received are required.</p>
                          <div className="form-row">
                            <div className="form-group"><label>Payment Date <span style={{ color: '#dc2626' }}>*</span></label><input type="date" value={newPayment.payment_date} onChange={(e) => setNewPayment((prev) => ({ ...prev, payment_date: e.target.value }))} required /></div>
                            <div className="form-group"><label>Amount Received (₹) <span style={{ color: '#dc2626' }}>*</span></label><input type="number" min="0" step="0.01" placeholder="0" value={newPayment.amount_received} onChange={(e) => setNewPayment((prev) => ({ ...prev, amount_received: e.target.value }))} required /></div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>Paying Authority / DDO / EE</label><input type="text" value={newPayment.paying_authority} onChange={(e) => setNewPayment((prev) => ({ ...prev, paying_authority: e.target.value }))} /></div>
                            <div className="form-group"><label>Voucher No</label><input type="text" value={newPayment.voucher_no} onChange={(e) => setNewPayment((prev) => ({ ...prev, voucher_no: e.target.value }))} /></div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>CM Book No</label><input type="text" value={newPayment.cm_book_no} onChange={(e) => setNewPayment((prev) => ({ ...prev, cm_book_no: e.target.value }))} /></div>
                            <div className="form-group"><label>CM Book Page Nos</label><input type="text" value={newPayment.cm_book_page_nos} onChange={(e) => setNewPayment((prev) => ({ ...prev, cm_book_page_nos: e.target.value }))} /></div>
                          </div>
                          <div className="form-row">
                            <div className="form-group"><label>Cash Book No</label><input type="text" value={newPayment.cash_book_no} onChange={(e) => setNewPayment((prev) => ({ ...prev, cash_book_no: e.target.value }))} /></div>
                            <div className="form-group"><label>Cash Book Page No</label><input type="text" value={newPayment.cash_book_page_no} onChange={(e) => setNewPayment((prev) => ({ ...prev, cash_book_page_no: e.target.value }))} /></div>
                          </div>
                          <div className="form-group"><label>Remarks</label><textarea rows="3" value={newPayment.remarks} onChange={(e) => setNewPayment((prev) => ({ ...prev, remarks: e.target.value }))} /></div>
                          <div className="form-group">
                            <label>Payment Attachment</label>
                            <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handlePaymentAttachmentChange} disabled={paymentAttachmentUploading} />
                            {paymentAttachmentUploading && <p style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>Uploading payment attachment...</p>}
                            {newPayment.attachment && <p style={{ marginTop: 6, fontSize: 12, color: '#047857' }}>{newPayment.attachment.name}</p>}
                          </div>
                          <div className="modal-actions" style={{ marginTop: 0 }}>
                            {paymentEditIndex != null && (
                              <button type="button" className="btn-secondary" onClick={handleCancelPaymentEdit} disabled={paymentSaving || paymentAttachmentUploading}>
                                Cancel edit
                              </button>
                            )}
                            <button type="button" className="btn-primary" onClick={handleSavePaymentRecord} disabled={paymentSaving || paymentAttachmentUploading}>
                              {paymentSaving ? 'Saving...' : paymentEditIndex != null ? 'Update payment' : 'Add payment'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'linked',
                    label: 'Linked Correspondence Subject',
                    content: (
                      <div>
                        <LinkedCorrespondenceCard linkedCorrespondenceId={selectedBill.linked_correspondence} />
                        <div style={{ marginTop: 16 }}>
                          <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>All Linked Correspondence Subjects</h4>
                          <div className="table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Correspondence Subject</th>
                                  <th>Category</th>
                                  <th>Ref No</th>
                                  <th>Letter Date</th>
                                  <th>Direction</th>
                                  <th>From / To</th>
                                  <th>Status</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedBillChains.length === 0 ? (
                                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No linked Correspondence Subjects mapped yet.</td></tr>
                                ) : (
                                  selectedBillChains.map((chain) => {
                                    const latestEntry = safeArray(chain.entries)[0] || {};
                                    return (
                                      <tr key={chain.id}>
                                        <td style={{ fontWeight: 500, color: '#7C3AED' }}>{chain.subject}</td>
                                        <td>{chain.category || 'General'}</td>
                                        <td>{latestEntry.ref_no || chain.first_letter_ref || '-'}</td>
                                        <td>{formatDateIN(latestEntry.letter_date || chain.last_entry_date)}</td>
                                        <td>{latestEntry.direction || '-'}</td>
                                        <td>{latestEntry.from && latestEntry.to ? `${latestEntry.from} / ${latestEntry.to}` : '-'}</td>
                                        <td><StatusBadge tone={(chain.status || 'Open').toLowerCase()}>{chain.status || 'Open'}</StatusBadge></td>
                                        <td>
                                          {onViewThread ? (
                                            <button type="button" className="btn-secondary btn-sm" onClick={() => onViewThread(chain.id)}>Open Subject</button>
                                          ) : '-'}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'attachments',
                    label: 'Attachments',
                    content: (
                      <div>
                        {(selectedBill.attachments || []).length === 0 ? (
                          <p style={{ fontSize: 13, color: '#6b7280' }}>No attachments linked to this RA Bill.</p>
                        ) : (
                          <div style={{ marginTop: 4 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                              {selectedBill.attachments.map((file, idx) => (
                                <span key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <span style={{ fontSize: 13, color: '#374151' }}>{file?.name || `Attachment ${idx + 1}`}</span>
                                  <button type="button" className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, `RA Bill attachment: ${file?.name}`))}>View</button>
                                  <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, `RA Bill attachment: ${file?.name}`)}>Download</button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'restructure-history',
                    label: 'Restructure History',
                    content: (
                      <div>
                        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                          Audit timeline for structural changes on this bill.
                        </p>
                        <div className="table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>When</th>
                                <th>By</th>
                                <th>Reason</th>
                                <th className="amount-cell">Net Payable (₹)</th>
                                <th className="amount-cell">Deductions (₹)</th>
                                <th className="amount-cell">Recoveries (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {safeArray(selectedBill.restructure_history).length === 0 ? (
                                <tr>
                                  <td colSpan="6" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>
                                    No restructure activity logged.
                                  </td>
                                </tr>
                              ) : (
                                safeArray(selectedBill.restructure_history)
                                  .slice()
                                  .sort((a, b) => String(b.ts || '').localeCompare(String(a.ts || '')))
                                  .map((entry, idx) => (
                                    <tr key={`${entry.ts || 'ts'}-${idx}`}>
                                      <td>{formatDateIN(entry.ts)}</td>
                                      <td>{entry.by || '-'}</td>
                                      <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>{entry.reason || '-'}</td>
                                      <td className="amount-cell">{formatCurrencyNoSymbol(entry.net_payable || 0)}</td>
                                      <td className="amount-cell">{formatCurrencyNoSymbol(entry.total_deductions || 0)}</td>
                                      <td className="amount-cell">{formatCurrencyNoSymbol(entry.total_recoveries || 0)}</td>
                                    </tr>
                                  ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ),
                  },
                ]}
                activeKey={selectedBillTab}
                onChange={setSelectedBillTab}
              />
            </div>
          </div>
        </div>
      )}

      </div>

      {raPrintPreview && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: '96vw', maxWidth: 1200 }}>
            <div className="modal-header">
              <h2>{raPrintPreview.title}</h2>
              <button className="btn-close" onClick={closePrintPreview}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body" style={{ paddingTop: 8 }}>
              <p style={{ marginTop: 0, marginBottom: 12, fontSize: 12, color: '#6b7280' }}>{raPrintPreview.subtitle}</p>
              <iframe
                ref={printPreviewFrameRef}
                title="RA Bill Print Preview"
                srcDoc={raPrintPreview.fullHtml}
                style={{ width: '100%', height: '70vh', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff' }}
              />
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleDownloadPreview} disabled={previewDownloading}>
                  {previewDownloading ? 'Downloading...' : 'Download PDF'}
                </button>
                <button type="button" className="btn-primary" onClick={handlePrintPreview}>Print</button>
                <button type="button" className="btn-secondary" onClick={closePrintPreview}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RABillsRegister;
